import IncidentMedia from "../components/IncidentMedia";
import { useVisibleUnits } from "../hooks/useVisibleUnits";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  IconButton,
  Tooltip,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardLayout from "../layouts/DashboardLayout";
import { AuthContext } from "../context/AuthContext";
import { useAnnouncement } from "../context/AnnouncementContext";
import api from "../api/client";
import useWazeAlerts from "../hooks/useWazeAlerts";
import useEmergencyClearance from '../hooks/useEmergencyClearance';
import EmergencyClearanceReview from '../components/EmergencyClearanceReview';
import { activateEmergency as submitEmergencyActivation } from '../api/emergencyActions';
import useUnitHeartbeat from "../hooks/useUnitHeartbeat";
import useDesktopStatusIntent from "../hooks/useDesktopStatusIntent";
import { shouldRefreshWaze } from "../api/wazeRefresh";
import { subscribeToActiveUnitStream } from "../api/statusStream";
import { createDispatchRefresh, applyDispatchSnapshot } from "../api/dispatchRefresh";
import { createMissedCheckReporter, submitMissedStatusAction, readMissedStatusList, acknowledgeStatusCheck } from "../api/missedStatusActions";
import { setStatusSnapshot } from "../state/statusChannel";
import { SOUND_EVENTS, getEmergencySoundPath } from "../constants/audio";
import AvlMap from "../components/AvlMap";
import MapCenteredDashboard from "../components/MapCenteredDashboard";
import useElementHeight from "../hooks/useElementHeight";


import AbandonedVehicleAlert from "../components/AbandonedVehicleAlert";
import EventSignupModal from "../components/EventSignupModal";
import { createAbandonedVehicle, getActiveAbandonedVehicles } from "../api/abandonedVehicles";
import { createIncident, createWazeIncident } from "../services/offlineApi";
import {
  INCIDENT_TYPES,
  SERVICE_OPTIONS,
  SOURCE_OPTIONS,
} from "../constants/incidentOptions";
import {
  DEFAULT_STATUS_CHECK_MINUTES,
  DEFAULT_PING_INTERVAL_SECONDS,
} from "../constants/systemConfig";
import { geocodeKey, reverseGeocodeLookup, reverseGeocodeResponse } from "../utils/reverseGeocode";
import { captureIncidentCoordinates } from "../utils/incidentGps";
import { getTenCodeLabel } from "../constants/preferences";
import { resolveDivisionFromCounty, TRAVEL_DIVISION } from "../constants/regions";
import TabletAndroidIcon from "@mui/icons-material/TabletAndroid";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SchoolIcon from "@mui/icons-material/School";
import { useSoundPack } from "../hooks/useSoundPack";
import { formatUtcToLocal, formatUtcToLocalTime } from "../utils/dateUtils";
import { getStatusCheckThreshold as getStatusCheckThresholdUtil, buildStatusCheckState } from "../utils/statusCheckUtils";

const STATUS_BUTTONS = [
  { label: "EMERGENCY", value: "emergency", color: "error" },
  { label: "OUT OF SERVICE", value: "out-of-service", color: "inherit" },
  { label: "AVAILABLE", value: "available", color: "success" },
  { label: "BUSY", value: "busy", color: "warning" },
];

const STATUS_VALUES = {
  OPEN: "open",
  CLEARED: "closed",
  INCOMPLETE: "incomplete",
};

const BUSY_REASON_OPTIONS = [
  { value: "out-of-vehicle", label: "OUT OF VEHICLE" },
  { value: "work", label: "WORK" },
  { value: "special-event", label: "SPECIAL EVENT" },
  { value: "gym-pt", label: "GYM/PT" },
  { value: "training", label: "TRAINING" },
  { value: "vehicle-maintenance", label: "VEHICLE MAINTENANCE" },
];

const WAZE_CITY_OPTIONS = [
  "McKinney",
  "Allen",
  "Anna",
  "Melissa",
  "Van Alstyne",
  "Richardson",
  "Plano",
  "Sherman",
  "Dallas",
];

// Waze type options match the displayed labels in the alert table
const WAZE_TYPE_OPTIONS = [
  { value: "ASSIST_MOTORIST", label: "Assist Motorist" },
  { value: "DEBRIS", label: "Debris" },
  { value: "ACCIDENT", label: "Accident" },
  { value: "ASSIST_OTHER_AGENCY", label: "Assist Other Agency" },
];

const WAZE_DISTANCE_OPTIONS = [
  { value: 0, label: "Show All" },
  { value: 0.5, label: "Within 0.5 miles" },
  { value: 1, label: "Within 1 mile" },
  { value: 1.25, label: "Within 1.25 miles" },
  { value: 1.5, label: "Within 1.5 miles" },
  { value: 1.75, label: "Within 1.75 miles" },
  { value: 2, label: "Within 2 miles" },
  { value: 3, label: "Within 3 miles" },
  { value: 4, label: "Within 4 miles" },
  { value: 5, label: "Within 5 miles" },
  { value: 10, label: "Within 10 miles" },
  { value: 15, label: "Within 15 miles" },
  { value: 20, label: "Within 20 miles" },
  { value: 25, label: "Within 25 miles" },
];

// Helper to get Waze alert category from type/subtype
const getWazeAlertCategory = (type, subtype) => {
  const subtypeUpper = (subtype || "").toUpperCase();
  const typeUpper = (type || "").toUpperCase();

  if (subtypeUpper.includes("EMERGENCY_VEHICLE")) return "ASSIST_OTHER_AGENCY";
  if (subtypeUpper.includes("CAR_STOPPED")) return "ASSIST_MOTORIST";
  if (typeUpper === "HAZARD") return "DEBRIS";
  if (typeUpper === "ACCIDENT") return "ACCIDENT";
  return "DEBRIS"; // Default
};

// Expand direction abbreviations in street names (only at start or after space + before digit/letter)
const expandDirections = (street) => {
  if (!street) return street;
  return street
    .replace(/\bNB\b/g, "NORTHBOUND")
    .replace(/\bSB\b/g, "SOUTHBOUND")
    .replace(/\bEB\b/g, "EASTBOUND")
    .replace(/\bWB\b/g, "WESTBOUND")
    .replace(/(?<=^|\s)N(?=\s+\d|\s+[A-Z]{2})/g, "NORTHBOUND")
    .replace(/(?<=^|\s)S(?=\s+\d|\s+[A-Z]{2})/g, "SOUTHBOUND")
    .replace(/(?<=^|\s)E(?=\s+\d|\s+[A-Z]{2})/g, "EASTBOUND")
    .replace(/(?<=^|\s)W(?=\s+\d|\s+[A-Z]{2})/g, "WESTBOUND");
};

// Normalize server incident type to display format (UPPERCASE with spaces)
// Handles: "assist-motorist" -> "ASSIST MOTORIST", "Assist Motorist" -> "ASSIST MOTORIST"
const normalizeIncidentType = (serverType) => {
  if (!serverType) return "";
  // Convert dashes to spaces and uppercase
  const normalized = serverType.replace(/-/g, ' ').toUpperCase();
  // Check if it matches a known type
  const match = INCIDENT_TYPES.find(
    (t) => t.toUpperCase() === normalized
  );
  return match || normalized;
};

const REQUIRED_CLEAR_FIELDS = {
  source: "Source",
  incidentType: "Incident Type",
  notes: "Notes",
  servicesProvided: "Disposition",
  "vehicle.make": "MAKE",
  "vehicle.model": "MODEL",
  "vehicle.color": "COLOR",
};

const formatMissingFieldList = (fields) =>
  fields
    .map((field) => REQUIRED_CLEAR_FIELDS[field] || field)
    .join(", ");

const formatTransportTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const appendNoteLine = (notes, line) => {
  const trimmed = notes?.trim();
  if (!trimmed) {
    return line;
  }
  return `${trimmed}\n${line}`;
};

const parseTransportState = (notes) => {
  if (!notes) {
    return { active: false, address: "" };
  }

  const lines = notes.split(/\r?\n/);
  let lastTransportIndex = -1;
  let lastEndIndex = -1;
  let lastAddress = "";

  lines.forEach((line, index) => {
    const upper = line.toUpperCase();
    if (upper.includes("TRANSPORTING")) {
      lastTransportIndex = index;
      const match = line.match(/TRANSPORTING TO (.+?) AT/i);
      if (match) {
        lastAddress = match[1].trim();
      }
    }
    if (upper.includes("END OF TRANSPORT")) {
      lastEndIndex = index;
    }
  });

  const active = lastTransportIndex !== -1 && lastTransportIndex > lastEndIndex;
  return { active, address: lastAddress };
};


const formatDisplay = (value) => (value ? String(value).toUpperCase() : "—");
const ADMIN_ROLES = ["admin", "supervisor"];
const DEBRIS_DISPOSITION = "DEBRIS REMOVAL";
const VEHICLE_TEMPLATE = {
  year: "",
  make: "",
  model: "",
  color: "",
  state: "",
  plate: "",
};
const FLASH_DURATION_MS = 2400;
const STATUS_CHECK_POLL_MS = 30000;
const INCIDENTS_REFRESH_MS = 15000;
const STREAM_RECONNECT_DELAY_MS = 6000;
const GPS_REFRESH_INTERVAL_MS = 6000;
const GPS_STALE_THRESHOLD_MS = 12000;
const GPS_HIDDEN_STORAGE_KEY = "rvGpsHidden";
const GPS_REACTIVATION_AT_KEY = "rvGpsReactivateAt";
const GPS_REACTIVATION_DELAY_MS = 30000;
const GPS_DISABLED_STORAGE_KEY = "rvGpsDisabled";
const STATUS_ALERT_REPEAT_MS = 5000;
const EMERGENCY_CONFIRM_SECONDS = 5;

const parseCoordinatesFromLocation = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  // A road name containing two numbers is not a coordinate pair.
  const matches = value.trim().match(/^(?:LAT(?:ITUDE)?\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)\s*(?:[,/]\s*|\s+)(?:LON(?:GITUDE)?\s*[:=]?\s*)?(-?\d+(?:\.\d+)?)$/i);
  if (!matches) {
    return null;
  }

  const latitude = Number(matches[1]);
  const longitude = Number(matches[2]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }

  return { latitude, longitude };
};

function Dashboard() {
  const [dispatchBannerRef, dispatchBannerHeight] = useElementHeight();
  const { user } = useContext(AuthContext);
  const emergencyOwnerRef = useRef(user?.id);
  emergencyOwnerRef.current = user?.id;
  const emergencyToken = localStorage.getItem('rv_token');
  const emergencyClearance = useEmergencyClearance(user);
  const { snowbankEnabled } = useAnnouncement();
  const useTenCodes = user?.useTenCodes || false;
  const dashboardStyle = (user?.dashboardStyle || "mapview").toLowerCase();
  const isMapView = dashboardStyle === "mapview";
  const { playCue } = useSoundPack();
  const clearedIncidentCuesRef = useRef(new Map());
  const announceIncidentClear = useCallback((clearedCalls) => {
    let announce = false;
    for (const incident of clearedCalls) {
      if (!incident?.id) continue;
      const ended = Date.parse(incident.occurredEndUtc || "");
      const key = JSON.stringify([user?.id, incident.id, Number.isFinite(ended) ? ended : "closed"]);
      if (clearedIncidentCuesRef.current.has(key)) continue;
      clearedIncidentCuesRef.current.set(key, true);
      announce = true;
    }
    // Bound the session's audio receipts without throttling unrelated incidents.
    while (clearedIncidentCuesRef.current.size > 2048) {
      clearedIncidentCuesRef.current.delete(clearedIncidentCuesRef.current.keys().next().value);
    }
    if (announce) playCue(SOUND_EVENTS.INCIDENT_CLEARED);
  }, [playCue, user?.id]);
  const statusIntent = useDesktopStatusIntent(user);
  const markStatusIntent = statusIntent.mark;
  const [unitStatus, setUnitStatus] = useState("available");
  const preEmergencyStatusRef = useRef("available");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const prefersTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [tabletMode, setTabletMode] = useState(() => {
    if (typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('ranger_tablet_mode');
      if (saved !== null) return JSON.parse(saved);
    }
    return prefersTablet;
  });

  // Sidebar state for mapview mode
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    }
    return false;
  });

  const handleSidebarToggle = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('sidebarCollapsed', String(newValue));
      }
      return newValue;
    });
  }, []);

  const [incidentBanner, setIncidentBanner] = useState(null);
  const [newIncidentOpen, setNewIncidentOpen] = useState(false);
  const [newIncidentSubmitting, setNewIncidentSubmitting] = useState(false);
  const callIntentRef = useRef(false);
  const [newIncidentForm, setNewIncidentForm] = useState({
    source: "",
    incidentType: "",
    notes: "",
    vehicle: { ...VEHICLE_TEMPLATE },
  });
  const [newIncidentError, setNewIncidentError] = useState("");
  const [quickCreateIncidentMap, setQuickCreateIncidentMap] = useState({});
  const [detailRequiresSourceConfirm, setDetailRequiresSourceConfirm] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [incidentsError, setIncidentsError] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailIncidentId, setDetailIncidentId] = useState(null);
  const [detailIncident, setDetailIncident] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailForm, setDetailForm] = useState(() => ({
    source: "",
    incidentType: "",
    notes: "",
    servicesProvided: [],
    vehicle: { ...VEHICLE_TEMPLATE },
    latitude: "",
    longitude: "",
    locationDescription: "",
    streetName: "",
    trafficDirection: "",
  }));
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailAttaching, setDetailAttaching] = useState(false);
  const [detailValidationErrors, setDetailValidationErrors] = useState([]);
  const hasValidationError = useCallback(
    (field) => detailValidationErrors.includes(field),
    [detailValidationErrors]
  );
  const [transportDialog, setTransportDialog] = useState({ open: false, mode: "start" });
  const [transportAddress, setTransportAddress] = useState("");
  const [transportStartAddress, setTransportStartAddress] = useState("");
  const [transportActive, setTransportActive] = useState(false);
  const [transportIncidentId, setTransportIncidentId] = useState(null);
  const [manualOnScene, setManualOnScene] = useState(false);
  const [userEnroute, setUserEnroute] = useState(false);
  const [enrouteDestinationId, setEnrouteDestinationId] = useState(null);
  const [manualActiveIncident, setManualActiveIncident] = useState(null);
  const [gpsDivision, setGpsDivision] = useState(null); // GPS-resolved division from county
  const lastDivisionGeocodeRef = useRef(0);
  const pendingQuickCallRef = useRef(null);
  const conflictFlightRef = useRef(false);
  const [incidentConflict, setIncidentConflict] = useState(null);
  const [incidentConflictOpen, setIncidentConflictOpen] = useState(false);
  const [incidentConflictSaving, setIncidentConflictSaving] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState({ open: false, incident: null });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [abandonedPrompt, setAbandonedPrompt] = useState({ open: false, active: false });
  const [abandonedVehicles, setAbandonedVehicles] = useState([]);
  const [flashingIncidents, setFlashingIncidents] = useState([]);
  const [updatedFlashingIncidents, setUpdatedFlashingIncidents] = useState([]);
  const flashTimeouts = useRef(new Map());
  const updateFlashTimeouts = useRef(new Map());
  const hasFetchedInitial = useRef(false);
  const [statusCheckIncidents, setStatusCheckIncidents] = useState([]);
  const [statusCheckRangers, setStatusCheckRangers] = useState([]);
  const [statusAcknowledgements, setStatusAcknowledgements] = useState({});
  const [statusModalIncident, setStatusModalIncident] = useState(null);
  const [statusCheckError, setStatusCheckError] = useState('');
  useEffect(() => { setStatusCheckError(''); }, [statusModalIncident?.incident?.id]);
  const [statusCheckSettings, setStatusCheckSettings] = useState({
    defaultMinutes: DEFAULT_STATUS_CHECK_MINUTES,
    overrides: {},
    pingIntervalSeconds: DEFAULT_PING_INTERVAL_SECONDS,
  });
  const [statusBackdropAlert, setStatusBackdropAlert] = useState(false);
  const statusModalOpen = Boolean(statusModalIncident);
  const statusBackdropTimerRef = useRef(null);
  const statusModalPinnedRef = useRef(null);
  const statusAudioTimerRef = useRef(null);
  const statusAlertAudioRef = useRef(null);
  const incidentsAudioPrimedRef = useRef(false);
  const previousIncidentsRef = useRef([]);
  const localIncidentIdsRef = useRef(new Set());
  const seenIncidentIdsRef = useRef(new Set()); // Track all incident IDs we've seen to prevent duplicate audio
  const previousGpsHiddenRef = useRef();
  const statusCuePrimedRef = useRef(false);
  const previousStatusCheckSetRef = useRef(new Set());
  const { alerts: wazeAlerts, loading: wazeAlertsLoading, error: wazeError,
    fetchAlerts: fetchWazeAlerts, clearAlerts: clearWazeAlerts } = useWazeAlerts(user);
  const [wazeClaimingId, setWazeClaimingId] = useState(null);
  const [wazeCityFilter, setWazeCityFilter] = useState(() => {
    if (typeof window === "undefined") return [];
    const stored = window.localStorage.getItem("wazeCityFilter");
    return stored ? JSON.parse(stored) : [];
  });
  const [wazeEnabled, setWazeEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem("wazeEnabled");
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [wazeTypeFilter, setWazeTypeFilter] = useState(() => {
    if (typeof window === "undefined") return [];
    const stored = window.localStorage.getItem("wazeTypeFilter");
    return stored ? JSON.parse(stored) : [];
  });
  const [wazeDistanceFilter, setWazeDistanceFilter] = useState(() => {
    if (typeof window === "undefined") return 5; // Default to 5 miles
    const stored = window.localStorage.getItem("wazeDistanceFilter");
    return stored !== null ? JSON.parse(stored) : 5; // Default to 5 miles
  });
  const [wazeConfigOpen, setWazeConfigOpen] = useState(false);


  // Assist Ranger modal state
  const [assistRangerOpen, setAssistRangerOpen] = useState(false);
  const [assistRangerJoining, setAssistRangerJoining] = useState(false);

  // Missed status check alert state
  const [missedStatusChecks, setMissedStatusChecks] = useState([]);
  const [missedStatusCheckOpen, setMissedStatusCheckOpen] = useState(false);
  const missedStatusCheckOpenRef = useRef(false);
  const announcedMissedChecksRef = useRef({ session: null, keys: new Set() });
  // Keep ref in sync with state so polling callback can read current value without re-creating
  useEffect(() => { missedStatusCheckOpenRef.current = missedStatusCheckOpen; }, [missedStatusCheckOpen]);
  const [missedStatusCheckTaking, setMissedStatusCheckTaking] = useState(false);
  const [missedStatusCheckResolving, setMissedStatusCheckResolving] = useState(false);
  const [missedHandoffReview, setMissedHandoffReview] = useState(null);
  const [missedAssignmentHistory, setMissedAssignmentHistory] = useState(null);
  useEffect(() => {
    setMissedStatusChecks([]);
    setSelectedMissedCheck(null);
    setMissedStatusCheckOpen(false);
    setMissedHandoffReview(null);
    setMissedAssignmentHistory(null);
  }, [user?.id]);
  const [selectedMissedCheck, setSelectedMissedCheck] = useState(null);
  const missedCheckReportedRef = useRef(null);
  if (!missedCheckReportedRef.current) missedCheckReportedRef.current = createMissedCheckReporter({
    api, currentSession: () => localStorage.getItem("rv_token"),
  });

  const stopStatusAlertAudio = useCallback(() => {
    const audio = statusAlertAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  // Emergency audio handling
  const stopEmergencyAudio = useCallback(() => {
    if (emergencyAudioIntervalRef.current) {
      clearInterval(emergencyAudioIntervalRef.current);
      emergencyAudioIntervalRef.current = null;
    }
    const audio = emergencyAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  const startEmergencyAudio = useCallback(() => {
    if (typeof window === "undefined") return;

    // Stop any existing emergency audio
    stopEmergencyAudio();

    try {
      const source = getEmergencySoundPath();
      const audio = new Audio(source);
      audio.preload = "auto";
      audio.loop = true;
      emergencyAudioRef.current = audio;

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((err) => console.warn("Emergency audio blocked", err));
      }
    } catch (error) {
      console.error("Unable to prepare emergency audio", error);
    }
  }, [stopEmergencyAudio]);

  const ensureStatusAlertAudio = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }

    if (statusAlertAudioRef.current) {
      return statusAlertAudioRef.current;
    }

    try {
      const base = typeof process !== "undefined" && process.env?.PUBLIC_URL ? process.env.PUBLIC_URL : "";
      const source = `${base}/sounds/statuscheckalarm.mp3`;
      const audio = new Audio(source);
      audio.loop = false;
      audio.preload = "auto";
      statusAlertAudioRef.current = audio;
      return audio;
    } catch (error) {
      console.error("Unable to prepare status alert audio", error);
      return null;
    }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined" || !statusModalOpen || !statusBackdropAlert) {
      if (statusAudioTimerRef.current) {
        clearInterval(statusAudioTimerRef.current);
        statusAudioTimerRef.current = null;
      }
      stopStatusAlertAudio();
      return undefined;
    }

    const audio = ensureStatusAlertAudio();
    if (!audio) {
      return undefined;
    }

    const playAlert = () => {
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((err) => console.warn("Status alert audio blocked", err));
      }
    };

    playAlert();
    statusAudioTimerRef.current = window.setInterval(playAlert, STATUS_ALERT_REPEAT_MS);

    return () => {
      if (statusAudioTimerRef.current) {
        clearInterval(statusAudioTimerRef.current);
        statusAudioTimerRef.current = null;
      }
      stopStatusAlertAudio();
    };
  }, [ensureStatusAlertAudio, statusBackdropAlert, statusModalOpen, stopStatusAlertAudio]);
  useEffect(() => {
    if (!statusModalOpen) {
      setStatusBackdropAlert(false);
      if (statusBackdropTimerRef.current) {
        clearTimeout(statusBackdropTimerRef.current);
        statusBackdropTimerRef.current = null;
      }
      return undefined;
    }

    setStatusBackdropAlert(false);
    if (statusBackdropTimerRef.current) {
      clearTimeout(statusBackdropTimerRef.current);
    }

    statusBackdropTimerRef.current = setTimeout(() => setStatusBackdropAlert(true), 30000); // 30 seconds

    return () => {
      if (statusBackdropTimerRef.current) {
        clearTimeout(statusBackdropTimerRef.current);
        statusBackdropTimerRef.current = null;
      }
    };
  }, [statusModalOpen, statusModalIncident?.incident?.id]);

  // Report when the overdue status prompt remains unanswered.
  // Defined here so it's available for the useEffect below
  const clearMissedCheckReport = useCallback((incidentId, rangerId) => {
    missedCheckReportedRef.current.clear(incidentId, rangerId);
  }, []);

  const reportMissedStatusCheck = useCallback(async (incidentId, rangerId, cycle) => {
    try {
      const receipt = await missedCheckReportedRef.current.report(incidentId, rangerId, cycle);
      if (receipt?.id) setIncidentBanner(previous => previous?.context === 'missed-status-report' ? null : previous);
    } catch (error) {
      setIncidentBanner({ type: 'error', context: 'missed-status-report', message: 'STATUS ALERT COULD NOT BE SAVED. RETRYING WHEN CONNECTED.' });
    }
  }, []);

  // Escalate an unanswered overdue prompt, retaining retryability on failure.
  useEffect(() => {
    if (!statusBackdropAlert || !statusModalIncident) return;

    const { incident } = statusModalIncident;
    if (!incident?.id) return;

    const rangerId = user?.rangerId || incident.primaryRangerId;
    if (rangerId) {
      const attempt = () => reportMissedStatusCheck(incident.id, rangerId, incident.statusCheckAcknowledgedUtc || incident.occurredAtUtc);
      attempt();
      const retry = setInterval(attempt, 10000);
      return () => clearInterval(retry);
    }
  }, [statusBackdropAlert, statusModalIncident, user?.rangerId, reportMissedStatusCheck]);

  const [decodedLocations, setDecodedLocations] = useState({});
  // TODO: surface unitHistory in map trails once UI is wired.
  // eslint-disable-next-line no-unused-vars
  const [unitHistory, setUnitHistory] = useState({});
  const [usePollingFallback, setUsePollingFallback] = useState(true);
  const eventSourceRef = useRef(null);
  const streamRetryRef = useRef(null);
  const pollingFallbackRef = useRef(true);
  const [remoteActiveUnits, setRemoteActiveUnits] = useState([]);
  const [activeUnitsLoading, setActiveUnitsLoading] = useState(false);
  const [activeUnitsError, setActiveUnitsError] = useState("");
  const [gpsLocation, setGpsLocation] = useState("");
  const [gpsCoords, setGpsCoords] = useState(null);
  const incidentGpsRef = useRef(null);
  incidentGpsRef.current = gpsCoords;
  const [gpsHidden, setGpsHidden] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const rawReactivation = window.localStorage.getItem(GPS_REACTIVATION_AT_KEY);
    const reactivateAt = rawReactivation ? Number(rawReactivation) : NaN;
    if (Number.isFinite(reactivateAt) && reactivateAt > Date.now()) {
      return true;
    }
    return window.localStorage.getItem(GPS_HIDDEN_STORAGE_KEY) === "true";
  });
  const [gpsReactivateAt, setGpsReactivateAt] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }
    const raw = window.localStorage.getItem(GPS_REACTIVATION_AT_KEY);
    const timestamp = raw ? Number(raw) : NaN;
    if (Number.isFinite(timestamp) && timestamp > Date.now()) {
      return timestamp;
    }
    if (raw) {
      window.localStorage.removeItem(GPS_REACTIVATION_AT_KEY);
    }
    return null;
  });
  const [gpsCooldownRemaining, setGpsCooldownRemaining] = useState(0);
  const [gpsDisabled, setGpsDisabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(GPS_DISABLED_STORAGE_KEY) === "true";
  });
  const [busyReason, setBusyReason] = useState("");
  const [busyLocation, setBusyLocation] = useState("");
  const [busyInfo, setBusyInfo] = useState("");
  const [busyManualMode, setBusyManualMode] = useState(false);
  const [busyDetailsVisible, setBusyDetailsVisible] = useState(false);
  const [busyModalOpen, setBusyModalOpen] = useState(false);
  const busyHideTimerRef = useRef(null);
  const gpsWatchIdRef = useRef(null);
  const lastGpsUpdateRef = useRef(0);
  const gpsRefreshInFlightRef = useRef(false);

  // Emergency alert state
  const [activeEmergencies, setActiveEmergencies] = useState([]);
  const [emergencySilenced, setEmergencySilenced] = useState(false);
  const emergencyAudioRef = useRef(null);
  const emergencyAudioIntervalRef = useRef(null);

  // Self-cancel emergency modal state
  const [supervisorClear, setSupervisorClear] = useState({ open: false, rangerId: null });
  const [supervisorClearLoading, setSupervisorClearLoading] = useState(false);
  const [selfCancelOpen, setSelfCancelOpen] = useState(false);
  const [selfCancelReason, setSelfCancelReason] = useState("");
  const [selfCancelPin, setSelfCancelPin] = useState("");
  const [selfCancelLoading, setSelfCancelLoading] = useState(false);
  const [selfCancelError, setSelfCancelError] = useState("");
  const [emergencyConfirmOpen, setEmergencyConfirmOpen] = useState(false);
  const [emergencyCountdown, setEmergencyCountdown] = useState(EMERGENCY_CONFIRM_SECONDS);
  const [emergencyActivating, setEmergencyActivating] = useState(false);
  const emergencyCountdownIntervalRef = useRef(null);

  // Track which emergencies the current user has taken responsibility for
  const [responsibilityTaken, setResponsibilityTaken] = useState({});
  const [responsibilityLoading, setResponsibilityLoading] = useState({});

  // Handle emergency audio based on active emergencies (excluding own emergencies)
  useEffect(() => {
    // Filter out user's own emergencies - only alert for OTHER rangers
    const otherEmergencies = activeEmergencies.filter(
      (e) => e.userId !== user?.id
    );

    if (otherEmergencies.length > 0 && !emergencySilenced) {
      startEmergencyAudio();
    } else {
      stopEmergencyAudio();
    }

    return () => {
      stopEmergencyAudio();
    };
  }, [activeEmergencies, user?.id, emergencySilenced, startEmergencyAudio, stopEmergencyAudio]);

  // Reset silence state when emergencies are cleared
  useEffect(() => {
    const otherEmergencies = activeEmergencies.filter((e) => e.userId !== user?.id);
    if (otherEmergencies.length === 0) {
      setEmergencySilenced(false);
    }
  }, [activeEmergencies, user?.id]);

  const commitGpsPosition = useCallback(
    (latitude, longitude, heading, observedAt = Date.now(), accuracy) => {
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return;
      }

      const formatted = `LAT ${latitude.toFixed(5)}, LON ${longitude.toFixed(5)}`;
      setGpsCoords({ latitude, longitude, heading: Number.isFinite(heading) ? heading : undefined, observedAt, accuracy });
      setGpsLocation(formatted);
      lastGpsUpdateRef.current = Date.now();
    },
    [setGpsCoords, setGpsLocation]
  );

  useEffect(() => {
    pollingFallbackRef.current = usePollingFallback;
  }, [usePollingFallback]);

  useEffect(() => {
    // Only auto-set if user hasn't explicitly chosen
    if (typeof localStorage !== 'undefined' && localStorage.getItem('ranger_tablet_mode') === null) {
      setTabletMode(prefersTablet);
    }
  }, [prefersTablet]);

  // Persist tablet mode to localStorage
  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('ranger_tablet_mode', JSON.stringify(tabletMode));
    }
  }, [tabletMode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(GPS_HIDDEN_STORAGE_KEY, gpsHidden ? "true" : "false");
  }, [gpsHidden]);

  const statusAccountRef = useRef(user?.id);
  useEffect(() => {
    if (statusAccountRef.current === user?.id) return;
    statusAccountRef.current = user?.id;
    preEmergencyStatusRef.current = 'available';
    setEmergencyActivating(false); setEmergencyConfirmOpen(false); setSelfCancelLoading(false);
    setSupervisorClear({ open: false, rangerId: null }); setSupervisorClearLoading(false);
    setSelfCancelOpen(false); setSelfCancelPin(''); setSelfCancelReason(''); setSelfCancelError('');
    setUnitStatus('available'); setBusyReason(''); setBusyLocation(''); setBusyInfo('');
    setBusyManualMode(false); setBusyModalOpen(false); setBusyDetailsVisible(false);
    setManualOnScene(false); setUserEnroute(false); setEnrouteDestinationId(null);
    setManualActiveIncident(null); setTransportActive(false); setTransportIncidentId(null);
    setTransportAddress(''); setTransportStartAddress('');
  }, [user?.id]);

  // Canonical state never creates a new status intent. Preserve a pending edit.
  useEffect(() => {
    if (!user || statusIntent.pending || busyModalOpen) return;
    const own = remoteActiveUnits.find(unit => unit.userId === user.id);
    if (!own) return;
    const value = (own.status || "available").toLowerCase().replace(/\s+/g, "-");
    setUnitStatus(value);
    setManualOnScene(Boolean(own.onScene));
    setUserEnroute(own.statusLabel === "ENROUTE");
    if (value === "busy") {
      const reasonLabel = (own.statusLabel || "").replace(/^BUSY\s*-\s*/i, "");
      const reason = BUSY_REASON_OPTIONS.find(option => option.label.toUpperCase() === reasonLabel.toUpperCase());
      setBusyReason(reason?.value || "");
      setBusyLocation(own.location || "");
      setBusyInfo(own.info || "");
      setBusyManualMode(!own.onScene && own.statusLabel !== "ENROUTE" && own.statusLabel !== "TRANSPORTING");
    } else {
      setBusyReason(""); setBusyInfo(""); setBusyLocation(""); setBusyManualMode(false);
    }
  }, [remoteActiveUnits, user, statusIntent.pending, busyModalOpen]);

  useEffect(() => {
    if (typeof previousGpsHiddenRef.current === "undefined") {
      previousGpsHiddenRef.current = gpsHidden;
      return;
    }

    if (previousGpsHiddenRef.current === gpsHidden) {
      return;
    }

    playCue(gpsHidden ? SOUND_EVENTS.GPS_DISABLED : SOUND_EVENTS.GPS_ENABLED);
    previousGpsHiddenRef.current = gpsHidden;
  }, [gpsHidden, playCue]);

  useEffect(() => {
    if (!gpsReactivateAt) {
      setGpsCooldownRemaining(0);
      return;
    }

    const updateRemaining = () => {
      const remainingSeconds = Math.max(0, Math.ceil((gpsReactivateAt - Date.now()) / 1000));
      setGpsCooldownRemaining(remainingSeconds);
      if (remainingSeconds <= 0) {
        setGpsReactivateAt(null);
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(GPS_REACTIVATION_AT_KEY);
        }
        setGpsHidden(false);
      }
    };

    updateRemaining();

    if (typeof window === "undefined") {
      return undefined;
    }

    const intervalId = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(intervalId);
  }, [gpsReactivateAt]);

  useEffect(() => {
    const timeouts = flashTimeouts.current;
    const updateTimeouts = updateFlashTimeouts.current;
    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      timeouts.clear();
      updateTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      updateTimeouts.clear();
    };
  }, []);

  const handleGpsVisibilityToggle = useCallback(
    (_, checked) => {
      if (checked) {
        setGpsHidden(true);
        if (gpsReactivateAt) {
          setGpsReactivateAt(null);
          if (typeof window !== "undefined") {
            window.localStorage.removeItem(GPS_REACTIVATION_AT_KEY);
          }
        }
        return;
      }

      if (gpsReactivateAt) {
        return;
      }

      const target = Date.now() + GPS_REACTIVATION_DELAY_MS;
      setGpsReactivateAt(target);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(GPS_REACTIVATION_AT_KEY, String(target));
      }
    },
    [gpsReactivateAt]
  );

  // Handle GPS disabled toggle - completely stops GPS requests on desktop
  const handleGpsDisabledToggle = useCallback((_, checked) => {
    setGpsDisabled(checked);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(GPS_DISABLED_STORAGE_KEY, checked ? "true" : "false");
    }
    // Clear GPS coords when disabling so we don't send stale data
    if (checked) {
      setGpsCoords(null);
      setGpsLocation("");
    }
  }, []);

  const loadStatusCheckSettings = useCallback(async () => {
    try {
      const response = await api.get("/status-check-settings");
      const payload = response.data || {};
      const defaultMinutes = Number(payload.defaultMinutes);
      const safeDefault = Number.isFinite(defaultMinutes)
        ? defaultMinutes
        : DEFAULT_STATUS_CHECK_MINUTES;

      const pingSeconds = Number(payload.pingIntervalSeconds);
      const safePing = Number.isFinite(pingSeconds) && pingSeconds > 0
        ? pingSeconds
        : DEFAULT_PING_INTERVAL_SECONDS;

      const overridesArray = Array.isArray(payload.overrides) ? payload.overrides : [];
      const overrides = overridesArray.reduce((map, entry) => {
        if (!entry || !entry.incidentType) {
          return map;
        }
        const minutes = Number(entry.minutes);
        if (!Number.isFinite(minutes) || minutes <= 0) {
          return map;
        }
        map[entry.incidentType.trim().toLowerCase()] = minutes;
        return map;
      }, {});

      setStatusCheckSettings({
        defaultMinutes: safeDefault,
        overrides,
        pingIntervalSeconds: safePing,
      });
    } catch (error) {
      console.error("Unable to load status check settings.", error);
      setStatusCheckSettings((prev) => ({ ...prev }));
    }
  }, []);

  useEffect(() => {
    loadStatusCheckSettings();
  }, [loadStatusCheckSettings]);

  // Fetch active missed status checks (alerts to other users)
  const refreshMissedStatusChecks = useMemo(() => createDispatchRefresh({
    currentSession: () => localStorage.getItem("rv_token"),
    load: async () => readMissedStatusList(await api.get("/missedstatuschecks/active", { timeout: 15000 })),
    apply: checks => {
      setMissedStatusChecks(checks);
      setSelectedMissedCheck(previous => previous ? checks.find(check => check.id === previous.id) || null : null);
      if (checks.length === 0) setMissedStatusCheckOpen(false);
      const unresolvedForOthers = checks.filter(check => !check.responsibleUserId && check.rangerId?.toUpperCase() !== user?.rangerId?.toUpperCase());
      const session = localStorage.getItem("rv_token");
      const previous = announcedMissedChecksRef.current;
      const keys = new Set(unresolvedForOthers.map(check => `${check.id}:${check.rangerId.toUpperCase()}`));
      const hasNew = [...keys].some(key => previous.session !== session || !previous.keys.has(key));
      announcedMissedChecksRef.current = { session, keys };
      if (hasNew && !missedStatusCheckOpenRef.current) setMissedStatusCheckOpen(true);
    },
  }), [user]);

  const fetchMissedStatusChecks = useCallback(async () => {
    if (!user) return;
    const session = localStorage.getItem("rv_token");
    try {
      await refreshMissedStatusChecks();
      if (session === localStorage.getItem("rv_token")) setIncidentBanner(previous => previous?.context === 'missed-status-refresh' ? null : previous);
    } catch (error) {
      if (session === localStorage.getItem("rv_token")) setIncidentBanner({ type: 'error', context: 'missed-status-refresh', message: 'UNABLE TO REFRESH STATUS ALERTS. SHOWING LAST RECEIVED STATE.' });
    }
  }, [user, refreshMissedStatusChecks]);

  // Poll for missed status checks every 10 seconds
  useEffect(() => {
    if (!user) return undefined;
    fetchMissedStatusChecks();
    const intervalId = setInterval(fetchMissedStatusChecks, 10000);
    return () => clearInterval(intervalId);
  }, [fetchMissedStatusChecks, user]);

  // Take responsibility for a missed status check
  const handleTakeResponsibility = useCallback(async (checkId) => {
    setMissedStatusCheckTaking(true);
    const session = localStorage.getItem("rv_token");
    try {
      const data = await submitMissedStatusAction(api, `/missedstatuschecks/${checkId}/take-responsibility`, {});
      if (session !== localStorage.getItem("rv_token")) return;
      setSelectedMissedCheck(data);
      await fetchMissedStatusChecks();
    } catch (error) {
      const message = error.response?.data?.message || "Failed to take responsibility";
      setIncidentBanner({ type: "error", message: message.toUpperCase() });
      if (error.response?.status === 409) {
        // Someone else took it, refresh the list
        await fetchMissedStatusChecks();
      }
    } finally {
      setMissedStatusCheckTaking(false);
    }
  }, [fetchMissedStatusChecks]);

  const handleMissedHandoff = useCallback(async () => {
    const reviewed = missedHandoffReview;
    if (!reviewed || missedStatusCheckTaking) return;
    const session = localStorage.getItem("rv_token");
    setMissedStatusCheckTaking(true);
    try {
      const data = await submitMissedStatusAction(api, `/missedstatuschecks/${reviewed.id}/take-over`, {
        expectedResponsibleUserId: reviewed.responsibleUserId,
        expectedTakenAtUtc: reviewed.responsibilityTakenAtUtc || null,
      });
      if (session !== localStorage.getItem("rv_token")) return;
      setMissedHandoffReview(null);
      setSelectedMissedCheck(data);
      setMissedAssignmentHistory(null);
      await fetchMissedStatusChecks();
    } catch (error) {
      if (session !== localStorage.getItem("rv_token")) return;
      setIncidentBanner({ type: "error", message: (error.response?.data?.message || "Handoff was not confirmed. Refresh alerts before retrying.").toUpperCase() });
      if (error.response?.status === 409 || error.response?.status === 404) {
        setMissedHandoffReview(null);
        await fetchMissedStatusChecks();
      }
    } finally { setMissedStatusCheckTaking(false); }
  }, [missedHandoffReview, missedStatusCheckTaking, fetchMissedStatusChecks]);

  const handleMissedAssignmentHistory = useCallback(async () => {
    if (!selectedMissedCheck) return;
    const id = selectedMissedCheck.id;
    const session = localStorage.getItem("rv_token");
    try {
      const response = await api.get(`/missedstatuschecks/${id}/assignments`);
      if (response.status !== 200 || !Array.isArray(response.data)) throw new Error("History unavailable");
      if (session === localStorage.getItem("rv_token")) setMissedAssignmentHistory({ id, rows: response.data });
    } catch (_) {
      if (session === localStorage.getItem("rv_token")) setMissedAssignmentHistory({ id, error: "Assignment history could not be loaded. Try again." });
    }
  }, [selectedMissedCheck]);

  // Resolve a missed status check (10-4 or clear)
  const handleResolveMissedCheck = useCallback(async (checkId, resolution) => {
    setMissedStatusCheckResolving(true);
    const session = localStorage.getItem("rv_token");
    try {
      await submitMissedStatusAction(api, `/missedstatuschecks/${checkId}/resolve`, { resolution });
      if (session !== localStorage.getItem("rv_token")) return;
      setSelectedMissedCheck(null);
      setMissedStatusCheckOpen(false);
      await fetchMissedStatusChecks();
      setIncidentBanner({
        type: "success",
        message: resolution === "clear"
          ? "INCIDENT CLEARED - RANGER CAN FINISH DETAILS LATER"
          : "STATUS CHECK CONFIRMED - RANGER IS OK"
      });
    } catch (error) {
      console.error("Failed to resolve missed status check", error);
      setIncidentBanner({ type: "error", message: "FAILED TO RESOLVE STATUS CHECK" });
    } finally {
      setMissedStatusCheckResolving(false);
    }
  }, [fetchMissedStatusChecks]);

  const getStatusCheckThreshold = useCallback(
    (incidentType, source) => {
      return getStatusCheckThresholdUtil(incidentType, source, statusCheckSettings);
    },
    [statusCheckSettings.defaultMinutes, statusCheckSettings.overrides]
  );


  const queueFlash = useCallback((incidentIds = []) => {
    if (!Array.isArray(incidentIds) || incidentIds.length === 0) {
      return;
    }

    setFlashingIncidents((prev) => {
      const next = [...prev];
      let changed = false;

      incidentIds.forEach((id) => {
        if (!next.includes(id)) {
          next.push(id);
          changed = true;
        }

        const existingTimeout = flashTimeouts.current.get(id);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        const timeoutId = setTimeout(() => {
          setFlashingIncidents((current) => current.filter((value) => value !== id));
          flashTimeouts.current.delete(id);
        }, FLASH_DURATION_MS);

        flashTimeouts.current.set(id, timeoutId);
      });

      return changed ? next : prev;
    });
  }, []);

  const queueUpdateFlash = useCallback((incidentIds = []) => {
    if (!Array.isArray(incidentIds) || incidentIds.length === 0) {
      return;
    }

    setUpdatedFlashingIncidents((prev) => {
      const next = [...prev];
      let changed = false;

      incidentIds.forEach((id) => {
        if (!next.includes(id)) {
          next.push(id);
          changed = true;
        }

        const existingTimeout = updateFlashTimeouts.current.get(id);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        const timeoutId = setTimeout(() => {
          setUpdatedFlashingIncidents((current) => current.filter((value) => value !== id));
          updateFlashTimeouts.current.delete(id);
        }, FLASH_DURATION_MS);

        updateFlashTimeouts.current.set(id, timeoutId);
      });

      return changed ? next : prev;
    });
  }, []);

  const refreshDispatch = useMemo(() => createDispatchRefresh({
    currentSession: () => localStorage.getItem("rv_token"),
    load: async () => (await api.get("/status/snapshot", { timeout: 15000, params: { _ts: Date.now() } })).data,
    apply: snapshot => applyDispatchSnapshot(snapshot, {
      units: setRemoteActiveUnits,
      history: setUnitHistory,
      emergencies: setActiveEmergencies,
      incidents: items => {
        setIncidents(prev => {
          const previousIds = new Set(prev.map(incident => incident.id));
          const skipFlash = !hasFetchedInitial.current && prev.length === 0;
          const newIds = skipFlash ? [] : items.filter(incident => !previousIds.has(incident.id)).map(incident => incident.id);
          if (newIds.length) queueFlash(newIds);
          return items;
        });
        hasFetchedInitial.current = true;
      },
    }),
  }), [queueFlash]);

  const fetchIncidents = useCallback(async () => {
    setIncidentsLoading(true);
    try {
      await refreshDispatch();
      setIncidentsError("");
      setActiveUnitsError("");
    } catch (error) {
      setIncidentsError("UNABLE TO REFRESH DISPATCH. SHOWING LAST RECEIVED STATE.");
    } finally {
      setIncidentsLoading(false);
    }
  }, [refreshDispatch]);

  const fetchAbandonedVehicles = useCallback(async () => {
    try {
      const vehicles = await getActiveAbandonedVehicles();
      setAbandonedVehicles(Array.isArray(vehicles) ? vehicles : []);
    } catch (error) {
      console.warn("Unable to load abandoned vehicles.", error);
    }
  }, []);

  useEffect(() => {
    fetchAbandonedVehicles();
    const intervalId = setInterval(fetchAbandonedVehicles, 60000);
    return () => clearInterval(intervalId);
  }, [fetchAbandonedVehicles]);

  const fetchActiveUnits = fetchIncidents;

  useEffect(() => {
    if (!user) {
      setUsePollingFallback(true);
      setRemoteActiveUnits([]);
      setUnitHistory({});

      if (typeof window !== "undefined" && streamRetryRef.current) {
        window.clearTimeout(streamRetryRef.current);
        streamRetryRef.current = null;
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      return undefined;
    }

    if (typeof window === "undefined") {
      return undefined;
    }

    let cancelled = false;

    const clearRetryTimer = () => {
      if (!streamRetryRef.current) {
        return;
      }

      window.clearTimeout(streamRetryRef.current);
      streamRetryRef.current = null;
    };

    const closeStream = () => {
      if (!eventSourceRef.current) {
        return;
      }

      eventSourceRef.current.close();
      eventSourceRef.current = null;
    };

    const handleMessage = (payload) => {
      if (cancelled || !payload) {
        return;
      }

      if (shouldRefreshWaze(payload.reason)) fetchWazeAlerts();
      if (payload.reason?.startsWith("missed-status-") || payload.reason === "status-check-acknowledged") fetchMissedStatusChecks();
      // Re-read canonical state even for empty/partial events and reconnect snapshots.
      // Delayed event payloads cannot overwrite a newer HTTP snapshot.
      refreshDispatch().then(() => {
        if (cancelled) return;
        setIncidentsError("");
        setActiveUnitsError("");
        setActiveUnitsLoading(false);
        setUsePollingFallback(false);
      }).catch(() => {
        if (!cancelled) {
          setUsePollingFallback(true);
          setIncidentsError("UNABLE TO REFRESH DISPATCH. SHOWING LAST RECEIVED STATE.");
        }
      });
    };

    const scheduleReconnect = () => {
      if (cancelled || streamRetryRef.current) {
        return;
      }

      streamRetryRef.current = window.setTimeout(() => {
        streamRetryRef.current = null;
        if (!cancelled) {
          connect();
        }
      }, STREAM_RECONNECT_DELAY_MS);
    };

    const handleError = () => {
      if (cancelled) {
        return;
      }

      setUsePollingFallback((prev) => (prev ? prev : true));
      scheduleReconnect();
    };

    function connect() {
      if (cancelled) {
        return;
      }

      clearRetryTimer();
      closeStream();

      const source = subscribeToActiveUnitStream({
        onMessage: handleMessage,
        onError: handleError,
        onOpen: () => {
          if (cancelled) {
            return;
          }
          setActiveUnitsError("");
        },
      });

      if (!source) {
        setUsePollingFallback((prev) => (prev ? prev : true));
        return;
      }

      eventSourceRef.current = source;
    }

    connect();

    return () => {
      cancelled = true;
      clearRetryTimer();
      closeStream();
    };
  }, [user, refreshDispatch, fetchWazeAlerts, fetchMissedStatusChecks]);

  const buildDetailForm = useCallback((detail) => {
    const servicesProvided = Array.isArray(detail?.servicesProvided)
      ? detail.servicesProvided.filter(Boolean)
      : [];
    if (isDebrisType(detail?.incidentType) && servicesProvided.length === 0) {
      servicesProvided.push(DEBRIS_DISPOSITION);
    }
    return {
      source: detail?.source || "",
      incidentType: normalizeIncidentType(detail?.incidentType),
      notes: detail?.notes || "",
      servicesProvided,
      vehicle: { ...VEHICLE_TEMPLATE, ...(detail?.vehicle ?? {}) },
      latitude: typeof detail?.latitude === "number" ? String(detail.latitude) : "",
      longitude: typeof detail?.longitude === "number" ? String(detail.longitude) : "",
      locationDescription: detail?.locationDescription || "",
      streetName: detail?.streetName || "",
      trafficDirection: detail?.trafficDirection || "",
    };
  }, []);

  const clearValidationFlag = useCallback((field) => {
    setDetailValidationErrors((prev) => (prev.includes(field) ? prev.filter((key) => key !== field) : prev));
  }, []);

  const computeMissingClearFields = useCallback(() => {
    if (!detailForm) {
      return Object.keys(REQUIRED_CLEAR_FIELDS);
    }

    const missing = [];
    if (!detailForm.source) {
      missing.push("source");
    }
    if (!detailForm.incidentType) {
      missing.push("incidentType");
    }
    if (!detailForm.notes || !detailForm.notes.trim()) {
      missing.push("notes");
    }

    const hasDisposition = Array.isArray(detailForm.servicesProvided)
      && detailForm.servicesProvided.filter(Boolean).length > 0;
    if (!hasDisposition) {
      missing.push("servicesProvided");
    }

    return missing;
  }, [detailForm]);

  const buildDetailPayload = useCallback(
    (overrides = {}) => {
      if (!detailForm) {
        return { ...overrides };
      }

      const trimmedNotes = detailForm.notes?.trim();
      let servicesProvided = Array.isArray(detailForm.servicesProvided)
        ? detailForm.servicesProvided.filter(Boolean)
        : [];

      if (isDebrisType(detailForm.incidentType) && servicesProvided.length === 0) {
        servicesProvided = [DEBRIS_DISPOSITION];
      }

      // Parse latitude/longitude - only include if valid numbers
      const latNum = detailForm.latitude ? parseFloat(detailForm.latitude) : undefined;
      const lngNum = detailForm.longitude ? parseFloat(detailForm.longitude) : undefined;

      const payload = {
        source: detailForm.source || undefined,
        incidentType: detailForm.incidentType || undefined,
        notes: trimmedNotes || undefined,
        servicesProvided,
        vehicle: detailForm.vehicle,
        latitude: Number.isFinite(latNum) ? latNum : undefined,
        longitude: Number.isFinite(lngNum) ? lngNum : undefined,
        locationDescription: detailForm.locationDescription?.trim() || undefined,
        streetName: detailForm.streetName?.trim() || undefined,
        trafficDirection: detailForm.trafficDirection || undefined,
        ...overrides,
      };

      return payload;
    },
    [detailForm]
  );

  const loadIncidentDetail = useCallback(
    async (incidentId, { showLoader = true } = {}) => {
      if (!incidentId) {
        return;
      }

      if (showLoader) {
        setDetailLoading(true);
      }
      setDetailError("");

      try {
        const response = await api.get(`/incidents/${incidentId}`);
        setDetailIncident(response.data);
        setDetailForm(buildDetailForm(response.data));
        setDetailValidationErrors([]);
      } catch (error) {
        console.error(error);
        setDetailError("UNABLE TO LOAD INCIDENT DETAILS.");
      } finally {
        if (showLoader) {
          setDetailLoading(false);
        }
      }
    },
    [buildDetailForm]
  );

  useEffect(() => {
    if (!detailIncidentId || !detailIncident) {
      return;
    }

    if (transportIncidentId && transportIncidentId !== detailIncidentId) {
      return;
    }

    const { active, address } = parseTransportState(detailIncident.notes);
    setTransportActive(active);
    setTransportIncidentId(active ? detailIncidentId : null);
    if (address) {
      setTransportAddress(address);
    }
    setTransportStartAddress(active ? address : "");
  }, [detailIncident, detailIncidentId, transportIncidentId]);

  const persistDetailChanges = useCallback(
    async (overrides = {}, { successMessage = "Incident updated.", soundEvent } = {}) => {
      if (!detailIncidentId || !detailForm) {
        return false;
      }

      setDetailSaving(true);
      setDetailError("");

      try {
        const payload = buildDetailPayload(overrides);
        await api.put(`/incidents/${detailIncidentId}`, payload);
        setDetailEditMode(false);
        // Close the dialog instead of showing read-only view
        setDetailOpen(false);
        fetchIncidents();
        const key = String(detailIncidentId);
        setQuickCreateIncidentMap((prev) => {
          if (!prev[key]) {
            return prev;
          }
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setDetailRequiresSourceConfirm(false);
        if (soundEvent === SOUND_EVENTS.INCIDENT_CLEARED) {
          announceIncidentClear([{ id: detailIncidentId, occurredEndUtc: payload.occurredEndUtc }]);
        } else if (soundEvent) {
          playCue(soundEvent);
        }
        return true;
      } catch (error) {
        console.error(error);
        setDetailError("UNABLE TO SAVE INCIDENT.");
        return false;
      } finally {
        setDetailSaving(false);
      }
    },
    [
      buildDetailPayload,
      detailForm,
      detailIncidentId,
      fetchIncidents,
      playCue,
      announceIncidentClear,
      setDetailRequiresSourceConfirm,
      setQuickCreateIncidentMap,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    const tick = async () => {
      if (cancelled) {
        return;
      }

      await fetchIncidents();

      if (cancelled || typeof window === "undefined") {
        return;
      }

      timeoutId = window.setTimeout(tick, INCIDENTS_REFRESH_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [fetchIncidents]);

  // Fetch Waze alerts every 2 minutes (only if enabled)
  useEffect(() => {
    if (!wazeEnabled) {
      clearWazeAlerts();
      return;
    }

    let cancelled = false;
    let timeoutId;

    const tick = async () => {
      if (cancelled) {
        return;
      }

      await fetchWazeAlerts();

      if (cancelled || typeof window === "undefined") {
        return;
      }

      timeoutId = window.setTimeout(tick, 120000); // 2 minutes
    };

    tick();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [fetchWazeAlerts, clearWazeAlerts, wazeEnabled]);

  // Persist wazeEnabled to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("wazeEnabled", JSON.stringify(wazeEnabled));
    }
  }, [wazeEnabled]);

  useEffect(() => {
    // On first load, just record all incident IDs we've seen - don't play any audio
    if (!incidentsAudioPrimedRef.current) {
      previousIncidentsRef.current = [...incidents];
      // Add all current incident IDs to seenIncidentIdsRef so we don't play audio for them later
      incidents.forEach((incident) => {
        if (incident?.id) {
          seenIncidentIdsRef.current.add(String(incident.id));
        }
      });
      if (hasFetchedInitial.current) {
        incidentsAudioPrimedRef.current = true;
      }
      return;
    }

    const prevMap = new Map(previousIncidentsRef.current.map((item) => [item.id, item]));
    const newForUser = [];
    const generalNew = [];
    const cleared = [];
    const statusChanges = [];
    const normalizeStatus = (value) => (value ? String(value).toLowerCase() : "");
    const normalizedUserId = user?.rangerId ? user.rangerId.toUpperCase() : null;

    const isAssignedToUser = (incident) => {
      if (!normalizedUserId) {
        return false;
      }

      if (incident.primaryRangerId && incident.primaryRangerId.toUpperCase() === normalizedUserId) {
        return true;
      }

      if (Array.isArray(incident.rangerIds)) {
        return incident.rangerIds.some((id) => id && id.toUpperCase() === normalizedUserId);
      }

      return false;
    };

    incidents.forEach((incident) => {
      const previous = prevMap.get(incident.id);
      const idKey = incident?.id ? String(incident.id) : null;

      if (!previous) {
        // Skip if we've already seen this incident (prevents duplicate audio)
        if (idKey && seenIncidentIdsRef.current.has(idKey)) {
          return;
        }

        // Skip if this is a locally created incident (we play incident-created instead)
        if (idKey && localIncidentIdsRef.current.has(idKey)) {
          // Mark as seen but don't play dispatch audio
          seenIncidentIdsRef.current.add(idKey);
          localIncidentIdsRef.current.delete(idKey);
          return;
        }

        // Mark as seen
        if (idKey) {
          seenIncidentIdsRef.current.add(idKey);
        }

        if (isAssignedToUser(incident)) {
          newForUser.push(incident.id);
        } else {
          generalNew.push(incident.id);
        }
        return;
      }

      const prevStatus = normalizeStatus(previous.status);
      const nextStatus = normalizeStatus(incident.status);

      if (prevStatus !== STATUS_VALUES.CLEARED && nextStatus === STATUS_VALUES.CLEARED) {
        cleared.push(incident);
        return;
      }

      if (prevStatus !== nextStatus) {
        statusChanges.push(incident.id);
        return;
      }
    });

    // Only play dispatch sound when an incident is assigned TO THIS USER
    if (newForUser.length > 0) {
      playCue(SOUND_EVENTS.DISPATCH_ASSIGNED);
    }

    if (cleared.length > 0) {
      announceIncidentClear(cleared);
    } else if (statusChanges.length > 0) {
      playCue(SOUND_EVENTS.INCIDENT_UPDATED);
      queueUpdateFlash(statusChanges);
    }

    // Store a shallow copy to prevent reference issues
    previousIncidentsRef.current = [...incidents];
  }, [incidents, playCue, announceIncidentClear, queueUpdateFlash, user?.rangerId]);

  useEffect(() => {
    if (!user) {
      setRemoteActiveUnits([]);
      setActiveUnitsLoading(false);
      setActiveUnitsError("");
      return undefined;
    }

    if (!usePollingFallback) {
      setActiveUnitsLoading(false);
      return undefined;
    }

    let cancelled = false;
    let timeoutId;
    let firstRun = true;

    const poll = async () => {
      if (firstRun) {
        setActiveUnitsLoading(true);
      }

      try {
        await fetchActiveUnits();
      } finally {
        if (firstRun) {
          setActiveUnitsLoading(false);
          firstRun = false;
        }
      }

      if (cancelled || typeof window === "undefined" || !pollingFallbackRef.current) {
        return;
      }

      const intervalSeconds = 5;

      timeoutId = window.setTimeout(poll, intervalSeconds * 1000);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [fetchActiveUnits, statusCheckSettings.pingIntervalSeconds, usePollingFallback, user]);

  const clearBusyHideTimer = useCallback(() => {
    if (busyHideTimerRef.current) {
      clearTimeout(busyHideTimerRef.current);
      busyHideTimerRef.current = null;
    }
  }, []);

  const stopGpsWatch = useCallback(() => {
    if (gpsWatchIdRef.current !== null && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.clearWatch(gpsWatchIdRef.current);
      gpsWatchIdRef.current = null;
    }
    lastGpsUpdateRef.current = 0;
    gpsRefreshInFlightRef.current = false;
  }, []);

  useEffect(() => {
    return () => clearBusyHideTimer();
  }, [clearBusyHideTimer]);

  useEffect(() => () => stopGpsWatch(), [stopGpsWatch]);

  const scheduleBusyHide = useCallback(
    (delayMs) => {
      clearBusyHideTimer();
      busyHideTimerRef.current = setTimeout(() => {
        setBusyDetailsVisible(false);
      }, delayMs);
    },
    [clearBusyHideTimer]
  );

  const resetBusyMeta = useCallback(() => {
    setBusyReason("");
    setBusyLocation("");
    setBusyInfo("");
    setBusyManualMode(false);
    setBusyDetailsVisible(false);
    setBusyModalOpen(false);
    clearBusyHideTimer();
  }, [clearBusyHideTimer]);

  const handleStatusChange = useCallback(
    (value, { manual = false } = {}) => {
      const shouldUpdateStatus = !(manual && value === "emergency");

      // Play status updated sound when user changes status (not for emergency)
      if (value !== "emergency" && value !== unitStatus) {
        playCue(SOUND_EVENTS.STATUS_UPDATED);
      }

      // For manual BUSY, set status immediately AND open the modal for optional details
      if (value === "busy" && manual) {
        markStatusIntent();
        setUnitStatus("busy");
        setBusyManualMode(true);
        setBusyModalOpen(true);
        return;
      }

      if (shouldUpdateStatus) {
        markStatusIntent();
        setUnitStatus(value);

        if (value === "busy") {
          setBusyManualMode(Boolean(manual));

          if (manual) {
            setBusyDetailsVisible(true);
            scheduleBusyHide(60000);
          }

          if (!manual) {
            setBusyReason("");
            setBusyLocation("");
            setBusyInfo("");
            setBusyDetailsVisible(false);
            clearBusyHideTimer();
          }
        } else {
          resetBusyMeta();
          // Clear enroute when going to non-busy status
          setUserEnroute(false);
          setEnrouteDestinationId(null);

          // Clear manual incident state when going available or out-of-service
          if (value === "available" || value === "out-of-service") {
            setManualActiveIncident(null);
            setManualOnScene(false);
          }
        }
      }

      // Emergency is now handled by handleActivateEmergency via the button
      // No longer showing the fake modal
    },
    [clearBusyHideTimer, resetBusyMeta, scheduleBusyHide, playCue, unitStatus, markStatusIntent]
  );

  useEffect(() => {
    if (unitStatus === "busy" && busyManualMode) {
      setBusyDetailsVisible(true);
      scheduleBusyHide(30000);
      return;
    }

    if (!busyManualMode || unitStatus !== "busy") {
      clearBusyHideTimer();
      setBusyDetailsVisible(false);
    }
  }, [busyManualMode, unitStatus, clearBusyHideTimer, scheduleBusyHide]);

  const handleBusyModalSubmit = useCallback(() => {
    markStatusIntent();
    // Status is already set to busy, just close the modal
    setBusyDetailsVisible(true);
    setBusyModalOpen(false);
    scheduleBusyHide(60000);
  }, [scheduleBusyHide, markStatusIntent]);

  const handleBusyModalCancel = useCallback(() => {
    // Just close the modal - user stays busy but without the optional details
    setBusyModalOpen(false);
  }, []);

  const handleNewIncidentChange = (field) => (event) => {
    const value = event.target.value;
    const nextValue = field === "notes" && typeof value === "string" ? value.toUpperCase() : value;
    setNewIncidentForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleNewVehicleChange = (field) => (event) => {
    const value = event.target.value;
    setNewIncidentForm((prev) => ({
      ...prev,
      vehicle: { ...prev.vehicle, [field]: value },
    }));
  };

  // Convert heading (0-360 degrees) to traffic direction
  const headingToTrafficDirection = useCallback((heading) => {
    if (heading === null || heading === undefined || isNaN(heading)) return null;
    // Normalize heading to 0-360
    heading = ((heading % 360) + 360) % 360;
    // Cardinal directions: N=0, E=90, S=180, W=270
    if (heading >= 315 || heading < 45) return "NB";
    if (heading >= 45 && heading < 135) return "EB";
    if (heading >= 135 && heading < 225) return "SB";
    if (heading >= 225 && heading < 315) return "WB";
    return null;
  }, []);

  // Reverse geocode to get street name
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const response = await reverseGeocodeResponse(lat, lng);
      const data = await response.json();
      if (data?.address) {
        const { road, highway } = data.address;
        return road || highway || null;
      }
    } catch (e) {
      console.error('Reverse geocode error:', e);
    }
    return null;
  }, []);

  const requestCoordinates = useCallback(
    () =>
      new Promise((resolve) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          resolve(null);
          return;
        }

        // Check if high accuracy is in cooldown
        const HIGH_ACCURACY_COOLDOWN_MS = 5 * 60 * 1000;
        const lastFailure = window.__gpsHighAccuracyFailedAt;
        const useHighAccuracy = !lastFailure || (Date.now() - lastFailure > HIGH_ACCURACY_COOLDOWN_MS);

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude, heading } = position.coords;
            // Success - clear failure tracking if using high accuracy
            if (useHighAccuracy) {
              window.__gpsHighAccuracyFailedAt = null;
            }
            commitGpsPosition(latitude, longitude, heading, position.timestamp, position.coords.accuracy);
            resolve({ latitude, longitude, heading });
          },
          () => {
            // Mark high accuracy as failed if it was being used
            if (useHighAccuracy) {
              window.__gpsHighAccuracyFailedAt = Date.now();
            }
            resolve(null);
          },
          {
            enableHighAccuracy: useHighAccuracy,
            timeout: useHighAccuracy ? 10000 : 20000,
            maximumAge: 0
          }
        );
      }),
    [commitGpsPosition]
  );

  const showNewIncidentDialog = useCallback(() => {
    setNewIncidentForm({ source: "", incidentType: "", notes: "", vehicle: { ...VEHICLE_TEMPLATE } });
    setNewIncidentError("");
    setNewIncidentOpen(true);
  }, []);

  // Note: handleNewIncidentOpen moved after currentActiveIncident definition

  const handleNewIncidentClose = () => {
    if (!newIncidentSubmitting) {
      setNewIncidentOpen(false);
    }
  };

  const handleIncidentConflictClose = () => {
    if (!incidentConflictSaving) {
      pendingQuickCallRef.current = null;
      setIncidentConflictOpen(false);
      setIncidentConflict(null);
    }
  };

  const submitIncident = useCallback(
    async (overrides = {}) => {
      if (callIntentRef.current) return null;
      const { _quickCreate: quickCreateFlag = false, ...valueOverrides } = overrides;

      if (!user?.rangerId) {
        setIncidentBanner({ type: "error", message: "Add ranger ID to log incidents." });
        return false;
      }

      const sourceValue = valueOverrides.source ?? newIncidentForm.source;
      const incidentTypeValue = valueOverrides.incidentType ?? newIncidentForm.incidentType;
      const notesValue = valueOverrides.notes ?? newIncidentForm.notes;

      if (!sourceValue || !incidentTypeValue) {
        setNewIncidentError("SOURCE AND INCIDENT TYPE ARE REQUIRED.");
        return false;
      }

      callIntentRef.current = true;
      setNewIncidentSubmitting(true);
      setNewIncidentError("");

      try {
        const occurredAtUtc = new Date().toISOString();
        const coords = await captureIncidentCoordinates({ userId: user.id, getCurrentFix: () => incidentGpsRef.current });
        if (!coords && !window.confirm('No recent accurate GPS position is available. Create this call WITHOUT a location? Choose Cancel to retry GPS.')) {
          throw new Error('Call not created: GPS unavailable. Check location permission and retry.');
        }

        // Auto-detect street name and traffic direction
        let streetName = null;
        let trafficDirection = null;

        if (coords?.latitude && coords?.longitude) {
          // Get street name via reverse geocoding
          streetName = await reverseGeocode(coords.latitude, coords.longitude);
          // Get traffic direction from device heading
          trafficDirection = headingToTrafficDirection(coords.heading);
        }

        // Include vehicle data if this type needs it
        const vehicleData = newIncidentForm.vehicle;
        const includeVehicle = needsVehicleInfo(incidentTypeValue) &&
          vehicleData && Object.values(vehicleData).some((v) => v && v.trim());

        const payload = {
          incidentType: incidentTypeValue,
          occurredAtUtc,
          latitude: coords?.latitude ?? undefined,
          longitude: coords?.longitude ?? undefined,
          streetName: streetName || undefined,
          trafficDirection: trafficDirection || undefined,
          primaryRangerId: user.rangerId,
          notes: notesValue?.trim() || undefined,
          participantRangerIds: [user.rangerId],
          ...(includeVehicle && { vehicle: vehicleData }),
        };

        const saved = await createIncident({ ...payload, source: sourceValue });
        if (saved.pending) {
          setIncidentBanner({ type: "warning", message: saved.message + " Delivery is unconfirmed; do not enter a duplicate call." });
          setNewIncidentOpen(false);
          return null;
        }
        const response = { data: saved.incident };
        const newId = saved.incident.id;

        if (newId) {
          localIncidentIdsRef.current.add(String(newId));
          setManualActiveIncident({
            id: newId,
            cadId: response.data?.cadId || "PENDING",
            incidentType: incidentTypeValue,
            occurredAtUtc,
          });

          if (quickCreateFlag) {
            const key = String(newId);
            setQuickCreateIncidentMap((prev) => {
              if (prev[key]) {
                return prev;
              }
              return { ...prev, [key]: true };
            });
          }
        }

        setManualOnScene(true);
        handleStatusChange("busy");
        fetchIncidents();
        setNewIncidentOpen(false);
        playCue(SOUND_EVENTS.INCIDENT_CREATED);
        return newId || true; // Return the incident ID for quick incident flow
      } catch (error) {
        console.error(error);
        const apiMessage = error.response?.data;
        const message = typeof apiMessage === "string" ? apiMessage.toUpperCase() : error.message || "UNABLE TO CREATE INCIDENT.";
        setNewIncidentError(message);
        if (quickCreateFlag) setIncidentBanner({ type: "error", message });
        return false;
      } finally {
        callIntentRef.current = false;
        setNewIncidentSubmitting(false);
      }
    },
    [
      fetchIncidents,
      handleStatusChange,
      headingToTrafficDirection,
      newIncidentForm,
      playCue,
      requestCoordinates,
      reverseGeocode,
      setQuickCreateIncidentMap,
      user,
    ]
  );

  const handleNewIncidentSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      await submitIncident();
    },
    [submitIncident]
  );

  const handleQuickCreateIncident = useCallback(
    async (preset = { source: "Self", incidentType: "Assist Motorist", notes: "" }) => {
      await submitIncident({ ...preset, _quickCreate: true });
    },
    [submitIncident]
  );

  // Moved earlier to avoid forward reference issues
  const activeIncidents = useMemo(
    () => incidents.filter((incident) => !incident.occurredEndUtc),
    [incidents]
  );

  const hasOtherActiveIncidents = useCallback(
    (excludeId) => {
      if (!user?.rangerId) {
        return false;
      }

      if (manualActiveIncident && manualActiveIncident.id !== excludeId) {
        return true;
      }

      return activeIncidents.some((incident) => {
        if (excludeId && incident.id === excludeId) {
          return false;
        }

        if (!Array.isArray(incident.rangerIds)) {
          return false;
        }

        return incident.rangerIds.some(
          (id) => id?.toUpperCase() === user.rangerId.toUpperCase()
        );
      });
    },
    [activeIncidents, manualActiveIncident, user?.rangerId]
  );

  // Auto-clear participant status when an incident the user was part of gets closed/cleared
  const prevMyIncidentIdsRef = useRef(new Set());
  const previousIncidentOwnerRef = useRef(null);
  useEffect(() => {
    if (!user?.rangerId) return;

    const myCurrentIds = new Set(
      activeIncidents
        .filter(inc => Array.isArray(inc.rangerIds) && inc.rangerIds.some(id => id?.toUpperCase() === user.rangerId.toUpperCase()))
        .map(inc => inc.id)
    );

    const incidentOwner = `${user.id}:${user.rangerId}`;
    if (previousIncidentOwnerRef.current !== incidentOwner) {
      previousIncidentOwnerRef.current = incidentOwner;
      prevMyIncidentIdsRef.current = myCurrentIds;
      return;
    }
    const prevIds = prevMyIncidentIdsRef.current;

    // Check if any incident the user was in has disappeared (closed by primary)
    if (prevIds.size > 0) {
      const removedIds = [...prevIds].filter(id => !myCurrentIds.has(id));
      if (removedIds.length > 0) {
        // Always clear manualActiveIncident if it was one of the removed incidents
        if (manualActiveIncident && removedIds.includes(manualActiveIncident.id)) {
          setManualActiveIncident(null);
          setManualOnScene(false);
        }

        if (myCurrentIds.size === 0) {
          // All user's incidents are gone — restore status to available
          handleStatusChange("available");
          setManualOnScene(false);
          setManualActiveIncident(null);
          setIncidentBanner({ type: "info", message: "Incident was cleared by lead ranger. Status set to available." });
        }
      }
    }

    prevMyIncidentIdsRef.current = myCurrentIds;
  }, [activeIncidents, user?.id, user?.rangerId, handleStatusChange, manualActiveIncident]);

  const handleDetailSubmit = useCallback(async (event) => {
    event.preventDefault();
    await persistDetailChanges({}, {
      successMessage: "Incident updated.",
      soundEvent: SOUND_EVENTS.INCIDENT_UPDATED,
    });
  }, [persistDetailChanges]);

  const handleIncidentRowClick = (incident) => {
    // Check if user is the primary ranger on this incident
    const isPrimaryUser = user?.rangerId &&
      incident.primaryRangerId?.toUpperCase() === user.rangerId.toUpperCase();

    setDetailOpen(true);
    setDetailIncidentId(incident.id);
    setDetailIncident(null);
    setDetailEditMode(isPrimaryUser); // Auto-enable edit mode for primary user
    setDetailValidationErrors([]);
    loadIncidentDetail(incident.id);
  };

  const openIncidentEditor = useCallback(
    (incidentId) => {
      if (!incidentId) {
        return;
      }

      setDetailOpen(true);
      setDetailIncidentId(incidentId);
      setDetailIncident(null);
      setDetailRequiresSourceConfirm(Boolean(quickCreateIncidentMap[String(incidentId)]));
      setDetailEditMode(true);
      setDetailValidationErrors([]);
      loadIncidentDetail(incidentId);
    },
    [loadIncidentDetail, quickCreateIncidentMap]
  );

  const handleConflictFinishLater = useCallback(async () => {
    if (!incidentConflict || conflictFlightRef.current) {
      return;
    }

    const session = localStorage.getItem("rv_token");
    if (!session || (pendingQuickCallRef.current && pendingQuickCallRef.current.session !== session)) {
      pendingQuickCallRef.current = null;
      setIncidentConflictOpen(false);
      setIncidentConflict(null);
      return;
    }
    conflictFlightRef.current = true;
    setIncidentConflictSaving(true);
    try {
      const paused = await api.put(`/incidents/${incidentConflict.id}`, {
        occurredEndUtc: new Date().toISOString(),
        status: STATUS_VALUES.INCOMPLETE,
      }, { expectedSessionToken: session });
      if (paused?.status !== 204) throw new Error("Current incident could not be paused.");
      if (session !== localStorage.getItem("rv_token")) return;

      const hasOtherActive = hasOtherActiveIncidents(incidentConflict.id);
      if (!hasOtherActive) {
        setManualOnScene(false);
        handleStatusChange("available");
      }

      if (manualActiveIncident && manualActiveIncident.id === incidentConflict.id) {
        setManualActiveIncident(null);
      }

      setIncidentBanner({ type: "info", message: "Current incident saved to finish later." });
      setIncidentConflictOpen(false);
      setIncidentConflict(null);
      await fetchIncidents();
      const quickCall = pendingQuickCallRef.current;
      pendingQuickCallRef.current = null;
      if (quickCall) {
        if (quickCall.session !== localStorage.getItem("rv_token")) return;
        const newId = await submitIncident({ ...quickCall.preset, _quickCreate: true });
        if (typeof newId === "number") openIncidentEditor(newId);
      } else {
        showNewIncidentDialog();
      }
    } catch (error) {
      console.error(error);
      setIncidentBanner({ type: "error", message: "Unable to pause the current incident." });
    } finally {
      conflictFlightRef.current = false;
      setIncidentConflictSaving(false);
    }
  }, [fetchIncidents, handleStatusChange, hasOtherActiveIncidents, incidentConflict, manualActiveIncident, showNewIncidentDialog, submitIncident, openIncidentEditor]);

  const beginIncidentEdit = useCallback(() => {
    if (!detailIncidentId) {
      return;
    }
    const key = String(detailIncidentId);
    setDetailRequiresSourceConfirm(Boolean(quickCreateIncidentMap[key]));
    setDetailEditMode(true);
  }, [detailIncidentId, quickCreateIncidentMap]);

  const handleDetailClose = () => {
    if (!detailSaving) {
      setDetailOpen(false);
      setDetailValidationErrors([]);
      setDetailRequiresSourceConfirm(false);
      setAbandonedPrompt({ open: false, active: false });
    }
  };

  const handleDetailFieldChange = (field) => (event) => {
    const value = event.target.value;
    const nextValue = field === "notes" && typeof value === "string" ? value.toUpperCase() : value;
    setDetailForm((prev) => {
      if (!prev) {
        return prev;
      }

      if (field === "incidentType") {
        const next = {
          ...prev,
          incidentType: value,
        };
        const previousServices = Array.isArray(prev.servicesProvided)
          ? prev.servicesProvided.filter(Boolean)
          : [];
        if (isDebrisType(value)) {
          const withoutDebris = previousServices.filter((service) => service !== DEBRIS_DISPOSITION);
          next.servicesProvided = [DEBRIS_DISPOSITION, ...withoutDebris];
        } else if (isDebrisType(prev.incidentType)) {
          next.servicesProvided = previousServices.filter((service) => service !== DEBRIS_DISPOSITION);
        }
        return next;
      }

      return { ...prev, [field]: nextValue };
    });

    if (field === "incidentType" && isDebrisType(value)) {
      clearValidationFlag("servicesProvided");
    }

    if (field === "notes") {
      if (nextValue?.trim()) {
        clearValidationFlag(field);
      }
      return;
    }

    if (nextValue) {
      clearValidationFlag(field);
    }
  };

  const handleDetailServicesChange = (event) => {
    const {
      target: { value },
    } = event;
    const selections = typeof value === "string" ? value.split(",") : value;
    setDetailForm((prev) =>
      prev
        ? {
            ...prev,
            servicesProvided: selections,
          }
        : prev
    );

    if (Array.isArray(selections) && selections.filter(Boolean).length > 0) {
      clearValidationFlag("servicesProvided");
    }
  };

  const handleDetailVehicleChange = (field) => (event) => {
    const value = event.target.value;
    setDetailForm((prev) =>
      prev
        ? {
            ...prev,
            vehicle: {
              ...prev.vehicle,
              [field]: value,
            },
          }
        : prev
    );
  };

  const handleFinishLater = useCallback(async () => {
    if (!detailIncidentId || !detailForm) {
      return;
    }

    const missing = computeMissingClearFields();
    setDetailValidationErrors(missing);

    const success = await persistDetailChanges(
      {
        occurredEndUtc: new Date().toISOString(),
        status: STATUS_VALUES.INCOMPLETE,
      },
      {
        successMessage: missing.length
          ? "Incident saved with missing info. Please finish later."
          : "Incident marked for review before clearing.",
      }
    );

    if (success) {
      if (!hasOtherActiveIncidents(detailIncidentId)) {
        handleStatusChange("available");
        setManualOnScene(false);
      }
      if (manualActiveIncident && manualActiveIncident.id === detailIncidentId) {
        setManualActiveIncident(null);
      }
      setDetailValidationErrors([]);
      setDetailOpen(false);
    }
  }, [computeMissingClearFields, detailForm, detailIncidentId, handleStatusChange, hasOtherActiveIncidents, manualActiveIncident, persistDetailChanges]);

  const handleClearIncident = useCallback(async () => {
    if (!detailIncidentId || !detailForm) {
      return;
    }

    const missing = computeMissingClearFields();
    if (missing.length > 0) {
      setDetailValidationErrors(missing);
      if (!detailEditMode) {
        setDetailEditMode(true);
      }
      return;
    }

    setDetailValidationErrors([]);

    const success = await persistDetailChanges(
      {
        occurredEndUtc: new Date().toISOString(),
        status: STATUS_VALUES.CLEARED,
      },
      {
        successMessage: "Incident cleared.",
        soundEvent: SOUND_EVENTS.INCIDENT_CLEARED,
      }
    );

    if (success) {
      if (!hasOtherActiveIncidents(detailIncidentId)) {
        handleStatusChange("available");
        setManualOnScene(false);
      }
      if (manualActiveIncident && manualActiveIncident.id === detailIncidentId) {
        setManualActiveIncident(null);
      }
      setDetailValidationErrors([]);
      setDetailOpen(false);
    }
  }, [computeMissingClearFields, detailEditMode, detailForm, detailIncidentId, handleStatusChange, hasOtherActiveIncidents, manualActiveIncident, persistDetailChanges]);

  // Abandoned vehicle handler - validates make/model/color and prompts for shoulder info
  const handleAbandonedClick = useCallback(() => {
    if (!detailIncidentId || !detailForm) {
      return;
    }

    // Enter abandoned mode (disables disposition, shows info)
    setAbandonedPrompt({ open: false, active: true });

    // Check if make, model, and color are filled
    const vehicleMissing = [];
    if (!detailForm.vehicle?.make?.trim()) {
      vehicleMissing.push("make");
    }
    if (!detailForm.vehicle?.model?.trim()) {
      vehicleMissing.push("model");
    }
    if (!detailForm.vehicle?.color?.trim()) {
      vehicleMissing.push("color");
    }

    if (vehicleMissing.length > 0) {
      setDetailValidationErrors(vehicleMissing.map(f => `vehicle.${f}`));
      setIncidentBanner({
        type: "error",
        message: "MISSING REQUIRED VEHICLE FIELDS: MAKE, MODEL, COLOR"
      });
      return;
    }

    // Open the prompt to ask for shoulder info
    setAbandonedPrompt({ open: true, active: true });
  }, [detailForm, detailIncidentId]);

  const handleAbandonedConfirm = useCallback(async (shoulderInfo) => {
    if (!detailIncidentId || !detailForm) {
      return;
    }

    // Build notes with shoulder info
    const currentNotes = detailForm.notes?.trim() || "";
    const abandonedNote = `ABANDONED VEHICLE - ${shoulderInfo.toUpperCase()}`;
    const newNotes = currentNotes ? `${abandonedNote}\n${currentNotes}` : abandonedNote;

    // Set disposition to ABANDONED
    const newServices = new Set(detailForm.servicesProvided || []);
    newServices.add("ABANDONED");

    // Update the form first
    setDetailForm((prev) => ({
      ...prev,
      notes: newNotes,
      servicesProvided: Array.from(newServices),
    }));

    setAbandonedPrompt({ open: false, active: false });

    // Save to abandoned vehicles database for tracking
    try {
      const incidentLocation = detailIncident?.latitude && detailIncident?.longitude
        ? { lat: detailIncident.latitude, lng: detailIncident.longitude }
        : gpsCoords
        ? { lat: gpsCoords.latitude, lng: gpsCoords.longitude }
        : null;

      if (incidentLocation) {
        await createAbandonedVehicle({
          incidentId: detailIncidentId,
          latitude: incidentLocation.lat,
          longitude: incidentLocation.lng,
          locationDescription: detailIncident?.location || "",
          shoulderLocation: shoulderInfo,
          vehicleMake: detailForm.vehicle?.make || "",
          vehicleModel: detailForm.vehicle?.model || "",
          vehicleColor: detailForm.vehicle?.color || "",
          vehiclePlate: detailForm.vehicle?.plate || null,
          vehicleState: detailForm.vehicle?.state || null,
          vehicleYear: detailForm.vehicle?.year || null,
          notes: newNotes,
        });
        console.log("Abandoned vehicle saved to tracking database");
      }
    } catch (err) {
      console.error("Failed to save abandoned vehicle to tracking database:", err);
      // Don't block the main flow if this fails
    }

    // Now clear the incident
    const success = await persistDetailChanges(
      {
        notes: newNotes,
        servicesProvided: Array.from(newServices),
        occurredEndUtc: new Date().toISOString(),
        status: STATUS_VALUES.CLEARED,
      },
      {
        successMessage: "Abandoned vehicle logged and incident cleared.",
        soundEvent: SOUND_EVENTS.INCIDENT_CLEARED,
      }
    );

    if (success) {
      if (!hasOtherActiveIncidents(detailIncidentId)) {
        handleStatusChange("available");
        setManualOnScene(false);
      }
      if (manualActiveIncident && manualActiveIncident.id === detailIncidentId) {
        setManualActiveIncident(null);
      }
      setDetailValidationErrors([]);
      setDetailOpen(false);
    }
  }, [detailForm, detailIncident, detailIncidentId, gpsCoords, handleStatusChange, hasOtherActiveIncidents, manualActiveIncident, persistDetailChanges]);

  const handleAbandonedCancel = useCallback(() => {
    setAbandonedPrompt({ open: false, active: false });
  }, []);

  const isTransportActiveForIncident = transportActive && detailIncidentId && transportIncidentId === detailIncidentId;

  const openTransportDialogFor = useCallback((mode) => {
    if (mode === "start") {
      setTransportAddress("");
      setTransportStartAddress("");
    }
    setTransportDialog({ open: true, mode });
  }, []);

  const closeTransportDialog = useCallback(() => {
    setTransportDialog({ open: false, mode: "start" });
  }, []);

  const applyTransportNotes = useCallback((existingNotes, address) => {
    const timeLabel = formatTransportTime();
    const trimmedAddress = address?.trim().toUpperCase();
    const note = trimmedAddress
      ? `TRANSPORTING TO ${trimmedAddress} AT ${timeLabel}`
      : `TRANSPORTING AT ${timeLabel}`;
    return appendNoteLine(existingNotes, note);
  }, []);

  const handleStartTransport = useCallback(async () => {
    if (!detailIncidentId || !detailForm) {
      return;
    }

    const trimmedAddress = transportAddress.trim().toUpperCase();
    const updatedNotes = applyTransportNotes(detailForm.notes, trimmedAddress);

    setDetailForm((prev) => prev
      ? {
          ...prev,
          notes: updatedNotes,
        }
      : prev);

    const success = await persistDetailChanges(
      {
        notes: updatedNotes,
      },
      {
        successMessage: "Transport started.",
        soundEvent: SOUND_EVENTS.INCIDENT_UPDATED,
      }
    );

    if (success) {
      markStatusIntent();
      setTransportActive(true);
      setTransportIncidentId(detailIncidentId);
      setTransportStartAddress(trimmedAddress);
      setTransportAddress(trimmedAddress);
      setUserEnroute(false);
      setManualOnScene(false);
      setUnitStatus("busy");
      closeTransportDialog();
    }
  }, [applyTransportNotes, closeTransportDialog, detailForm, detailIncidentId, persistDetailChanges, transportAddress, markStatusIntent]);

  const handleEndTransport = useCallback(async (overrideAddress) => {
    if (!detailIncidentId || !detailForm) {
      return;
    }

    const addressValue = typeof overrideAddress === "string" ? overrideAddress : transportAddress;
    const trimmedAddress = addressValue.trim().toUpperCase();
    let updatedNotes = detailForm.notes;

    if (!transportStartAddress && trimmedAddress) {
      updatedNotes = appendNoteLine(updatedNotes, `TRANSPORT DESTINATION: ${trimmedAddress}`);
    }

    updatedNotes = appendNoteLine(updatedNotes, `END OF TRANSPORT AT ${formatTransportTime()}`);

    const updatedServices = new Set(detailForm.servicesProvided || []);
    updatedServices.add("COURTESY RIDE");

    setDetailForm((prev) => prev
      ? {
          ...prev,
          notes: updatedNotes,
          servicesProvided: Array.from(updatedServices),
        }
      : prev);

    const success = await persistDetailChanges(
      {
        notes: updatedNotes,
        servicesProvided: Array.from(updatedServices),
      },
      {
        successMessage: "Transport ended.",
        soundEvent: SOUND_EVENTS.INCIDENT_UPDATED,
      }
    );

    if (success) {
      markStatusIntent();
      setTransportActive(false);
      setTransportIncidentId(null);
      setTransportStartAddress("");
      if (trimmedAddress) {
        setTransportAddress(trimmedAddress);
      }
      closeTransportDialog();
    }
  }, [closeTransportDialog, detailForm, detailIncidentId, persistDetailChanges, transportAddress, transportStartAddress, markStatusIntent]);

  const openDeleteIncidentDialog = (incident) => {
    if (!incident) {
      return;
    }
    setDeletePrompt({ open: true, incident });
  };

  const handleDeleteDialogClose = () => {
    if (!deleteSubmitting) {
      setDeletePrompt({ open: false, incident: null });
    }
  };

  const handleConfirmDeleteIncident = useCallback(async () => {
    if (!deletePrompt.incident?.id) {
      return;
    }

    setDeleteSubmitting(true);
    try {
      await api.delete(`/incidents/${deletePrompt.incident.id}`);

      if (detailIncidentId === deletePrompt.incident.id) {
        setDetailOpen(false);
        setDetailIncident(null);
      }

      if (manualActiveIncident && manualActiveIncident.id === deletePrompt.incident.id) {
        setManualActiveIncident(null);
        setManualOnScene(false);
      }

      await fetchIncidents();
      setQuickCreateIncidentMap((prev) => {
        const key = String(deletePrompt.incident.id);
        if (!prev[key]) {
          return prev;
        }
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setDetailRequiresSourceConfirm(false);
    } catch (error) {
      console.error(error);
      setIncidentBanner({ type: "error", message: "Unable to archive call." });
    } finally {
      setDeleteSubmitting(false);
      setDeletePrompt({ open: false, incident: null });
    }
  }, [deletePrompt.incident, detailIncidentId, fetchIncidents, manualActiveIncident]);

  const acknowledgeIncidentStatus = useCallback(async (incidentId) => {
    setStatusCheckError('');
    try {
      return await acknowledgeStatusCheck({
        api, incidentId, currentSession: () => localStorage.getItem('rv_token'),
        apply: ({ iso, timestamp }) => {
          setStatusAcknowledgements(previous => ({ ...previous, [String(incidentId)]: timestamp }));
          setIncidents(previous => previous.map(incident => incident.id === incidentId ? { ...incident, statusCheckAcknowledgedUtc: iso } : incident));
        },
      });
    } catch (error) {
      setStatusCheckError('STATUS CHECK CONFIRMATION WAS NOT RECEIVED. RETRY WHEN CONNECTED.');
      return false;
    }
  }, []);

  const handleStatusCheckAcknowledge = useCallback(async () => {
    const incidentId = statusModalIncident?.incident?.id;
    if (!incidentId) {
      return;
    }

    const acknowledged = await acknowledgeIncidentStatus(incidentId);
    if (acknowledged) {
      statusModalPinnedRef.current = null;
      if (statusModalIncident?.incident?.primaryRangerId) {
        clearMissedCheckReport(incidentId, statusModalIncident.incident.primaryRangerId);
      }
      if (user?.rangerId) {
        clearMissedCheckReport(incidentId, user.rangerId);
      }
      setStatusModalIncident(null);
    }
  }, [acknowledgeIncidentStatus, clearMissedCheckReport, statusModalIncident, user?.rangerId]);

  const handleStatusCheckClear = useCallback(async () => {
    const incidentId = statusModalIncident?.incident?.id;
    if (!incidentId) {
      return;
    }

    const acknowledged = await acknowledgeIncidentStatus(incidentId);
    if (acknowledged) {
      statusModalPinnedRef.current = null;
      if (statusModalIncident?.incident?.primaryRangerId) {
        clearMissedCheckReport(incidentId, statusModalIncident.incident.primaryRangerId);
      }
      if (user?.rangerId) {
        clearMissedCheckReport(incidentId, user.rangerId);
      }
      setStatusModalIncident(null);
      openIncidentEditor(incidentId);
    }
  }, [acknowledgeIncidentStatus, clearMissedCheckReport, openIncidentEditor, statusModalIncident, user?.rangerId]);

  // Clear another user's status check - finds the incident for the ranger and acknowledges it
  const handleClearOtherUserStatusCheck = useCallback(async (rangerId) => {
    if (!rangerId) return;

    const normalizedRangerId = rangerId.toUpperCase();

    // Find the incident(s) associated with this ranger that need status check
    const rangerIncidents = activeIncidents.filter((incident) => {
      // Check if this ranger is on the incident
      const isPrimary = incident.primaryRangerId?.toUpperCase() === normalizedRangerId;
      const isParticipant = Array.isArray(incident.rangerIds) &&
        incident.rangerIds.some((id) => id?.toUpperCase() === normalizedRangerId);

      return isPrimary || isParticipant;
    });

    if (rangerIncidents.length === 0) {
      setIncidentBanner({ type: "warning", message: `NO ACTIVE INCIDENT FOUND FOR ${rangerId}` });
      return;
    }

    // Acknowledge status check for all incidents this ranger is on
    let anyAcknowledged = false;
    for (const incident of rangerIncidents) {
      if (statusCheckIncidents.includes(incident.id)) {
        const acknowledged = await acknowledgeIncidentStatus(incident.id);
        if (acknowledged) {
          anyAcknowledged = true;
          clearMissedCheckReport(incident.id, rangerId);
        }
      }
    }

    if (anyAcknowledged) {
      setIncidentBanner({ type: "success", message: `STATUS CHECK CLEARED FOR ${rangerId}` });
    }
  }, [activeIncidents, acknowledgeIncidentStatus, clearMissedCheckReport, statusCheckIncidents]);

  const clearEmergencyCountdown = useCallback(() => {
    if (emergencyCountdownIntervalRef.current) {
      window.clearInterval(emergencyCountdownIntervalRef.current);
      emergencyCountdownIntervalRef.current = null;
    }
  }, []);

  const closeEmergencyConfirm = useCallback(() => {
    clearEmergencyCountdown();
    setEmergencyConfirmOpen(false);
    setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
  }, [clearEmergencyCountdown]);

  const performActivateEmergency = useCallback(async () => {
    if (emergencyActivating) {
      return;
    }
    if (unitStatus === "emergency") {
      setIncidentBanner({ type: "warning", message: "YOU ARE ALREADY IN EMERGENCY STATUS" });
      return;
    }

    setEmergencyActivating(true);
    try {
      let location = gpsLocation || null;
      let lat = gpsCoords?.latitude ?? null;
      let lon = gpsCoords?.longitude ?? null;

      await submitEmergencyActivation(api, {
        location,
        latitude: lat,
        longitude: lon,
      }, emergencyToken, user?.id);
      if (emergencyOwnerRef.current !== user?.id) return;

      preEmergencyStatusRef.current = unitStatus;
      setUnitStatus("emergency");
      startEmergencyAudio();
      setIncidentBanner({ type: "error", message: "EMERGENCY ALERT SAVED." });
    } catch (error) {
      if (emergencyOwnerRef.current !== user?.id) return;
      // Keep request credentials and PINs out of console diagnostics.
      setIncidentBanner({ type: "error", message: "EMERGENCY CONFIRMATION NOT RECEIVED. CHECK DISPATCH BEFORE RETRYING." });
    } finally {
      if (emergencyOwnerRef.current === user?.id) setEmergencyActivating(false);
    }
  }, [emergencyActivating, gpsLocation, gpsCoords, startEmergencyAudio, unitStatus, user?.id, emergencyToken]);

  useEffect(() => {
    if (!emergencyConfirmOpen) {
      return;
    }

    setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
    clearEmergencyCountdown();
    emergencyCountdownIntervalRef.current = window.setInterval(() => {
      setEmergencyCountdown((prev) => {
        if (prev <= 1) {
          clearEmergencyCountdown();
          setEmergencyConfirmOpen(false);
          setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
          void performActivateEmergency();
          return EMERGENCY_CONFIRM_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearEmergencyCountdown();
    };
  }, [clearEmergencyCountdown, emergencyConfirmOpen, performActivateEmergency]);

  useEffect(() => {
    return () => {
      clearEmergencyCountdown();
    };
  }, [clearEmergencyCountdown]);

  // Activate emergency - called when user presses EMERGENCY button
  const handleActivateEmergency = useCallback(() => {
    if (unitStatus === "emergency") {
      setIncidentBanner({ type: "warning", message: "YOU ARE ALREADY IN EMERGENCY STATUS" });
      return;
    }
    if (emergencyConfirmOpen || emergencyActivating) {
      return;
    }
    setEmergencyCountdown(EMERGENCY_CONFIRM_SECONDS);
    setEmergencyConfirmOpen(true);
  }, [unitStatus, emergencyConfirmOpen, emergencyActivating]);

  // Review first; a frozen confirmation is submitted only after a second action.
  const handleClearEmergency = useCallback(async (rangerId) => {
    setSelfCancelOpen(false);
    setSupervisorClear({ open: true, rangerId: rangerId || null });
    await emergencyClearance.prepare(rangerId ? 'ranger' : 'all', rangerId);
  }, [emergencyClearance.prepare]);

  const confirmSupervisorClear = useCallback(async () => {
    setSupervisorClearLoading(true);
    try {
      const receipt = await emergencyClearance.submit({ expectedScope: supervisorClear.rangerId ? 'ranger' : 'all',
        expectedRangerId: supervisorClear.rangerId });
      if (!receipt || emergencyOwnerRef.current !== user?.id) return;
      setSupervisorClear({ open: false, rangerId: null });
      await fetchIncidents();
      setIncidentBanner({ type: 'success', message: 'EMERGENCY CLEARANCE SAVED.' });
    } catch { /* The review retains confirmation uncertainty and never retries automatically. */ }
    finally { if (emergencyOwnerRef.current === user?.id) setSupervisorClearLoading(false); }
  }, [emergencyClearance.submit, supervisorClear.rangerId, user?.id, fetchIncidents]);

  // Self-cancel own emergency
  const handleSelfCancelEmergency = useCallback(async () => {
    if (!selfCancelReason) {
      setSelfCancelError("Please select a reason.");
      return;
    }
    if (!selfCancelPin) {
      setSelfCancelError("Please enter your PIN.");
      return;
    }
    setSelfCancelLoading(true);
    setSelfCancelError("");
    try {
      const receipt = await emergencyClearance.submit({ expectedScope: 'self', reason: selfCancelReason, pin: selfCancelPin,
        restoreStatus: preEmergencyStatusRef.current || 'available' });
      if (!receipt) return;
      if (emergencyOwnerRef.current !== user?.id) return;
      setSelfCancelOpen(false);
      setSelfCancelReason("");
      setSelfCancelPin("");
      stopEmergencyAudio();
      // Clearance and contextual restoration are one server transaction.
      await fetchIncidents();
      setIncidentBanner({ type: "success", message: "YOUR EMERGENCY HAS BEEN CANCELLED" });
    } catch (error) {
      if (emergencyOwnerRef.current !== user?.id) return;
      // The review shows the server error without logging a PIN-bearing request.
      const msg = error.response?.data?.message || "Failed to cancel emergency. Check your PIN.";
      setSelfCancelError(typeof msg === "string" ? msg : "Failed to cancel emergency.");
    } finally {
      if (emergencyOwnerRef.current === user?.id) setSelfCancelLoading(false);
    }
  }, [selfCancelReason, selfCancelPin, fetchIncidents, stopEmergencyAudio, user?.id, emergencyClearance.submit]);

  // Take responsibility for another ranger's emergency
  const handleTakeEmergencyResponsibility = useCallback(async (rangerId) => {
    setResponsibilityLoading((prev) => ({ ...prev, [rangerId]: true }));
    try {
      await api.post(`/status/emergency/${rangerId}/take-responsibility`);
      setResponsibilityTaken((prev) => ({ ...prev, [rangerId]: true }));
      setIncidentBanner({ type: "success", message: `RESPONSIBILITY TAKEN FOR RANGER ${rangerId}` });
    } catch (error) {
      console.error("Failed to take responsibility", error);
      const msg = error.response?.data?.message || "FAILED TO TAKE RESPONSIBILITY";
      setIncidentBanner({ type: "error", message: typeof msg === "string" ? msg : "FAILED TO TAKE RESPONSIBILITY" });
    } finally {
      setResponsibilityLoading((prev) => ({ ...prev, [rangerId]: false }));
    }
  }, []);

  // Waze alert handlers - RESPOND dispatches ranger but does NOT create incident
  const handleWazeRespondAlert = useCallback(async (alertId) => {
    setWazeClaimingId(alertId);
    try {
      const response = await api.post(`/waze/alerts/${alertId}/respond`);
      await fetchWazeAlerts();
      setIncidentBanner({
        type: "success",
        message: `EN ROUTE TO ${response.data?.incidentType || "WAZE ALERT"}.`
      });
      // The claim remains valid while an emergency takes priority over status.
      if (response.data?.unitStatusApplied !== false) {
        markStatusIntent();
        setUserEnroute(true);
        setManualOnScene(false);
      } else {
        setIncidentBanner({ type: "info", message: "ALERT CLAIMED. YOUR EMERGENCY REMAINS ACTIVE." });
      }
    } catch (error) {
      console.error("Unable to respond to Waze alert.", error);
      setIncidentBanner({ type: "error", message: "UNABLE TO RESPOND TO ALERT." });
    } finally {
      setWazeClaimingId(null);
    }
  }, [fetchWazeAlerts, markStatusIntent]);

  const handleWazeReleaseAlert = useCallback(async (alertId) => {
    setWazeClaimingId(alertId);
    try {
      await api.post(`/waze/alerts/${alertId}/release`);
      await fetchWazeAlerts();
      setIncidentBanner({ type: "info", message: "WAZE ALERT RELEASED." });
      // Reset enroute status since we're no longer responding to this alert
      markStatusIntent();
      setUserEnroute(false);
      setManualOnScene(false);
      setEnrouteDestinationId(null);
    } catch (error) {
      console.error("Unable to release Waze alert.", error);
      setIncidentBanner({ type: "error", message: "UNABLE TO RELEASE ALERT." });
    } finally {
      setWazeClaimingId(null);
    }
  }, [fetchWazeAlerts, markStatusIntent]);

  const handleWazeClearAlert = useCallback(async (alertId, reason) => {
    setWazeClaimingId(alertId);
    try {
      await api.post(`/waze/alerts/${alertId}/clear`, { reason: reason || "UTL" });
      await fetchWazeAlerts();
      setIncidentBanner({ type: "success", message: "WAZE ALERT CLEARED." });
      // Reset enroute status since we're done with this alert
      markStatusIntent();
      setUserEnroute(false);
      setManualOnScene(false);
      setEnrouteDestinationId(null);
    } catch (error) {
      console.error("Unable to clear Waze alert.", error);
      setIncidentBanner({ type: "error", message: "UNABLE TO CLEAR ALERT." });
    } finally {
      setWazeClaimingId(null);
    }
  }, [fetchWazeAlerts, markStatusIntent]);

  const wazeConversionsInFlight = useRef(new Set());
  const handleWazeConvertToIncident = useCallback(async (alertId) => {
    if (wazeConversionsInFlight.current.has(alertId)) return;
    wazeConversionsInFlight.current.add(alertId);
    setWazeClaimingId(alertId);
    try {
      const alert = wazeAlerts.find(item => item.id === alertId);
      if (!alert) throw new Error("Alert is no longer in the current list. Refresh Waze alerts.");
      const saved = await createWazeIncident(alert, { onScene: true });
      if (saved.pending) { setIncidentBanner({ type: "warning", message: saved.message + " Delivery is unconfirmed; do not enter a duplicate call." }); return; }
      await fetchWazeAlerts();
      await fetchIncidents();
      const newIncidentId = saved.incident?.id;
      if (newIncidentId) {
        localIncidentIdsRef.current.add(newIncidentId);
      }
      if (saved.replayed || saved.incident?.isArchived) {
        setIncidentBanner({ type: "info", message: `WAZE CALL ALREADY CONFIRMED: ${saved.incident?.cadId || ""}. UNIT STATUS UNCHANGED.` });
        if (newIncidentId && !saved.incident?.isArchived) openIncidentEditor(newIncidentId);
        return;
      }
      playCue(SOUND_EVENTS.NEW_INCIDENT);
      setIncidentBanner({
        type: "success",
        message: `INCIDENT CREATED: ${saved.incident?.cadId || "UNKNOWN"}`
      });
      // Transition from enroute to on-scene with this new incident
      if (saved.response?.unitStatusApplied) {
        setUserEnroute(false); setManualOnScene(true);
        setManualActiveIncident(newIncidentId ? { id: newIncidentId } : null); setEnrouteDestinationId(null);
      }
      // Open the incident editor
      if (newIncidentId) {
        openIncidentEditor(newIncidentId);
      }
    } catch (error) {
      console.error("Unable to convert Waze alert.", error);
      setIncidentBanner({ type: "error", message: "UNABLE TO CONVERT ALERT." });
    } finally {
      wazeConversionsInFlight.current.delete(alertId);
      setWazeClaimingId(null);
    }
  }, [fetchWazeAlerts, fetchIncidents, playCue, openIncidentEditor, wazeAlerts]);

  // ON SCENE handler - user is within 0.25 miles, create incident directly with direction from Magvar
  const handleWazeOnScene = useCallback(async (alert) => {
    if (wazeConversionsInFlight.current.has(alert.id)) return;
    wazeConversionsInFlight.current.add(alert.id);
    setWazeClaimingId(alert.id);
    try {
      // Convert Magvar (0-360) to traffic direction (NB, SB, EB, WB)
      let trafficDirection = null;
      if (alert.magvar !== null && alert.magvar !== undefined) {
        const heading = ((alert.magvar % 360) + 360) % 360;
        if (heading >= 315 || heading < 45) trafficDirection = "NB";
        else if (heading >= 45 && heading < 135) trafficDirection = "EB";
        else if (heading >= 135 && heading < 225) trafficDirection = "SB";
        else if (heading >= 225 && heading < 315) trafficDirection = "WB";
      }

      const saved = await createWazeIncident(alert, {
        onScene: true,
        trafficDirection,
      });
      if (saved.pending) { setIncidentBanner({ type: "warning", message: saved.message + " Delivery is unconfirmed; do not enter a duplicate call." }); return; }
      await fetchWazeAlerts();
      await fetchIncidents();
      const newIncidentId = saved.incident?.id;
      if (newIncidentId) {
        localIncidentIdsRef.current.add(newIncidentId);
      }
      if (saved.replayed || saved.incident?.isArchived) {
        setIncidentBanner({ type: "info", message: `WAZE CALL ALREADY CONFIRMED: ${saved.incident?.cadId || ""}. UNIT STATUS UNCHANGED.` });
        if (newIncidentId && !saved.incident?.isArchived) openIncidentEditor(newIncidentId);
        return;
      }
      playCue(SOUND_EVENTS.NEW_INCIDENT);
      setIncidentBanner({
        type: "success",
        message: saved.response?.unitStatusApplied ? `ON SCENE - INCIDENT CREATED: ${saved.incident?.cadId || "UNKNOWN"}` : `CALL SAVED: ${saved.incident?.cadId || "UNKNOWN"}. UNIT STATUS UNCHANGED; VERIFY YOUR STATUS.`
      });
      // Immediately on-scene (skip enroute)
      if (saved.response?.unitStatusApplied) {
        setUserEnroute(false); setManualOnScene(true);
        setManualActiveIncident(newIncidentId ? { id: newIncidentId } : null); setEnrouteDestinationId(null);
      }
      // Open the incident editor
      if (newIncidentId) {
        openIncidentEditor(newIncidentId);
      }
    } catch (error) {
      console.error("Unable to create on-scene incident from Waze alert.", error);
      setIncidentBanner({ type: "error", message: "UNABLE TO CREATE ON-SCENE INCIDENT." });
    } finally {
      wazeConversionsInFlight.current.delete(alert.id);
      setWazeClaimingId(null);
    }
  }, [fetchWazeAlerts, fetchIncidents, playCue, openIncidentEditor]);

  // Waze city filter handlers - selecting city clears distance filter
  const handleWazeCityToggle = useCallback((city) => {
    setWazeCityFilter((prev) => {
      const next = prev.includes(city)
        ? prev.filter((c) => c !== city)
        : [...prev, city];
      if (typeof window !== "undefined") {
        window.localStorage.setItem("wazeCityFilter", JSON.stringify(next));
      }
      // If adding a city, clear distance filter (can only use one or the other)
      if (next.length > 0) {
        setWazeDistanceFilter(0);
        if (typeof window !== "undefined") {
          window.localStorage.setItem("wazeDistanceFilter", JSON.stringify(0));
        }
      }
      return next;
    });
  }, []);

  // Waze type filter handler
  const handleWazeTypeToggle = useCallback((type) => {
    setWazeTypeFilter((prev) => {
      const next = prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type];
      if (typeof window !== "undefined") {
        window.localStorage.setItem("wazeTypeFilter", JSON.stringify(next));
      }
      return next;
    });
  }, []);

  // Waze distance filter handler - selecting distance clears city filter
  const handleWazeDistanceChange = useCallback((distance) => {
    setWazeDistanceFilter(distance);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("wazeDistanceFilter", JSON.stringify(distance));
    }
    // If setting a distance, clear city filter (can only use one or the other)
    if (distance > 0) {
      setWazeCityFilter([]);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("wazeCityFilter");
      }
    }
  }, []);

  // Calculate distance between two GPS coordinates in miles
  const calculateDistanceMiles = useCallback((lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => deg * Math.PI / 180;
    const R = 3958.8; // Earth's radius in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  const filteredWazeAlerts = useMemo(() => {
    let filtered = wazeAlerts;

    // Filter by city
    if (wazeCityFilter.length > 0) {
      filtered = filtered.filter((alert) => {
        const city = (alert.city || "").toLowerCase();
        return wazeCityFilter.some((filterCity) =>
          city.includes(filterCity.toLowerCase())
        );
      });
    }

    // Filter by type (using category mapping)
    if (wazeTypeFilter.length > 0) {
      filtered = filtered.filter((alert) => {
        const category = getWazeAlertCategory(alert.type, alert.subtype);
        return wazeTypeFilter.includes(category);
      });
    }

    // Calculate distance for each alert and filter by distance if enabled
    filtered = filtered.map((alert) => {
      let distanceMiles = null;
      if (gpsCoords?.latitude && gpsCoords?.longitude && alert.latitude && alert.longitude) {
        distanceMiles = calculateDistanceMiles(
          gpsCoords.latitude,
          gpsCoords.longitude,
          alert.latitude,
          alert.longitude
        );
      }
      return { ...alert, distanceMiles };
    });

    // Filter by distance (if we have GPS coords and distance > 0)
    if (wazeDistanceFilter > 0) {
      filtered = filtered.filter((alert) => {
        if (alert.distanceMiles === null) return false;
        return alert.distanceMiles <= wazeDistanceFilter;
      });
    }

    // Sort by distance (closest first), alerts without distance at the end
    filtered.sort((a, b) => {
      if (a.distanceMiles === null && b.distanceMiles === null) return 0;
      if (a.distanceMiles === null) return 1;
      if (b.distanceMiles === null) return -1;
      return a.distanceMiles - b.distanceMiles;
    });

    return filtered;
  }, [wazeAlerts, wazeCityFilter, wazeTypeFilter, wazeDistanceFilter, gpsCoords, calculateDistanceMiles]);

  const sectionPadding = tabletMode ? 3 : 2.5;
  const tableSize = tabletMode ? "medium" : "small";
  const tableSx = useMemo(
    () => ({
      "& thead th": {
        fontSize: tabletMode ? "1.15rem" : "1rem",
        letterSpacing: "0.1em",
        fontWeight: 800,
        textTransform: "uppercase",
      },
      "& tbody td": {
        fontSize: tabletMode ? "1.05rem" : "0.95rem",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      },
    }),
    [tabletMode]
  );
  const buttonStyles = {
    flexGrow: 1,
    fontSize: tabletMode ? "1.2rem" : "1rem",
    fontWeight: 600,
    py: tabletMode ? 2.5 : 1.5,
    minHeight: tabletMode ? 72 : 48,
  };

  // Count active Waze filters for badge
  const wazeFilterCount = useMemo(() => {
    let count = 0;
    if (wazeCityFilter.length > 0) count++;
    if (wazeTypeFilter.length > 0) count++;
    if (wazeDistanceFilter > 0) count++;
    return count;
  }, [wazeCityFilter, wazeTypeFilter, wazeDistanceFilter]);

  const tabletModeToggle = useMemo(() => (
    <Tooltip title={tabletMode ? "Tablet Mode ON — tap to disable" : "Tablet Mode — tap to enable larger buttons"}>
      <IconButton
        color={tabletMode ? "primary" : "inherit"}
        onClick={() => setTabletMode((prev) => !prev)}
        size="small"
        sx={{
          border: tabletMode ? 2 : 1,
          borderColor: tabletMode ? "primary.main" : "divider",
          borderStyle: "solid",
          borderRadius: 1.5,
          p: 0.75,
          backgroundColor: tabletMode ? "action.selected" : "transparent",
          "&:hover": { backgroundColor: tabletMode ? "action.hover" : "action.hover" },
        }}
      >
        <TabletAndroidIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ), [tabletMode]);

  const tabletModeToolbar = tabletModeToggle;

  // Note: activeIncidents moved earlier in file to avoid forward reference issues

  useEffect(() => {
    setStatusAcknowledgements((prev) => {
      const activeIds = new Set(activeIncidents.map((incident) => incident.id));
      let changed = false;
      const next = Object.entries(prev).reduce((acc, [incidentId, timestamp]) => {
        if (activeIds.has(Number(incidentId))) {
          acc[incidentId] = timestamp;
        } else {
          changed = true;
        }
        return acc;
      }, {});

      return changed ? next : prev;
    });
  }, [activeIncidents]);
  const statusCheckIncidentSet = useMemo(
    () => new Set(statusCheckIncidents),
    [statusCheckIncidents]
  );
  const statusCheckRangerSet = useMemo(() => {
    const normalized = statusCheckRangers
      .map((id) => (typeof id === "string" ? id.toUpperCase() : null))
      .filter(Boolean);
    return new Set(normalized);
  }, [statusCheckRangers]);

  const evaluateStatusChecks = useCallback(() => {
    const { incidentIds, rangerIds, modalCandidate } = buildStatusCheckState({
      incidents: activeIncidents, getThreshold: getStatusCheckThreshold,
      acknowledgements: statusAcknowledgements, rangerId: user?.rangerId,
      pinnedIncidentId: statusModalPinnedRef.current,
    });
    setStatusCheckIncidents(previous => previous.length === incidentIds.length && previous.every((id, index) => id === incidentIds[index]) ? previous : incidentIds);
    setStatusCheckRangers(previous => previous.length === rangerIds.length && [...previous].sort().every((id, index) => id === rangerIds[index]) ? previous : rangerIds);
    statusModalPinnedRef.current = modalCandidate?.incident.id || null;
    setStatusModalIncident(previous => {
      if (!modalCandidate) return null;
      return previous?.incident === modalCandidate.incident && previous.minutes === modalCandidate.minutes ? previous : modalCandidate;
    });
  }, [activeIncidents, getStatusCheckThreshold, statusAcknowledgements, user?.rangerId]);

  useEffect(() => {
    evaluateStatusChecks();
    const intervalId = setInterval(evaluateStatusChecks, STATUS_CHECK_POLL_MS);
    return () => clearInterval(intervalId);
  }, [evaluateStatusChecks]);

  useEffect(() => {
    if (!statusCuePrimedRef.current) {
      previousStatusCheckSetRef.current = new Set(statusCheckIncidents);
      statusCuePrimedRef.current = true;
      return;
    }

    const previousSet = previousStatusCheckSetRef.current;
    const hasNew = statusCheckIncidents.some((id) => !previousSet.has(id));
    if (hasNew) {
      playCue(SOUND_EVENTS.STATUS_CHECK_REQUIRED, { urgent: true });
    }

    previousStatusCheckSetRef.current = new Set(statusCheckIncidents);
  }, [playCue, statusCheckIncidents]);

  const usersActiveIncident = useMemo(() => {
    if (!user?.rangerId) {
      return null;
    }

    return (
      activeIncidents.find((incident) =>
        Array.isArray(incident.rangerIds)
          ? incident.rangerIds.some((id) => id?.toUpperCase() === user.rangerId.toUpperCase())
          : false
      ) || null
    );
  }, [activeIncidents, user?.rangerId]);

  const currentActiveIncident = usersActiveIncident || manualActiveIncident;

  // handleNewIncidentOpen - must be after currentActiveIncident is defined
  const handleNewIncidentOpen = useCallback(() => {
    pendingQuickCallRef.current = null;
    if (currentActiveIncident) {
      setIncidentConflict(currentActiveIncident);
      setIncidentConflictOpen(true);
      return;
    }
    showNewIncidentDialog();
  }, [currentActiveIncident, showNewIncidentDialog]);

  // Quick incident creation - one-press solution
  const handleQuickIncident = useCallback(
    async (incidentType) => {
      // Special handling for "Assist Ranger" - show modal to pick existing incident
      if (incidentType.toLowerCase() === "assist ranger") {
        if (activeIncidents.length === 0) {
          setIncidentBanner({ type: "info", message: "No active incidents to assist with." });
          return;
        }
        setAssistRangerOpen(true);
        return;
      }

      if (currentActiveIncident) {
        pendingQuickCallRef.current = { session: localStorage.getItem("rv_token"), preset: { source: "SELF", incidentType } };
        setIncidentConflict(currentActiveIncident);
        setIncidentConflictOpen(true);
        return;
      }

      // Auto-set source to SELF for quick incidents
      const newIncidentId = await submitIncident({
        source: "SELF",
        incidentType,
        _quickCreate: true,
      });

      // Open the incident editor after successful creation
      if (newIncidentId && typeof newIncidentId === 'number') {
        openIncidentEditor(newIncidentId);
      }
    },
    [activeIncidents.length, currentActiveIncident, submitIncident, openIncidentEditor]
  );

  // Service Call - creates incident with source="Service Call" and type="Assist Motorist"
  const handleServiceCall = useCallback(
    async () => {
      if (currentActiveIncident) {
        pendingQuickCallRef.current = { session: localStorage.getItem("rv_token"), preset: { source: "Service Call", incidentType: "Assist Motorist" } };
        setIncidentConflict(currentActiveIncident);
        setIncidentConflictOpen(true);
        return;
      }

      const newIncidentId = await submitIncident({
        source: "Service Call",
        incidentType: "Assist Motorist",
        _quickCreate: true,
      });

      if (newIncidentId && typeof newIncidentId === 'number') {
        openIncidentEditor(newIncidentId);
      }
    },
    [currentActiveIncident, submitIncident, openIncidentEditor]
  );

  // Handle joining an incident as an assist ranger
  const handleJoinIncident = useCallback(async (incidentId) => {
    if (!incidentId) return;

    setAssistRangerJoining(true);
    try {
      console.log("Joining incident:", incidentId);
      const response = await api.post(`/incidents/${incidentId}/join`);
      console.log("Join response:", response.data);

      // Update status to reflect on scene
      handleStatusChange("busy");
      setManualOnScene(true);
      setManualActiveIncident(response.data);

      setIncidentBanner({ type: "success", message: `Joined incident ${response.data.cadId}. You are now on scene.` });
      setAssistRangerOpen(false);

      // Refresh incidents
      await fetchIncidents();

      // Open the incident editor
      openIncidentEditor(incidentId);
    } catch (err) {
      console.error("Failed to join incident:", err);
      console.error("Error response:", err.response);
      const message = err.response?.data?.message || err.response?.data || "Failed to join incident.";
      setIncidentBanner({ type: "error", message: typeof message === "string" ? message : "Failed to join incident." });
      setAssistRangerOpen(false); // Close modal so user can see the error banner
    } finally {
      setAssistRangerJoining(false);
    }
  }, [fetchIncidents, handleStatusChange, openIncidentEditor]);

  // Handle leaving an incident as an assist ranger
  const handleLeaveIncident = useCallback(async () => {
    if (!detailIncidentId) return;

    try {
      await api.post(`/incidents/${detailIncidentId}/leave`);

      // Clear the manual active incident if it matches
      if (manualActiveIncident && manualActiveIncident.id === detailIncidentId) {
        setManualActiveIncident(null);
      }

      // Always clear on-scene status when leaving - the user explicitly left
      setManualOnScene(false);

      // Refresh incidents to get latest data
      await fetchIncidents();

      // Use the incidents ref to check fresh data instead of stale closure
      // Since fetchIncidents sets state, we check by excluding the left incident
      // and looking at whether any remaining incident includes the user.
      // The safest approach: the auto-clear useEffect will fire on next render
      // with the updated activeIncidents, but for immediate UX we check optimistically.
      const otherActiveForUser = activeIncidents.some((incident) => {
        if (incident.id === detailIncidentId) return false;
        if (!Array.isArray(incident.rangerIds)) return false;
        return incident.rangerIds.some(
          (id) => id?.toUpperCase() === user?.rangerId?.toUpperCase()
        );
      });

      if (!otherActiveForUser) {
        handleStatusChange("available");
      }

      setIncidentBanner({ type: "info", message: "Left the incident. You are no longer on scene." });
      setDetailOpen(false);
    } catch (err) {
      console.error("Failed to leave incident:", err);
      const message = err.response?.data?.message || err.response?.data || "Failed to leave incident.";
      setIncidentBanner({ type: "error", message: typeof message === "string" ? message : "Failed to leave incident." });
    }
  }, [activeIncidents, detailIncidentId, fetchIncidents, handleStatusChange, manualActiveIncident, user?.rangerId]);

  // Handle attaching to an incident from the detail view
  const handleAttachToIncident = useCallback(async () => {
    if (!detailIncidentId) return;

    setDetailAttaching(true);
    try {
      const response = await api.post(`/incidents/${detailIncidentId}/join`);

      // Set status to busy and mark on scene
      handleStatusChange("busy");
      setManualOnScene(true);
      setManualActiveIncident(response.data);

      setIncidentBanner({ type: "success", message: `Attached to incident ${response.data.cadId || detailIncidentId}. You are now on scene.` });

      // Refresh incidents and reload detail
      await fetchIncidents();

      // Reload the detail view to show updated participants
      try {
        const detailRes = await api.get(`/incidents/${detailIncidentId}`);
        setDetailIncident(detailRes.data);
      } catch (_) { /* detail refresh is best-effort */ }
    } catch (err) {
      console.error("Failed to attach to incident:", err);
      const message = err.response?.data?.message || err.response?.data || "Failed to attach to incident.";
      setIncidentBanner({ type: "error", message: typeof message === "string" ? message : "Failed to attach to incident." });
    } finally {
      setDetailAttaching(false);
    }
  }, [detailIncidentId, fetchIncidents, handleStatusChange]);

  useEffect(() => {
    return () => {
      stopStatusAlertAudio();
    };
  }, [stopStatusAlertAudio]);

  useEffect(() => {
    const geo = typeof navigator !== "undefined" ? navigator.geolocation : null;
    // Don't track GPS if disabled or out-of-service
    const canTrack = Boolean(geo) && unitStatus !== "out-of-service" && !gpsDisabled;

    if (!canTrack) {
      stopGpsWatch();
      if (gpsDisabled) {
        // When GPS is disabled, clear coords so we don't send stale data
        setGpsCoords(null);
        setGpsLocation("");
      }
      lastGpsUpdateRef.current = 0;
      return undefined;
    }

    const watchId = geo.watchPosition(
      (position) => {
        const { latitude, longitude, heading } = position.coords;
        commitGpsPosition(latitude, longitude, heading, position.timestamp, position.coords.accuracy);
      },
      (error) => {
        console.error("GPS watch error", error);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 15000,
        timeout: 10000,
      }
    );

    gpsWatchIdRef.current = watchId;

    requestCoordinates().catch(() => undefined);

    return () => {
      if (watchId && typeof navigator !== "undefined" && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
        if (gpsWatchIdRef.current === watchId) {
          gpsWatchIdRef.current = null;
        }
      }
    };
  }, [commitGpsPosition, gpsDisabled, requestCoordinates, stopGpsWatch, unitStatus]);

  // Reverse-geocode GPS coords to determine county → division (throttled to every 60s)
  useEffect(() => {
    if (!gpsCoords?.latitude || !gpsCoords?.longitude) return;

    const now = Date.now();
    if (now - lastDivisionGeocodeRef.current < 60000) return;
    lastDivisionGeocodeRef.current = now;

    let cancelled = false;
    const resolveCounty = async () => {
      try {
        const response = await reverseGeocodeResponse(gpsCoords.latitude, gpsCoords.longitude);
        if (cancelled) return;
        const data = await response.json();
        if (cancelled) return;
        if (!data?.address) return;
        const county = data.address.county;
        const state = data?.address?.state;
        if (county || state) {
          const division = resolveDivisionFromCounty(county, state);
          setGpsDivision(division);
        } else {
          // If Nominatim doesn't return county, treat as Travel
          setGpsDivision(TRAVEL_DIVISION);
        }
      } catch (e) {
        console.error("Division geocode error:", e);
      }
    };

    const timeout = setTimeout(resolveCounty, 500);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [gpsCoords]);

  useEffect(() => {
    // Don't run GPS refresh when GPS is disabled
    if (gpsDisabled) {
      return undefined;
    }

    if (
      typeof window === "undefined" ||
      typeof navigator === "undefined" ||
      !navigator.geolocation
    ) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    // Track high accuracy failures - disable for 5 minutes after failure
    const HIGH_ACCURACY_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes

    function shouldUseHighAccuracy() {
      const lastFailure = window.__gpsHighAccuracyFailedAt;
      if (!lastFailure) return true;
      return Date.now() - lastFailure > HIGH_ACCURACY_COOLDOWN_MS;
    }

    function scheduleNext() {
      if (cancelled) {
        return;
      }
      timeoutId = window.setTimeout(checkForStalePosition, GPS_REFRESH_INTERVAL_MS);
    }

    function checkForStalePosition() {
      if (cancelled) {
        return;
      }

      if (unitStatus === "out-of-service") {
        scheduleNext();
        return;
      }

      const now = Date.now();
      if (lastGpsUpdateRef.current && now - lastGpsUpdateRef.current < GPS_STALE_THRESHOLD_MS) {
        scheduleNext();
        return;
      }

      if (gpsRefreshInFlightRef.current) {
        scheduleNext();
        return;
      }

      gpsRefreshInFlightRef.current = true;
      const useHighAccuracy = shouldUseHighAccuracy();

      navigator.geolocation.getCurrentPosition(
        (position) => {
          gpsRefreshInFlightRef.current = false;
          if (cancelled) {
            return;
          }
          // Success with high accuracy - clear any failure tracking
          if (useHighAccuracy) {
            window.__gpsHighAccuracyFailedAt = null;
          }
          commitGpsPosition(position.coords.latitude, position.coords.longitude, position.coords.heading, position.timestamp, position.coords.accuracy);
          scheduleNext();
        },
        (error) => {
          gpsRefreshInFlightRef.current = false;
          if (!cancelled) {
            // If high accuracy failed, mark it and log once
            if (useHighAccuracy) {
              window.__gpsHighAccuracyFailedAt = Date.now();
              if (!window.__gpsErrorLogged) {
                window.__gpsErrorLogged = true;
                console.warn("GPS high accuracy timed out - falling back to low accuracy for 5 minutes");
              }
            }
            scheduleNext();
          }
        },
        {
          enableHighAccuracy: useHighAccuracy,
          timeout: useHighAccuracy ? 10000 : 30000,
          maximumAge: 0
        }
      );
    }

    checkForStalePosition();

    return () => {
      cancelled = true;
      gpsRefreshInFlightRef.current = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [commitGpsPosition, gpsDisabled, unitStatus]);

  const busyReasonLabel = useMemo(() => {
    if (!busyReason) {
      return "";
    }
    const match = BUSY_REASON_OPTIONS.find((option) => option.value === busyReason);
    return match ? match.label : busyReason.toUpperCase();
  }, [busyReason]);

  const busyStatusLabel = busyReasonLabel ? `BUSY - ${busyReasonLabel}` : "BUSY";

  const userStatus = useMemo(() => {
    const baseLastUpdate = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const transportInfo = transportActive
      ? (transportAddress.trim() ? `TRANSPORTING TO ${transportAddress.trim()}` : "TRANSPORTING")
      : "";
    const base = {
      rangerId: user?.rangerId || "RV-000",
      location: "NOT PROVIDED",
      info: "", // Additional info should be empty by default, only set when BUSY with reason
      division: gpsDivision || user?.division || "-",
      lastUpdate: baseLastUpdate,
    };
    const trimmedBusyLocation = busyLocation.trim();
    const trimmedBusyInfo = busyInfo.trim();
    const manualLocation =
      unitStatus === "busy" && busyManualMode && trimmedBusyLocation ? trimmedBusyLocation : "";
    const incidentLocation = currentActiveIncident
      ? formatIncidentLocation(currentActiveIncident, decodedLocations)
      : "";
    const normalizedIncidentLocation = incidentLocation === "NOT PROVIDED" ? "" : incidentLocation;
    const normalizedGpsLocation = gpsCoords
      ? `LAT ${gpsCoords.latitude.toFixed(5)}, LON ${gpsCoords.longitude.toFixed(5)}`
      : gpsLocation?.trim() || "";
    const resolvedLocation =
      manualLocation || normalizedGpsLocation || normalizedIncidentLocation || base.location;

    if (!currentActiveIncident) {
      const next = { ...base, location: resolvedLocation };
      if (transportActive) {
        next.info = transportInfo;
        return next;
      }
      if (unitStatus === "busy" && busyManualMode) {
        next.info = trimmedBusyInfo || busyStatusLabel;
      }
      // Don't copy status to info - info should only show busy reason or incident type
      return next;
    }

    const next = {
      ...base,
      location: resolvedLocation,
      info:
        currentActiveIncident.incidentType ||
        formatIncidentStatus(currentActiveIncident.status) ||
        base.info,
      lastUpdate: currentActiveIncident.occurredAtUtc
        ? formatUtcToLocalTime(currentActiveIncident.occurredAtUtc)
        : base.lastUpdate,
    };

    if (transportActive) {
      next.info = transportInfo;
      return next;
    }

    if (unitStatus === "busy" && busyManualMode) {
      next.info = trimmedBusyInfo || busyStatusLabel;
    }

    return next;
  }, [busyInfo, busyLocation, busyManualMode, busyStatusLabel, currentActiveIncident, decodedLocations, gpsDivision, gpsCoords, unitStatus, user, gpsLocation, transportActive, transportAddress]);

  useEffect(() => {
    if (
      manualActiveIncident &&
      usersActiveIncident &&
      manualActiveIncident.id === usersActiveIncident.id
    ) {
      setManualActiveIncident(null);
    }
  }, [manualActiveIncident, usersActiveIncident]);

  // Note: hasOtherActiveIncidents moved earlier in file to avoid forward reference issues

  const derivedUserOnScene = useMemo(() => {
    if (!user?.rangerId) {
      return false;
    }

    return activeIncidents.some((incident) =>
      Array.isArray(incident.rangerIds)
        ? incident.rangerIds.some((id) => id?.toUpperCase() === user.rangerId.toUpperCase())
        : false
    );
  }, [activeIncidents, user?.rangerId]);

  useEffect(() => {
    if (manualOnScene && derivedUserOnScene) {
      setManualOnScene(false);
    }
  }, [derivedUserOnScene, manualOnScene]);

  // User is on scene if manually set OR derived from incidents (but NOT if enroute)
  const userOnScene = !userEnroute && (manualOnScene || derivedUserOnScene);

  const baseStatusLabel = unitStatus === "busy" ? busyStatusLabel : formatStatus(unitStatus);
  // Show TRANSPORTING when active, otherwise ENROUTE / ON SCENE / base status
  const requestedStatusLabel = transportActive
    ? "TRANSPORTING"
    : (userEnroute ? "ENROUTE" : (userOnScene ? "ON SCENE" : baseStatusLabel));
  const canonicalSelf = remoteActiveUnits.find(unit => unit.userId === user?.id);
  const unitStatusLabel = !statusIntent.pending && !busyModalOpen && canonicalSelf?.statusLabel
    ? canonicalSelf.statusLabel : requestedStatusLabel;
  const canManageIncidents = ADMIN_ROLES.includes((user?.role || "").toLowerCase());
  const canDeleteIncidents = (user?.role || "").toLowerCase() === "admin";

  const heartbeatError = useUnitHeartbeat(user,
    !gpsDisabled && gpsCoords ? { lat: gpsCoords.latitude, lng: gpsCoords.longitude, heading: gpsCoords.heading, observedAt: gpsCoords.observedAt, accuracy: gpsCoords.accuracy } : null,
    gpsDivision, { isMobile: false, intervalMs: 5000 });

  const pingPayload = useMemo(() => {
    if (!user) {
      return null;
    }

    const statusValue = unitStatus || "available";
    const labelValue = unitStatusLabel || formatStatus(statusValue);
    const normalizedLocation = userStatus.location?.trim();
    const normalizedInfo = userStatus.info?.trim();
    const normalizedDivision = userStatus.division?.trim();

    // Don't send GPS coordinates if GPS is disabled on desktop
    const shouldSendGps = !gpsDisabled && gpsCoords;

    return {
      status: statusValue,
      statusLabel: labelValue,
      location:
        normalizedLocation && normalizedLocation !== "NOT PROVIDED" ? normalizedLocation : undefined,
      info: normalizedInfo && normalizedInfo !== "NOT PROVIDED" ? normalizedInfo : undefined,
      division: normalizedDivision && normalizedDivision !== "-" ? normalizedDivision : (gpsDivision || undefined),
      onScene: Boolean(userOnScene),
      latitude: shouldSendGps ? gpsCoords.latitude : undefined,
      longitude: shouldSendGps ? gpsCoords.longitude : undefined,
      heading: shouldSendGps && gpsCoords?.heading != null ? gpsCoords.heading : undefined,
      locationObservedAtUtc: shouldSendGps && gpsCoords.observedAt ? new Date(gpsCoords.observedAt).toISOString() : undefined,
      accuracyMeters: shouldSendGps ? gpsCoords.accuracy : undefined,
      isMobile: false, // Desktop dashboard - mobile GPS takes priority
    };
  }, [unitStatus, unitStatusLabel, userStatus, userOnScene, user, gpsCoords, gpsDisabled, gpsDivision]);

  const flushStatusIntent = statusIntent.flush;
  useEffect(() => { flushStatusIntent(pingPayload, refreshDispatch); },
    [flushStatusIntent, pingPayload, refreshDispatch, statusIntent.revision]);

  const visibleRemoteUnits = useVisibleUnits(remoteActiveUnits, user?.hideStaleUnitsAfter24Hours);
  const activeUnits = useMemo(() => {
    const incidentLookup = new Map();
    activeIncidents.forEach((incident) => {
      const type = incident.incidentType ? incident.incidentType.toUpperCase() : null;
      if (!type) {
        return;
      }
      if (Array.isArray(incident.rangerIds)) {
        incident.rangerIds.forEach((rangerId) => {
          if (rangerId) {
            incidentLookup.set(rangerId.toUpperCase(), type);
          }
        });
      }
      if (incident.primaryRangerId) {
        incidentLookup.set(incident.primaryRangerId.toUpperCase(), type);
      }
    });

    const deriveLocationLabel = (locationValue) => {
      const coords = parseCoordinatesFromLocation(locationValue);
      if (!coords) {
        return null;
      }
      const key = geocodeKey(coords.latitude, coords.longitude);
      if (!key) {
        return null;
      }
      return decodedLocations[key] || null;
    };

    const decorateUnit = (unit) => {
      const normalizedId = unit.id ? unit.id.toUpperCase() : "";
      const needsStatusCheck = normalizedId && statusCheckRangerSet.has(normalizedId);

      let resolvedLabel = unit.locationLabel;
      if (!resolvedLabel) {
        if (Number.isFinite(unit.latitude) && Number.isFinite(unit.longitude)) {
          const key = geocodeKey(unit.latitude, unit.longitude);
          if (key) {
            resolvedLabel = decodedLocations[key] || null;
          }
        }
      }

      if (!resolvedLabel) {
        resolvedLabel = deriveLocationLabel(unit.location);
      }

      return {
        ...unit,
        displayStatus: needsStatusCheck ? "CHECK STATUS" : unit.status,
        displayInfo: needsStatusCheck ? "CHECK STATUS" : unit.info,
        needsStatusCheck: Boolean(needsStatusCheck),
        locationLabel: resolvedLabel || undefined,
      };
    };

    const userId = (userStatus.rangerId || "RV-000").toUpperCase();
    const userIncidentType = incidentLookup.get(userId) || currentActiveIncident?.incidentType?.toUpperCase();
    const sceneInfo = userIncidentType || "ON SCENE";

    const remoteSelf = remoteActiveUnits.find((unit) => {
      if (!unit) {
        return false;
      }
      const candidate = (unit.rangerId || `USER-${unit.userId}` || "").toUpperCase();
      return candidate === userId;
    });

    const remoteSelfCoords = remoteSelf
      ? (Number.isFinite(remoteSelf.latitude) && Number.isFinite(remoteSelf.longitude)
          ? { latitude: remoteSelf.latitude, longitude: remoteSelf.longitude }
          : parseCoordinatesFromLocation(remoteSelf.location))
      : null;
    const fallbackCoords = parseCoordinatesFromLocation(userStatus.location);
    const preferPhoneGps = remoteSelf?.locationFromMobile === true && remoteSelfCoords &&
      Number.isFinite(Date.parse(remoteSelf.locationObservedAtUtc)) && Date.now() - Date.parse(remoteSelf.locationObservedAtUtc) < 120000;
    // Keep the local desktop marker consistent with canonical phone-first GPS.
    const userLatitude = gpsDisabled || preferPhoneGps
      ? (remoteSelfCoords?.latitude ?? fallbackCoords?.latitude ?? null)
      : (Number.isFinite(gpsCoords?.latitude)
          ? gpsCoords.latitude
          : remoteSelfCoords?.latitude ?? fallbackCoords?.latitude ?? null);
    const userLongitude = gpsDisabled || preferPhoneGps
      ? (remoteSelfCoords?.longitude ?? fallbackCoords?.longitude ?? null)
      : (Number.isFinite(gpsCoords?.longitude)
          ? gpsCoords.longitude
          : remoteSelfCoords?.longitude ?? fallbackCoords?.longitude ?? null);

    const chooseLocation = () => {
      // Check for human-readable location from local state first (reverse geocoded addresses)
      const localLocation = (userStatus.location || "").trim();
      const isLocalLocationUseful = localLocation &&
        localLocation !== "NOT PROVIDED" &&
        !localLocation.startsWith("LAT ");

      if (isLocalLocationUseful) {
        return localLocation;
      }

      // Check remote location string (might have a nice address from server)
      const remoteLocation = (remoteSelf?.location || "").trim();
      const isRemoteLocationUseful = remoteLocation &&
        remoteLocation !== "NOT PROVIDED" &&
        !remoteLocation.startsWith("LAT ");

      if (isRemoteLocationUseful) {
        return remoteLocation;
      }

      // Fallback to raw coordinates if we have them
      if (remoteSelfCoords) {
        return `LAT ${remoteSelfCoords.latitude.toFixed(5)}, LON ${remoteSelfCoords.longitude.toFixed(5)}`;
      }

      // Last resort - local coords or "NOT PROVIDED"
      return localLocation || "NOT PROVIDED";
    };

    // Determine info to display based on status
    const chooseInfo = () => {
      if (gpsHidden) return "GPS DISABLED";
      if (userEnroute) {
        // When enroute, show the incident type they're responding to
        return userIncidentType ? `ENROUTE: ${userIncidentType}` : "ENROUTE";
      }
      if (userOnScene) return sceneInfo;
      return userStatus.info;
    };

    const userEntry = decorateUnit({
      id: userId,
      status: unitStatusLabel,
      location: chooseLocation(),
      info: chooseInfo(),
      division: userStatus.division,
      lastUpdate: userStatus.lastUpdate,
      latitude: userLatitude ?? undefined,
      longitude: userLongitude ?? undefined,
      gpsObservedAt: preferPhoneGps ? remoteSelf.locationObservedAtUtc : gpsCoords?.observedAt ? new Date(gpsCoords.observedAt).toISOString() : remoteSelf?.locationObservedAtUtc,
      accuracyMeters: preferPhoneGps ? remoteSelf.accuracyMeters : gpsCoords?.accuracy ?? remoteSelf?.accuracyMeters,
    });

    if (gpsHidden) {
      userEntry.displayInfo = "GPS DISABLED";
      userEntry.info = "GPS DISABLED";
    }

    const others = visibleRemoteUnits
      .filter((unit) => unit && (unit.rangerId || typeof unit.userId === "number"))
      .filter((unit) => (unit.rangerId || `USER-${unit.userId}` || "").toUpperCase() !== userId)
      .map((unit) => {
        const normalizedId = (unit.rangerId || `USER-${unit.userId}`).toUpperCase();
        const isStale = Boolean(unit.isStale);

        // Clean status values - if they contain "STALE", use the underlying status instead
        const rawStatus = unit.status || "available";
        const rawStatusLabel = unit.statusLabel || "";
        const rawInfo = unit.info || "";

        // Detect if stored values are contaminated with "STALE" from old data
        const statusIsStale = rawStatus.toUpperCase().includes("STALE");
        const statusLabelIsStale = rawStatusLabel.toUpperCase().includes("STALE");
        const infoIsStale = rawInfo.toUpperCase().includes("STALE");

        // Use clean status - fallback to "available" if stored value was "STALE"
        const cleanStatus = statusIsStale ? "available" : rawStatus;
        const cleanStatusLabel = statusLabelIsStale ? formatStatus(cleanStatus) : rawStatusLabel;
        const cleanInfo = infoIsStale ? formatStatus(cleanStatus) : rawInfo;

        // For stale units, always use the clean formatted status
        const statusLabel = unit.onScene
          ? "ON SCENE"
          : (isStale ? formatStatus(cleanStatus) : (cleanStatusLabel || formatStatus(cleanStatus)));

        // Additional info should only show meaningful info (like busy reason), not copy status
        // Info is meaningful if it exists and is different from the status/statusLabel
        const infoIsMeaningful = cleanInfo &&
          cleanInfo.toUpperCase() !== statusLabel.toUpperCase() &&
          cleanInfo.toUpperCase() !== formatStatus(cleanStatus).toUpperCase() &&
          cleanInfo.toUpperCase() !== "AVAILABLE" &&
          cleanInfo.toUpperCase() !== "OUT OF SERVICE" &&
          cleanInfo.toUpperCase() !== "OUT-OF-SERVICE";

        let infoValue = "";
        if (unit.onScene) {
          const incidentType = incidentLookup.get(normalizedId);
          infoValue = incidentType || "ON SCENE";
        } else if (infoIsMeaningful) {
          infoValue = cleanInfo;
        }

        // Prefer API lat/lng, fallback to parsing from location string
        const apiLat = unit.latitude;
        const apiLng = unit.longitude;
        const coords = (Number.isFinite(apiLat) && Number.isFinite(apiLng))
          ? { latitude: apiLat, longitude: apiLng }
          : parseCoordinatesFromLocation(unit.location);

        // Determine location display - prefer readable address, fallback to formatted coords
        const rawLocation = (unit.location || "").trim();
        const hasUsefulLocation = rawLocation && rawLocation !== "NOT PROVIDED" && !rawLocation.startsWith("LAT ");
        let displayLocation;
        if (hasUsefulLocation) {
          displayLocation = rawLocation;
        } else if (coords) {
          displayLocation = `LAT ${coords.latitude.toFixed(5)}, LON ${coords.longitude.toFixed(5)}`;
        } else {
          displayLocation = "NOT PROVIDED";
        }

        return decorateUnit({
          id: normalizedId,
          status: statusLabel,
          location: displayLocation,
          info: infoValue,
          division: unit.division || "-",
          lastUpdate: unit.lastSeenUtc
            ? formatUtcToLocal(unit.lastSeenUtc, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
            : "—",
          latitude: coords?.latitude,
          longitude: coords?.longitude,
          isStale, gpsObservedAt: unit.locationObservedAtUtc, accuracyMeters: unit.accuracyMeters,
        });
      })
      .sort((a, b) => {
        // Sort active units first, then stale units
        if (a.isStale !== b.isStale) {
          return a.isStale ? 1 : -1;
        }
        return a.id.localeCompare(b.id);
      });

    return [userEntry, ...others];
  }, [activeIncidents, currentActiveIncident, decodedLocations, gpsCoords, gpsDisabled, gpsHidden, remoteActiveUnits, visibleRemoteUnits, statusCheckRangerSet, unitStatusLabel, userEnroute, userStatus, userOnScene]);

  useEffect(() => {
    const pending = new Map();

    activeIncidents.forEach((incident) => {
      const fallback = parseCoordinatesFromLocation(incident?.locationDescription);
      const latitude = typeof incident?.latitude === 'number' ? incident.latitude : fallback?.latitude;
      const longitude = typeof incident?.longitude === 'number' ? incident.longitude : fallback?.longitude;
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return;
      }
      const key = geocodeKey(latitude, longitude);
      if (key && !decodedLocations[key]) {
        pending.set(key, { latitude, longitude });
      }
    });

    activeUnits.forEach((unit) => {
      const coords = Number.isFinite(unit.latitude) && Number.isFinite(unit.longitude)
        ? { latitude: unit.latitude, longitude: unit.longitude } : parseCoordinatesFromLocation(unit.location);
      if (!coords) {
        return;
      }
      const key = geocodeKey(coords.latitude, coords.longitude);
      if (key && !decodedLocations[key]) {
        pending.set(key, coords);
      }
    });

    if (pending.size === 0) {
      return;
    }

    pending.forEach(({ latitude, longitude }, key) => {
      reverseGeocodeLookup(latitude, longitude).then((label) => {
        if (!label) {
          return;
        }
        setDecodedLocations((prev) => {
          if (prev[key] === label) {
            return prev;
          }
          return { ...prev, [key]: label };
        });
      });
    });
  }, [activeIncidents, activeUnits]);

  const flashingSet = useMemo(() => new Set(flashingIncidents), [flashingIncidents]);
  const updatedFlashingSet = useMemo(() => new Set(updatedFlashingIncidents), [updatedFlashingIncidents]);

  // Track ranger IDs that are in emergency status for flashing
  const emergencyRangerIds = useMemo(() => {
    return new Set(activeEmergencies.map((e) => e.rangerId?.toUpperCase()).filter(Boolean));
  }, [activeEmergencies]);

  const statusCheckModalMinutes = statusModalIncident?.minutes ?? statusCheckSettings.defaultMinutes;
  const statusBackdropStyles = useMemo(() => {
    if (!statusBackdropAlert) {
      return { backgroundColor: "rgba(0, 0, 0, 0.65)" };
    }
    return {
      backgroundColor: "rgba(0, 0, 0, 0.65)",
      animation: "statusBackdropPulse 1.15s ease-in-out infinite",
      "@keyframes statusBackdropPulse": {
        "0%": { backgroundColor: "rgba(0, 0, 0, 0.65)" },
        "50%": { backgroundColor: "rgba(250, 204, 21, 0.45)" },
        "100%": { backgroundColor: "rgba(0, 0, 0, 0.65)" },
      },
    };
  }, [statusBackdropAlert]);
  const deleteTarget = deletePrompt.incident;
  const avlMapUnits = useMemo(() => {
    if (gpsHidden) {
      return [];
    }
    return activeUnits
      .map((unit) => {
        const coords =
          Number.isFinite(unit.latitude) && Number.isFinite(unit.longitude)
            ? { latitude: unit.latitude, longitude: unit.longitude }
            : parseCoordinatesFromLocation(unit.location);
        if (!coords) {
          return null;
        }

        return {
          id: unit.id,
          status: unit.displayStatus || unit.status,
          info: unit.displayInfo || unit.info,
          division: unit.division,
          latitude: coords.latitude,
          longitude: coords.longitude,
          updatedAt: unit.lastUpdate, gpsObservedAt: unit.gpsObservedAt, accuracyMeters: unit.accuracyMeters,
          needsStatusCheck: unit.needsStatusCheck,
          locationLabel: unit.locationLabel,
          isStale: unit.isStale,
        };
      })
        .filter(Boolean);
      }, [activeUnits, gpsHidden]);

  // Active incidents with coordinates for AVL map display
  const avlMapIncidents = useMemo(() => {
    if (gpsHidden) {
      return [];
    }
    return activeIncidents
      .filter((incident) =>
        Number.isFinite(incident.latitude) &&
        Number.isFinite(incident.longitude)
      )
      .map((incident) => ({
        id: incident.id,
        cadId: incident.cadId,
        incidentType: incident.incidentType,
        latitude: incident.latitude,
        longitude: incident.longitude,
        locationDescription: formatIncidentLocation(incident, decodedLocations),
        primaryRangerId: incident.primaryRangerId,
        source: incident.source,
      }));
  }, [activeIncidents, gpsHidden, decodedLocations]);

  // Waze alerts for AVL map display (only when enabled)
  const avlMapWazeAlerts = useMemo(() => {
    if (gpsHidden || !wazeEnabled) {
      return [];
    }

    // Helper to extract direction from street name
    const extractDirection = (street) => {
      if (!street) return null;
      const upperStreet = street.toUpperCase();
      // Check for direction words
      if (upperStreet.includes("NORTHBOUND") || / N\b/.test(upperStreet) || upperStreet.startsWith("N ")) {
        return "NORTHBOUND";
      }
      if (upperStreet.includes("SOUTHBOUND") || / S\b/.test(upperStreet) || upperStreet.startsWith("S ")) {
        return "SOUTHBOUND";
      }
      if (upperStreet.includes("EASTBOUND") || / E\b/.test(upperStreet) || upperStreet.startsWith("E ")) {
        return "EASTBOUND";
      }
      if (upperStreet.includes("WESTBOUND") || / W\b/.test(upperStreet) || upperStreet.startsWith("W ")) {
        return "WESTBOUND";
      }
      // Check for NB/SB/EB/WB abbreviations
      if (/\bNB\b/.test(upperStreet)) return "NORTHBOUND";
      if (/\bSB\b/.test(upperStreet)) return "SOUTHBOUND";
      if (/\bEB\b/.test(upperStreet)) return "EASTBOUND";
      if (/\bWB\b/.test(upperStreet)) return "WESTBOUND";
      return null;
    };

    // Helper to get abbreviated category
    const getCategoryAbbrev = (category) => {
      const abbrevs = {
        ASSIST_MOTORIST: "AM",
        DEBRIS: "DEB",
        ACCIDENT: "ACC",
        ASSIST_OTHER_AGENCY: "AOA",
      };
      return abbrevs[category] || "?";
    };

    return filteredWazeAlerts
      .filter((alert) =>
        Number.isFinite(alert.latitude) &&
        Number.isFinite(alert.longitude)
      )
      .map((alert) => {
        const category = getWazeAlertCategory(alert.type, alert.subtype);
        const direction = extractDirection(alert.street);
        const abbrev = getCategoryAbbrev(category);
        // Build map label: "AM - SOUTHBOUND" or just "AM" if no direction
        const mapLabel = direction ? `${abbrev} - ${direction}` : abbrev;

        return {
          id: `waze-${alert.id}`,
          wazeId: alert.id,
          category,
          latitude: alert.latitude,
          longitude: alert.longitude,
          street: alert.street,
          city: alert.city,
          status: alert.status,
          distanceMiles: alert.distanceMiles,
          mapLabel,
        };
      });
  }, [filteredWazeAlerts, gpsHidden, wazeEnabled]);

  const avlMapAbandonedVehicles = useMemo(() => {
    if (gpsHidden) {
      return [];
    }
    return (Array.isArray(abandonedVehicles) ? abandonedVehicles : [])
      .filter((vehicle) => Number.isFinite(vehicle?.latitude) && Number.isFinite(vehicle?.longitude))
      .map((vehicle) => ({
        id: vehicle.id,
        latitude: vehicle.latitude,
        longitude: vehicle.longitude,
        locationDescription: vehicle.locationDescription,
        shoulderLocation: vehicle.shoulderLocation,
        vehicleMake: vehicle.vehicleMake,
        vehicleModel: vehicle.vehicleModel,
        vehicleColor: vehicle.vehicleColor,
        vehiclePlate: vehicle.vehiclePlate,
        vehicleState: vehicle.vehicleState,
        vehicleYear: vehicle.vehicleYear,
        notes: vehicle.notes,
        createdAtUtc: vehicle.createdAtUtc,
      }));
  }, [abandonedVehicles, gpsHidden]);

  // Destination for AVL map when user is enroute
  const avlMapDestination = useMemo(() => {
    if (!userEnroute || !enrouteDestinationId) {
      return null;
    }
    const incident = activeIncidents.find((inc) => inc.id === enrouteDestinationId);
    if (!incident || !Number.isFinite(incident.latitude) || !Number.isFinite(incident.longitude)) {
      return null;
    }
    return {
      id: incident.id,
      cadId: incident.cadId,
      incidentType: incident.incidentType,
      latitude: incident.latitude,
      longitude: incident.longitude,
      locationDescription: incident.locationDescription,
      source: incident.source,
    };
  }, [userEnroute, enrouteDestinationId, activeIncidents]);

  const statusSnapshot = useMemo(() => {
    if (!user) {
      return null;
    }

    return {
      rangerId: userStatus.rangerId,
      status: unitStatus,
      statusLabel: unitStatusLabel,
      location: userStatus.location,
      info: userOnScene ? "ON SCENE" : userStatus.info,
      division: userStatus.division,
      onScene: userOnScene,
    };
  }, [unitStatus, unitStatusLabel, user, userOnScene, userStatus]);

  useEffect(() => {
    setStatusSnapshot(statusSnapshot);
  }, [statusSnapshot]);

  useEffect(() => () => setStatusSnapshot(null), []);

  const isIncidentRestricted = !!user?.incidentCreationRestricted && user?.role !== "admin" && user?.role !== "supervisor";

  // If incident creation is restricted, show only the restriction notice
  if (isIncidentRestricted) {
    return (
      <DashboardLayout tabletMode={false}>
        <Dialog
          open
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: "visible",
              textAlign: "center",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #ff9800 0%, #f44336 100%)",
              color: "white",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 3,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 48 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>
              Access Restricted
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 4, pb: 2, px: 4 }}>
            <Stack spacing={3} alignItems="center">
              <SchoolIcon sx={{ fontSize: 64, color: "text.secondary", opacity: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Incident Management is Not Yet Available
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                To gain access to incident creation and management, you must complete all required core classes and receive approval from leadership.
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  width: "100%",
                  backgroundColor: "action.hover",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase", mb: 1, letterSpacing: 0.5 }}>
                  Steps to Get Access
                </Typography>
                <Stack spacing={1} sx={{ textAlign: "left" }}>
                  <Typography variant="body2">1. Complete all required core training classes</Typography>
                  <Typography variant="body2">2. Submit confirmation to your supervisor</Typography>
                  <Typography variant="body2">3. Wait for leadership approval to be granted</Typography>
                </Stack>
              </Paper>
              <Typography variant="caption" color="text.secondary">
                If you believe this is an error, please contact your supervisor or an administrator.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3, px: 4 }}>
            <Button
              variant="contained"
              href="/my-vault"
              sx={{ textTransform: "uppercase", fontWeight: 700, px: 4, py: 1 }}
            >
              Go to MyVault
            </Button>
          </DialogActions>
        </Dialog>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      toolbarExtras={tabletModeToolbar}
      workspaceToolsProps={{ onWazeSettings: () => setWazeConfigOpen(true), wazeEnabled, wazeFilterCount }}
      sidebarCollapsed={isMapView ? sidebarCollapsed : undefined}
      onSidebarToggle={isMapView ? handleSidebarToggle : undefined}
      tabletMode={tabletMode}
    >
      <Box ref={dispatchBannerRef} sx={isMapView ? { position: 'fixed', top: 'calc(var(--cad-header-height, 64px) + 8px)', left: 'calc(var(--cad-navigation-width, 0px) + 8px)', right: 8, zIndex: 1101, bgcolor: 'background.paper', borderRadius: 1, px: 1.5, py: heartbeatError || statusIntent.error || statusIntent.pending || missedStatusChecks.length > 0 ? 0.75 : 0 } : undefined}>
      {heartbeatError && <Alert severity="warning">{heartbeatError}</Alert>}
      {statusIntent.error && <Alert severity="warning" action={
        <Stack direction="row" spacing={1}>
          <Button color="inherit" onClick={statusIntent.retry}>Retry requested change</Button>
          <Button color="inherit" onClick={statusIntent.discard}>Use current dispatch</Button>
        </Stack>
      }>{statusIntent.error}</Alert>}
      {statusIntent.pending && !statusIntent.error && <Typography role="status">Saving status change...</Typography>}
      {missedStatusChecks.length > 0 && <Alert severity="warning" action={
        <Button color="inherit" onClick={() => { setSelectedMissedCheck(null); setMissedStatusCheckOpen(true); }}>Review status alerts</Button>
      }>{missedStatusChecks.length} missed status {missedStatusChecks.length === 1 ? 'check requires' : 'checks require'} follow-up.</Alert>}
      </Box>
      {/* Map Centered View Overlay - when enabled, covers the standard content */}
      {isMapView && (
        <MapCenteredDashboard
          dispatchBannerHeight={dispatchBannerHeight}
          avlMapUnits={avlMapUnits}
          avlMapIncidents={avlMapIncidents}
          avlMapWazeAlerts={avlMapWazeAlerts}
          avlMapAbandonedVehicles={avlMapAbandonedVehicles}
          avlMapDestination={avlMapDestination}
          userEnroute={userEnroute}
          enrouteUnitId={userEnroute ? (user?.rangerId || `USER-${user?.id}`) : null}
          isDarkMode={isDarkMode}
          onIncidentClick={openIncidentEditor}
          activeIncidents={activeIncidents.map(incident => ({ ...incident, locationDescription: formatIncidentLocation(incident, decodedLocations) }))}
          onQuickCreateIncident={(type) => handleQuickIncident(type)}
          onServiceCall={handleServiceCall}
          newIncidentSubmitting={newIncidentSubmitting}
          userOnScene={userOnScene}
          sidebarCollapsed={sidebarCollapsed}
          // Status props
          unitStatus={unitStatus}
          onStatusChange={handleStatusChange}
          onEmergency={handleActivateEmergency}
          currentActiveIncident={currentActiveIncident}
          // Emergency self-cancel props
          activeEmergencies={activeEmergencies}
          onSelfCancelOpen={() => {
            setSelfCancelReason("");
            setSelfCancelPin("");
            setSelfCancelError("");
            setSupervisorClear({ open: false, rangerId: null });
                  setSelfCancelOpen(true);
                  emergencyClearance.prepare('self');
          }}
          // GPS and Waze props
          gpsCoords={gpsCoords}
          filteredWazeAlerts={filteredWazeAlerts}
          onWazeOnScene={handleWazeOnScene}
          onWazeUtl={(alertId) => handleWazeClearAlert(alertId, "UTL")}
          wazeClaimingId={wazeClaimingId}
          // Active units for sidebar list
          activeUnitsForList={activeUnits}
          // Tablet mode
          tabletMode={tabletMode}
        />
      )}

      {/* Standard Dashboard Content - hidden when mapView is enabled */}
      <Box sx={{ display: isMapView ? 'none' : 'block' }}>
      <Stack spacing={tabletMode ? 4 : 3}>
        {/* Emergency Alert Banner - shows for OTHER rangers' emergencies */}
        {activeEmergencies.filter((e) => e.userId !== user?.id).length > 0 && (
          <Box
            sx={{
              width: "100%",
              animation: "emergencyFlash 1s ease-in-out infinite",
              "@keyframes emergencyFlash": {
                "0%, 100%": {
                  backgroundColor: "rgba(244, 67, 54, 0.9)",
                  boxShadow: "0 0 20px rgba(244, 67, 54, 0.8)"
                },
                "50%": {
                  backgroundColor: "rgba(244, 67, 54, 0.5)",
                  boxShadow: "0 0 10px rgba(244, 67, 54, 0.4)"
                },
              },
              borderRadius: 2,
              p: 2,
            }}
          >
            {activeEmergencies.filter((e) => e.userId !== user?.id).map((emergency) => (
              <Box key={emergency.userId} sx={{ mb: activeEmergencies.length > 1 ? 2 : 0 }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: "white",
                    fontWeight: 900,
                    textAlign: "center",
                    textTransform: "uppercase",
                    letterSpacing: 2,
                    textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                  }}
                >
                  🚨 RANGER {emergency.rangerId || "UNKNOWN"} NEEDS HELP 🚨
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "white",
                    textAlign: "center",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    mt: 1,
                  }}
                >
                  {emergency.name && <span>{emergency.name} • </span>}
                  {emergency.phoneNumber && <span>📞 {emergency.phoneNumber} • </span>}
                  {emergency.location && <span>📍 {emergency.location}</span>}
                  {!emergency.location && emergency.latitude && emergency.longitude && (
                    <span>📍 LAT {emergency.latitude?.toFixed(5)}, LON {emergency.longitude?.toFixed(5)}</span>
                  )}
                  {emergency.division && <span> • {emergency.division}</span>}
                </Typography>
                <Box sx={{ textAlign: "center", mt: 2, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                  {(emergency.latitude && emergency.longitude) && (
                    <Button
                      variant="contained"
                      color="inherit"
                      onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${emergency.latitude},${emergency.longitude}`, "_blank")}
                      sx={{
                        backgroundColor: "white",
                        color: "#1976d2",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      🗺️ ROUTE TO
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => setEmergencySilenced(true)}
                    disabled={emergencySilenced}
                    sx={{
                      backgroundColor: "white",
                      color: "#ff9800",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {emergencySilenced ? "🔇 SILENCED" : "🔊 SILENCE"}
                  </Button>
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => handleTakeEmergencyResponsibility(emergency.rangerId)}
                    disabled={!!responsibilityTaken[emergency.rangerId] || !!responsibilityLoading[emergency.rangerId]}
                    sx={{
                      backgroundColor: responsibilityTaken[emergency.rangerId] ? "#c8e6c9" : "white",
                      color: "#2e7d32",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      "&:hover": {
                        backgroundColor: responsibilityTaken[emergency.rangerId] ? "#c8e6c9" : "#f5f5f5",
                      },
                    }}
                  >
                    {responsibilityLoading[emergency.rangerId]
                      ? "SENDING..."
                      : responsibilityTaken[emergency.rangerId]
                        ? "✅ RESPONSIBILITY TAKEN"
                        : "🤝 TAKE RESPONSIBILITY"}
                  </Button>
                  {(user?.role === "supervisor" || user?.role === "admin") && (
                    <Button
                      variant="contained"
                      color="inherit"
                      onClick={() => handleClearEmergency(emergency.rangerId)}
                      sx={{
                        backgroundColor: "white",
                        color: "#d32f2f",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      CLEAR EMERGENCY
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Your Emergency Active Banner - shows when the current user has an active emergency */}
        {activeEmergencies.some((e) => e.userId === user?.id) && (
          <Box
            sx={{
              width: "100%",
              backgroundColor: "rgba(255, 152, 0, 0.9)",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "white",
                fontWeight: 900,
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              ⚠️ YOUR EMERGENCY IS ACTIVE ⚠️
            </Typography>
            <Box sx={{ textAlign: "center", mt: 1 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setSelfCancelReason("");
                  setSelfCancelPin("");
                  setSelfCancelError("");
                  setSupervisorClear({ open: false, rangerId: null });
                  setSelfCancelOpen(true);
                  emergencyClearance.prepare('self');
                }}
                sx={{
                  backgroundColor: "white",
                  color: "#e65100",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                CANCEL MY EMERGENCY
              </Button>
            </Box>
          </Box>
        )}

        {incidentBanner && (
          <Alert
            severity={incidentBanner.type}
            onClose={() => setIncidentBanner(null)}
            sx={{ textTransform: "uppercase", fontWeight: 600 }}
          >
            {incidentBanner.message}
          </Alert>
        )}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            px: sectionPadding,
            py: tabletMode ? 2 : 1.75,
            borderRadius: 3,
            borderColor: snowbankEnabled ? "rgba(79, 195, 247, 0.6)" : "rgba(148, 163, 184, 0.35)",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.86)",
            backdropFilter: "blur(6px)",
            position: "relative",
            overflow: "hidden",
            // Snowbank effect - snow on top of the container
            ...(snowbankEnabled && {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "12px",
                background: "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(220,235,250,0.8) 40%, rgba(200,220,245,0.4) 70%, transparent 100%)",
                borderRadius: "10px 10px 50% 50% / 10px 10px 100% 100%",
                filter: "blur(1px)",
                zIndex: 1,
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-3px",
                left: "5%",
                right: "5%",
                height: "18px",
                background: "radial-gradient(ellipse at 20% 50%, rgba(255,255,255,0.9) 0%, transparent 50%), radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.95) 0%, transparent 45%), radial-gradient(ellipse at 80% 50%, rgba(255,255,255,0.9) 0%, transparent 50%)",
                filter: "blur(2px)",
                zIndex: 2,
              },
            }),
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={tabletMode ? 2.5 : 2}>
            {STATUS_BUTTONS.map((button) => (
              <Button
                key={button.value}
                variant={unitStatus === button.value ? "contained" : "outlined"}
                color={button.color}
                onClick={() => {
                  if (button.value === "emergency") {
                    handleActivateEmergency();
                  } else if (button.value === "available" && currentActiveIncident) {
                    // If user is on scene of an incident, open the edit screen instead
                    openIncidentEditor(currentActiveIncident.id);
                  } else {
                    handleStatusChange(button.value, { manual: true });
                  }
                }}
                sx={buttonStyles}
              >
                {getTenCodeLabel(button.value, useTenCodes) || button.label}
              </Button>
            ))}
            {userEnroute && (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  markStatusIntent();
                  setUserEnroute(false);
                  setEnrouteDestinationId(null);
                  setManualOnScene(true);
                }}
                sx={{
                  ...buttonStyles,
                  animation: "pulse 1.5s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.4)" },
                    "50%": { boxShadow: "0 0 0 10px rgba(76, 175, 80, 0)" },
                  },
                }}
              >
                ARRIVE ON SCENE
              </Button>
            )}
          </Stack>
        </Paper>
        {/* Quick Incident Creation Buttons */}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            px: sectionPadding,
            py: tabletMode ? 2 : 1.5,
            borderRadius: 3,
            borderColor: "rgba(148, 163, 184, 0.35)",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.86)",
            backdropFilter: "blur(6px)",
          }}
        >
          <Typography
            variant={tabletMode ? "h6" : "subtitle1"}
            sx={{ textTransform: "uppercase", fontWeight: 700, mb: 1.5, letterSpacing: 0.5 }}
          >
            Quick Incident
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {INCIDENT_TYPES.filter((t) => {
              const normalized = t.toLowerCase();
              return normalized !== "test" && normalized !== "assist other agency";
            }).map((type) => (
              <Button
                key={type}
                variant="contained"
                color="primary"
                onClick={() => handleQuickIncident(type)}
                disabled={newIncidentSubmitting}
                sx={{
                  textTransform: "uppercase",
                  fontWeight: 700,
                  fontSize: tabletMode ? "1rem" : "0.85rem",
                  px: tabletMode ? 3 : 2,
                  py: tabletMode ? 1.5 : 1,
                  minWidth: tabletMode ? 160 : 120,
                }}
              >
                {getTenCodeLabel(type, useTenCodes) || type}
              </Button>
            ))}
            <Button
              variant="contained"
              color="secondary"
              onClick={handleServiceCall}
              disabled={newIncidentSubmitting}
              sx={{
                textTransform: "uppercase",
                fontWeight: 700,
                fontSize: tabletMode ? "1rem" : "0.85rem",
                px: tabletMode ? 3 : 2,
                py: tabletMode ? 1.5 : 1,
                minWidth: tabletMode ? 160 : 120,
              }}
            >
              Service Call
            </Button>
          </Stack>
        </Paper>

        <SectionCard
          title="Active Incidents"
          emptyMessage="No live incidents right now."
          isEmpty={!incidentsLoading && activeIncidents.length === 0}
          tabletMode={tabletMode}
          loading={incidentsLoading}
          error={incidentsError}
        >
          <Table size={tableSize} sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>INCIDENT TYPE</TableCell>
                <TableCell>LOCATION</TableCell>
                <TableCell>PRIMARY UNIT</TableCell>
                <TableCell>DATE / TIME</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeIncidents.map((incident) => {
                const isUsersIncident = user?.rangerId
                  ? Array.isArray(incident.rangerIds) &&
                    incident.rangerIds.some(
                      (id) => id?.toUpperCase() === user.rangerId.toUpperCase()
                    )
                  : false;

                // Check if any ranger on this incident is in emergency
                const hasEmergencyRanger =
                  emergencyRangerIds.has(incident.primaryRangerId?.toUpperCase()) ||
                  (Array.isArray(incident.rangerIds) && incident.rangerIds.some(
                    (id) => emergencyRangerIds.has(id?.toUpperCase())
                  ));

                const incidentRowClasses = [];
                if (flashingSet.has(incident.id)) {
                  incidentRowClasses.push("incident-flash");
                } else if (updatedFlashingSet.has(incident.id)) {
                  incidentRowClasses.push("incident-update-flash");
                }
                if (statusCheckIncidentSet.has(incident.id)) {
                  incidentRowClasses.push("incident-status-check");
                }

                return (
                  <TableRow
                    key={incident.id}
                    hover
                    selected={isUsersIncident}
                    onClick={() => handleIncidentRowClick(incident)}
                    className={incidentRowClasses.join(" ") || undefined}
                    sx={hasEmergencyRanger ? {
                      cursor: "pointer",
                      animation: "emergencyRowFlash 1s ease-in-out infinite",
                      "@keyframes emergencyRowFlash": {
                        "0%, 100%": {
                          backgroundColor: "rgba(244, 67, 54, 0.3)",
                        },
                        "50%": {
                          backgroundColor: "rgba(244, 67, 54, 0.6)",
                        },
                      },
                    } : { cursor: "pointer" }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant={tabletMode ? "h5" : "body1"}
                          fontWeight={700}
                          sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
                        >
                          {incident.incidentType}
                        </Typography>
                        {incident.status?.toLowerCase() === STATUS_VALUES.INCOMPLETE && (
                          <Chip
                            label="INFO NEEDED"
                            color="error"
                            size={tabletMode ? "medium" : "small"}
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                        {statusCheckIncidentSet.has(incident.id) && (
                          <Chip
                            label="CHECK STATUS"
                            color="warning"
                            size={tabletMode ? "medium" : "small"}
                            sx={{ fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{formatIncidentLocation(incident, decodedLocations)}</TableCell>
                    <TableCell>
                      {incident.primaryRangerId || incident.rangerIds?.[0] || "—"}
                    </TableCell>
                    <TableCell>
                      {incident.occurredAtUtc
                        ? formatUtcToLocal(incident.occurredAtUtc, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SectionCard>

        {/* Waze Alerts Section - only show when enabled */}
        {wazeEnabled && (
        <SectionCard
          title="Waze Alerts"
          warning={wazeError}
          emptyMessage={
            wazeError ? "No confirmed alert snapshot is available." :
            (wazeCityFilter.length > 0 || wazeTypeFilter.length > 0 || wazeDistanceFilter > 0)
              ? "No alerts match your filters."
              : "No active Waze alerts."
          }
          isEmpty={!wazeAlertsLoading && filteredWazeAlerts.length === 0}
          tabletMode={tabletMode}
          loading={wazeAlertsLoading}
          actions={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setWazeConfigOpen(true)}
                sx={{ fontWeight: 700, textTransform: "uppercase" }}
              >
                {wazeFilterCount > 0 ? `${wazeFilterCount} Filter${wazeFilterCount > 1 ? 's' : ''} Active` : 'Configure'}
              </Button>
              {wazeFilterCount > 0 && (
                <Chip
                  label={`${filteredWazeAlerts.length}/${wazeAlerts.length}`}
                  size="small"
                  color="info"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          }
        >
          <Table size={tableSize} sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>TYPE</TableCell>
                <TableCell>LOCATION</TableCell>
                <TableCell>DISTANCE</TableCell>
                <TableCell>RELIABILITY</TableCell>
                <TableCell>CONFIDENCE</TableCell>
                <TableCell>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWazeAlerts.map((alert) => {
                const isClaiming = wazeClaimingId === alert.id;
                const isMyAlert = user?.rangerId && alert.claimedByRangerId?.toUpperCase() === user.rangerId.toUpperCase();

                // Parse subtype for better labeling
                const subtypeUpper = alert.subtype?.toUpperCase() || "";
                const isCarStopped = subtypeUpper.includes("CAR_STOPPED");
                const isOnRoad = subtypeUpper.includes("ON_ROAD");
                const isOnShoulder = subtypeUpper.includes("ON_SHOULDER");
                const isEmergencyVehicle = subtypeUpper.includes("EMERGENCY_VEHICLE");

                // Determine the main type label
                let typeLabel;
                if (isEmergencyVehicle) {
                  typeLabel = "ASSIST OTHER AGENCY";
                } else if (isCarStopped) {
                  typeLabel = "ASSIST MOTORIST";
                } else if (alert.type === "HAZARD") {
                  typeLabel = "DEBRIS";
                } else {
                  typeLabel = alert.type?.replace(/_/g, " ") || "UNKNOWN";
                }

                // Extract agency/vehicle info from report description for emergency vehicles
                let agencyDetail = "";
                if (isEmergencyVehicle && alert.reportDescription) {
                  const desc = alert.reportDescription;

                  // Known vehicle/agency patterns to extract
                  const vehiclePatterns = [
                    /\b(AAA\s*Truck)/i,
                    /\b(Tow\s*Truck)/i,
                    /\b(Police\s*(?:Car|Vehicle|Cruiser)?)/i,
                    /\b(Fire\s*(?:Truck|Engine|Vehicle)?)/i,
                    /\b(Ambulance)/i,
                    /\b(EMS\s*(?:Vehicle)?)/i,
                    /\b(Sheriff)/i,
                    /\b(Highway\s*Patrol)/i,
                    /\b(State\s*Trooper)/i,
                    /\b(DOT\s*(?:Truck|Vehicle)?)/i,
                    /\b(Service\s*(?:Truck|Vehicle))/i,
                    /\b(Ranger\s*(?:Truck|Vehicle)?)/i,
                    /\b(Construction\s*(?:Vehicle|Truck)?)/i,
                    /\b(Utility\s*(?:Truck|Vehicle)?)/i,
                    /\b(Emergency\s*Vehicle)/i,
                  ];

                  // Try to match known vehicle patterns first
                  for (const pattern of vehiclePatterns) {
                    const match = desc.match(pattern);
                    if (match) {
                      agencyDetail = match[1].trim().toUpperCase();
                      break;
                    }
                  }

                  // Fallback: extract text before common suffixes if no pattern matched
                  if (!agencyDetail) {
                    const suffixPatterns = [/\s+ahead\b/i, /\s+on\s+roadside\b/i, /\s+on\s+road\b/i, /\s+on\s+shoulder\b/i, /\s+stopped\b/i, /\s+blocking\b/i];
                    let cleaned = desc;
                    for (const suffix of suffixPatterns) {
                      cleaned = cleaned.replace(suffix, "");
                    }
                    // Remove "Report:" prefix if present
                    cleaned = cleaned.replace(/^report:\s*/i, "").trim();
                    // Remove trailing punctuation/whitespace
                    cleaned = cleaned.replace(/[,\-\s]+$/, "").trim();
                    if (cleaned && cleaned.length < 30 && cleaned.length > 2) {
                      agencyDetail = cleaned.toUpperCase();
                    }
                  }
                }

                // Use extracted vehicle type as the main label for emergency vehicles
                if (isEmergencyVehicle && agencyDetail) {
                  typeLabel = agencyDetail;
                }

                // Determine location detail (lane vs shoulder)
                let locationDetail = "";
                if (isEmergencyVehicle) {
                  // For emergency vehicles, show lane/shoulder position
                  if (isOnShoulder) locationDetail = "ON SHOULDER";
                  else if (isOnRoad) locationDetail = "IN LANE";
                  else locationDetail = "ON ROADSIDE";
                } else if (isCarStopped) {
                  // For assist motorist, show if on road or shoulder
                  if (isOnShoulder) locationDetail = "ON SHOULDER";
                  else if (isOnRoad) locationDetail = "IN LANE";
                } else if (alert.type === "HAZARD") {
                  // For debris, show lane vs shoulder
                  if (isOnShoulder) locationDetail = "ON SHOULDER";
                  else if (isOnRoad) locationDetail = "IN LANE";
                }

                // Format location: expand directions, remove state abbreviation
                let formattedStreet = expandDirections(alert.street || "");
                // Remove state abbreviation from city (e.g., "McKinney, TX" -> "McKinney")
                let formattedCity = (alert.city || "").replace(/,?\s*(TX|Texas)$/i, "").trim();
                const locationParts = [formattedStreet, formattedCity].filter(Boolean);
                const locationDisplay = locationParts.length > 0 ? locationParts.join(", ") : "Unknown";

                // Format distance
                const distanceDisplay = alert.distanceMiles !== null
                  ? `${alert.distanceMiles.toFixed(2)} mi`
                  : "—";

                return (
                  <TableRow
                    key={alert.id}
                    hover
                    selected={isMyAlert}
                    sx={{
                      backgroundColor: alert.status === "dispatched"
                        ? "rgba(255, 152, 0, 0.1)"
                        : undefined,
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={typeLabel}
                          color={
                            isEmergencyVehicle ? "info" :
                            isCarStopped ? "success" :
                            alert.type === "ACCIDENT" ? "error" :
                            alert.type === "HAZARD" ? "warning" :
                            alert.type === "ROAD_CLOSED" ? "secondary" :
                            "default"
                          }
                          size={tabletMode ? "medium" : "small"}
                          sx={{ fontWeight: 700 }}
                        />
                        {locationDetail && (
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", fontWeight: 500 }}>
                            {locationDetail}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant={tabletMode ? "body1" : "body2"}
                        sx={{ textTransform: "uppercase", fontWeight: 500 }}
                      >
                        {locationDisplay}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant={tabletMode ? "body1" : "body2"}
                        sx={{
                          fontWeight: 700,
                          color: alert.distanceMiles !== null && alert.distanceMiles <= 2 ? "success.main" : "inherit"
                        }}
                      >
                        {distanceDisplay}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Reliability score 0-10 */}
                      <Chip
                        label={`${alert.reliability}/10`}
                        size={tabletMode ? "medium" : "small"}
                        color={
                          alert.reliability >= 8 ? "success" :
                          alert.reliability >= 5 ? "warning" :
                          "error"
                        }
                        variant={alert.reliability >= 8 ? "filled" : "outlined"}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      {/* Confidence score 0-10 */}
                      <Chip
                        label={`${alert.confidence}/10`}
                        size={tabletMode ? "medium" : "small"}
                        color={
                          alert.confidence >= 8 ? "success" :
                          alert.confidence >= 5 ? "warning" :
                          "error"
                        }
                        variant={alert.confidence >= 8 ? "filled" : "outlined"}
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {/* Only show action buttons when within 0.25 miles */}
                        {alert.status === "active" && alert.distanceMiles !== null && alert.distanceMiles <= 0.25 && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size={tabletMode ? "medium" : "small"}
                              disabled={isClaiming}
                              onClick={() => handleWazeOnScene(alert)}
                              sx={{
                                fontWeight: 700,
                                textTransform: "uppercase",
                                animation: "pulse 1.5s ease-in-out infinite",
                                "@keyframes pulse": {
                                  "0%, 100%": { boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.4)" },
                                  "50%": { boxShadow: "0 0 0 8px rgba(76, 175, 80, 0)" },
                                },
                              }}
                            >
                              {isClaiming ? "..." : "ON SCENE"}
                            </Button>
                            <Button
                              variant="outlined"
                              color="warning"
                              size={tabletMode ? "medium" : "small"}
                              disabled={isClaiming}
                              onClick={() => handleWazeClearAlert(alert.id, "UTL")}
                              sx={{ fontWeight: 700, textTransform: "uppercase" }}
                            >
                              {isClaiming ? "..." : "UTL"}
                            </Button>
                          </>
                        )}
                        {alert.status === "dispatched" && isMyAlert && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size={tabletMode ? "medium" : "small"}
                              disabled={isClaiming}
                              onClick={() => handleWazeConvertToIncident(alert.id)}
                              sx={{ fontWeight: 700, textTransform: "uppercase" }}
                            >
                              CREATE INCIDENT
                            </Button>
                            <Button
                              variant="outlined"
                              color="warning"
                              size={tabletMode ? "medium" : "small"}
                              disabled={isClaiming}
                              onClick={() => handleWazeClearAlert(alert.id, "UTL")}
                              sx={{ fontWeight: 700, textTransform: "uppercase" }}
                            >
                              UTL
                            </Button>
                            <Button
                              variant="text"
                              color="secondary"
                              size={tabletMode ? "medium" : "small"}
                              disabled={isClaiming}
                              onClick={() => handleWazeReleaseAlert(alert.id)}
                              sx={{ fontWeight: 700, textTransform: "uppercase" }}
                            >
                              RELEASE
                            </Button>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </SectionCard>
        )}

        <SectionCard
          title="Active Units"
          emptyMessage="No other units in service."
          isEmpty={activeUnits.length === 0}
          tabletMode={tabletMode}
          loading={activeUnitsLoading}
        >
          <Table size={tableSize} sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell>UNIT</TableCell>
                <TableCell>STATUS</TableCell>
                {!gpsHidden && <TableCell>LOCATION</TableCell>}
                <TableCell>ADDITIONAL INFO</TableCell>
                <TableCell>DIVISION</TableCell>
                <TableCell>LAST CHECK-IN</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeUnits.map((unit) => {
                const isInEmergency = emergencyRangerIds.has(unit.id?.toUpperCase());
                const isCurrentUser = unit.id?.toUpperCase() === (userStatus.rangerId || "").toUpperCase();
                return (
                <TableRow
                  key={unit.id}
                  hover
                  selected={isCurrentUser}
                  className={unit.needsStatusCheck ? "unit-status-check" : undefined}
                  sx={isInEmergency ? {
                    animation: "emergencyRowFlash 1s ease-in-out infinite",
                    "@keyframes emergencyRowFlash": {
                      "0%, 100%": {
                        backgroundColor: "rgba(244, 67, 54, 0.3)",
                      },
                      "50%": {
                        backgroundColor: "rgba(244, 67, 54, 0.6)",
                      },
                    },
                  } : unit.isStale ? { opacity: 0.6, backgroundColor: "action.hover" } : undefined}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        variant={tabletMode ? "h6" : "body1"}
                        fontWeight={700}
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: unit.isStale ? "text.disabled" : "text.primary",
                        }}
                      >
                        {unit.id}
                      </Typography>
                      {unit.needsStatusCheck && (
                        <Chip
                          label="CHECK STATUS"
                          color="warning"
                          size={tabletMode ? "medium" : "small"}
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                      {/* Allow other users to clear status check for rangers who haven't acknowledged */}
                      {unit.needsStatusCheck && !isCurrentUser && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleClearOtherUserStatusCheck(unit.id)}
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            minWidth: "auto",
                            py: 0.25,
                            px: 1,
                          }}
                        >
                          CLEAR
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={unit.isStale ? { color: "text.disabled" } : undefined}>
                    {unit.displayStatus}
                  </TableCell>
                  {!gpsHidden && (
                    <TableCell sx={unit.isStale ? { color: "text.disabled" } : undefined}>
                      {unit.locationLabel || unit.location || "NOT PROVIDED"}
                    </TableCell>
                  )}
                  <TableCell sx={unit.isStale ? { color: "text.disabled" } : undefined}>
                    {unit.displayInfo}
                  </TableCell>
                  <TableCell sx={unit.isStale ? { color: "text.disabled" } : undefined}>
                    {unit.division}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={unit.isStale ? { color: "text.disabled" } : undefined}>
                        {unit.lastUpdate}
                      </Typography>
                      {unit.isStale && (
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 700,
                            color: "grey.500",
                            fontSize: "0.75rem",
                          }}
                        >
                          STALE
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
          {activeUnitsError && (
            <Alert severity="warning" sx={{ mt: 2, textTransform: "uppercase" }}>
              {activeUnitsError}
            </Alert>
          )}
        </SectionCard>

        <SectionCard
          title="AVL Map"
          emptyMessage="No GPS positions available."
          isEmpty={false}
          tabletMode={tabletMode}
          loading={activeUnitsLoading}
          actions={
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={<Switch checked={gpsDisabled} onChange={handleGpsDisabledToggle} color="error" />}
                label={gpsDisabled ? "GPS Disabled" : "GPS Enabled"}
                labelPlacement="start"
                sx={{ textTransform: "uppercase", fontWeight: 600 }}
              />
              <FormControlLabel
                control={<Switch checked={gpsHidden} onChange={handleGpsVisibilityToggle} color="warning" disabled={Boolean(gpsReactivateAt) || gpsDisabled} />}
                label={gpsHidden ? "Map Hidden" : "Map Visible"}
                labelPlacement="start"
                sx={{ textTransform: "uppercase", fontWeight: 600 }}
              />
              {gpsReactivateAt && (
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                  Reactivating in {gpsCooldownRemaining}s
                </Typography>
              )}
            </Stack>
          }
        >
          {gpsDisabled && (
            <Alert severity="info" sx={{ textTransform: "uppercase", mb: 1 }}>
              DESKTOP GPS DISABLED — USING PHONE LOCATION IF LOGGED IN ON MOBILE
            </Alert>
          )}
          {gpsHidden ? (
            <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
              {gpsReactivateAt
                ? `GPS REACTIVATING... ${gpsCooldownRemaining}s REMAINING`
                : "DISABLING YOUR GPS REMOVES THE ABILITY TO SEE THE MAP AND OTHERS LOCATIONS"}
            </Alert>
          ) : typeof window === "undefined" ? (
            <Alert severity="info" sx={{ textTransform: "uppercase" }}>
              MAP AVAILABLE IN BROWSER MODE ONLY.
            </Alert>
          ) : avlMapUnits.length === 0 && avlMapIncidents.length === 0 && avlMapWazeAlerts.length === 0 && avlMapAbandonedVehicles.length === 0 ? (
            <Alert severity="info" sx={{ textTransform: "uppercase" }}>
              NO GPS POSITIONS AVAILABLE YET.
            </Alert>
          ) : (
            <AvlMap
              units={avlMapUnits}
              incidents={avlMapIncidents}
              wazeAlerts={avlMapWazeAlerts}
              abandonedVehicles={avlMapAbandonedVehicles}
              destination={avlMapDestination}
              enrouteUnitId={userEnroute ? (user?.rangerId || `USER-${user?.id}`) : null}
              tabletMode={tabletMode}
              isDarkMode={isDarkMode}
              onIncidentClick={openIncidentEditor}
            />
          )}}
        </SectionCard>
      </Stack>
      </Box>

      {/* Waze Configuration Dialog */}
      <Dialog
        open={wazeConfigOpen}
        onClose={() => setWazeConfigOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Waze Alert Configuration
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Enable/Disable Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={wazeEnabled}
                  onChange={(e) => {
                    setWazeEnabled(e.target.checked);
                    if (typeof window !== "undefined") {
                      window.localStorage.setItem("wazeEnabled", JSON.stringify(e.target.checked));
                    }
                  }}
                  color="warning"
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 600, textTransform: "uppercase" }}>
                  {wazeEnabled ? "Waze Alerts Enabled" : "Waze Alerts Disabled"}
                </Typography>
              }
            />

            <Divider />

            {/* Distance Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase", mb: 1 }}>
                Distance Filter
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={wazeDistanceFilter}
                  onChange={(e) => handleWazeDistanceChange(e.target.value)}
                  disabled={!wazeEnabled || wazeCityFilter.length > 0}
                >
                  {WAZE_DISTANCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {wazeCityFilter.length > 0 && (
                  <FormHelperText>
                    Distance filter disabled when filtering by city
                  </FormHelperText>
                )}
                {wazeDistanceFilter > 0 && !gpsCoords && wazeCityFilter.length === 0 && (
                  <FormHelperText error>
                    GPS location required for distance filtering
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* City Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase", mb: 1 }}>
                Filter by City {wazeDistanceFilter > 0 && "(or)"}
              </Typography>
              {wazeDistanceFilter > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Selecting a city will clear the distance filter
                </Typography>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {WAZE_CITY_OPTIONS.map((city) => (
                  <Chip
                    key={city}
                    label={city.toUpperCase()}
                    size="small"
                    variant={wazeCityFilter.includes(city) ? "filled" : "outlined"}
                    color={wazeCityFilter.includes(city) ? "primary" : "default"}
                    onClick={() => handleWazeCityToggle(city)}
                    disabled={!wazeEnabled}
                    sx={{ fontWeight: 600, cursor: "pointer", mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Type Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase", mb: 1 }}>
                Filter by Alert Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {WAZE_TYPE_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.label.toUpperCase()}
                    size="small"
                    variant={wazeTypeFilter.includes(opt.value) ? "filled" : "outlined"}
                    color={wazeTypeFilter.includes(opt.value) ? "warning" : "default"}
                    onClick={() => handleWazeTypeToggle(opt.value)}
                    disabled={!wazeEnabled}
                    sx={{ fontWeight: 600, cursor: "pointer", mb: 1 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Summary */}
            {(wazeCityFilter.length > 0 || wazeTypeFilter.length > 0 || wazeDistanceFilter > 0) && (
              <Alert severity="info" sx={{ textTransform: "uppercase" }}>
                {wazeDistanceFilter > 0 && `Within ${wazeDistanceFilter} miles`}
                {wazeDistanceFilter > 0 && (wazeCityFilter.length > 0 || wazeTypeFilter.length > 0) && " • "}
                {wazeCityFilter.length > 0 && `${wazeCityFilter.length} cities`}
                {wazeCityFilter.length > 0 && wazeTypeFilter.length > 0 && " • "}
                {wazeTypeFilter.length > 0 && `${wazeTypeFilter.length} types`}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWazeCityFilter([]);
              setWazeTypeFilter([]);
              setWazeDistanceFilter(5); // Reset to default 5 miles
              if (typeof window !== "undefined") {
                window.localStorage.removeItem("wazeCityFilter");
                window.localStorage.removeItem("wazeTypeFilter");
                window.localStorage.setItem("wazeDistanceFilter", JSON.stringify(5));
              }
            }}
            color="error"
            sx={{ fontWeight: 700, textTransform: "uppercase" }}
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={() => setWazeConfigOpen(false)}
            variant="contained"
            sx={{ fontWeight: 700, textTransform: "uppercase" }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={incidentConflictOpen}
        onClose={handleIncidentConflictClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Incident In Progress
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
              YOU'RE ALREADY ON SCENE OF AN INCIDENT.
            </Alert>
            {incidentConflict ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ textTransform: "uppercase", mb: 1 }}>
                  {incidentConflict.incidentType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CAD {incidentConflict.cadId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Started {incidentConflict.occurredAtUtc
                    ? formatUtcToLocal(incidentConflict.occurredAtUtc)
                    : "Unknown"}
                </Typography>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Loading incident details...
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Finish the active incident later to start a new one, or cancel to return to the
              dashboard.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIncidentConflictClose} disabled={incidentConflictSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleConflictFinishLater}
            variant="contained"
            color="warning"
            disabled={incidentConflictSaving}
            sx={{ textTransform: "uppercase" }}
          >
            {incidentConflictSaving ? "Working..." : "Finish Later & Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assist Ranger - Select Incident Modal */}
      <Dialog
        open={assistRangerOpen}
        onClose={() => !assistRangerJoining && setAssistRangerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Assist Ranger - Select Incident
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ textTransform: "uppercase" }}>
              Select an active incident to assist with. You will be added as a participant.
            </Alert>
            {activeIncidents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                No active incidents available.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {activeIncidents.map((incident) => (
                  <Paper
                    key={incident.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: assistRangerJoining ? "default" : "pointer",
                      "&:hover": assistRangerJoining ? {} : { bgcolor: "action.hover" },
                      opacity: assistRangerJoining ? 0.6 : 1,
                    }}
                    onClick={() => !assistRangerJoining && handleJoinIncident(incident.id)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6" sx={{ textTransform: "uppercase" }}>
                          {incident.incidentType}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          CAD {incident.cadId} • Primary: {incident.primaryRangerId || "Unknown"}
                        </Typography>
                        {incident.location && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {incident.location}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={incident.status?.toUpperCase() || "ACTIVE"}
                        size="small"
                        color="warning"
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAssistRangerOpen(false)}
            disabled={assistRangerJoining}
            sx={{ textTransform: "uppercase" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Missed Status Check Alert Modal */}
      <Dialog
        open={missedStatusCheckOpen}
        onClose={() => {
          if (missedStatusCheckTaking || missedStatusCheckResolving) return;
          // Only close if user has taken responsibility and resolved, or there are no more alerts
          const unresolvedForOthers = missedStatusChecks.filter(
            (c) => !c.responsibleUserId && c.rangerId?.toUpperCase() !== user?.rangerId?.toUpperCase()
          );
          if (unresolvedForOthers.length === 0 || selectedMissedCheck) {
            setMissedStatusCheckOpen(false);
            setSelectedMissedCheck(null);
            setMissedHandoffReview(null);
            setMissedAssignmentHistory(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            border: "3px solid",
            borderColor: "error.main",
            m: { xs: 1, sm: 4 },
            width: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
            maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
          },
        }}
      >
        <DialogTitle
          sx={{
            textTransform: "uppercase",
            fontWeight: 700,
            bgcolor: "error.main",
            color: "error.contrastText",
          }}
        >
          ⚠️ MISSED STATUS CHECK ALERT
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, overflowWrap: "anywhere" }}>
          {missedHandoffReview ? (
            <Stack spacing={2}>
              <Alert severity="warning">The assigned account is unavailable. Review the current follow-up before taking responsibility.</Alert>
              <Typography>Ranger: {missedHandoffReview.rangerName || missedHandoffReview.rangerId}</Typography>
              <Typography>CAD: {missedHandoffReview.cadId || "N/A"}</Typography>
              <Typography>Assigned to: {missedHandoffReview.responsibleUserName || "Unavailable account"}</Typography>
              <Typography>Assigned at: {formatUtcToLocal(missedHandoffReview.responsibilityTakenAtUtc)}</Typography>
              <Typography>The original assignment and this handoff will remain in history. Contact details appear after the handoff is confirmed.</Typography>
              <Button variant="contained" disabled={missedStatusCheckTaking} onClick={handleMissedHandoff} sx={{ minHeight: 44 }}>
                {missedStatusCheckTaking ? "Confirming handoff?" : "Confirm handoff to me"}
              </Button>
            </Stack>
          ) : selectedMissedCheck ? (
            // User has taken responsibility - show phone number and resolution options
            <Stack spacing={3}>
              <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
                You have taken responsibility for this alert. Please contact the ranger and confirm their status.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "action.hover", color: "text.primary" }}>
                <Typography variant="h6" sx={{ textTransform: "uppercase", mb: 1 }}>
                  Ranger: {selectedMissedCheck.rangerName || selectedMissedCheck.rangerId}
                </Typography>
                {selectedMissedCheck.rangerPhone && (
                  <Button
                    component="a"
                    href={`tel:${selectedMissedCheck.rangerPhone}`}
                    variant="outlined"
                    sx={{ fontWeight: 700, mb: 2, minHeight: 44, maxWidth: "100%", overflowWrap: "anywhere" }}
                  >
                    Call {selectedMissedCheck.rangerPhone}
                  </Button>
                )}
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>Incident:</strong> {selectedMissedCheck.incidentType?.toUpperCase() || "UNKNOWN"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>CAD #:</strong> {selectedMissedCheck.cadId || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Location:</strong> {selectedMissedCheck.locationDescription || (selectedMissedCheck.latitude != null && selectedMissedCheck.longitude != null ? `LAT ${Number(selectedMissedCheck.latitude).toFixed(4)} / LON ${Number(selectedMissedCheck.longitude).toFixed(4)}` : "Unknown location")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Missed At:</strong> {formatUtcToLocal(selectedMissedCheck.missedAtUtc)}
                </Typography>
              </Paper>
              <Button onClick={handleMissedAssignmentHistory} sx={{ minHeight: 44 }}>Assignment history</Button>
              {missedAssignmentHistory?.id === selectedMissedCheck.id && (
                <Stack spacing={1}>
                  {missedAssignmentHistory.error ? <Alert severity="warning">{missedAssignmentHistory.error}</Alert> :
                    missedAssignmentHistory.rows.length === 0 ? <Typography>No recorded assignment events. Earlier assignments may predate history tracking.</Typography> :
                    missedAssignmentHistory.rows.map(entry => (
                      <Typography key={entry.id} variant="body2">
                        {formatUtcToLocal(entry.assignedAtUtc)}: {entry.actorName || `Account ${entry.actorUserId}`} assigned follow-up to {entry.assignedUserName || `Account ${entry.assignedUserId}`}.
                        {entry.previousUserId != null && ` Previous: ${entry.previousUserName || `Account ${entry.previousUserId}`} (${formatUtcToLocal(entry.previousTakenAtUtc)}).`}
                        {entry.reason === "account-disabled" ? " Previous account disabled." : entry.reason === "account-missing" ? " Previous account unavailable." : " Initial claim."}
                      </Typography>
                    ))}
                </Stack>
              )}
              <Typography variant="body2" sx={{ textTransform: "uppercase", textAlign: "center" }}>
                After contacting the ranger, select the appropriate action:
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  disabled={missedStatusCheckResolving}
                  onClick={() => handleResolveMissedCheck(selectedMissedCheck.id, "10-4")}
                  sx={{ textTransform: "uppercase", minHeight: 44, minWidth: { xs: 0, sm: 150 } }}
                >
                  {missedStatusCheckResolving ? <CircularProgress size={24} /> : "10-4 (OK)"}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  size="large"
                  disabled={missedStatusCheckResolving}
                  onClick={() => handleResolveMissedCheck(selectedMissedCheck.id, "clear")}
                  sx={{ textTransform: "uppercase", minHeight: 44, minWidth: { xs: 0, sm: 150 } }}
                >
                  {missedStatusCheckResolving ? <CircularProgress size={24} /> : "CLEAR"}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                10-4: Ranger is OK, continue incident • CLEAR: Close incident (ranger can finish later)
              </Typography>
            </Stack>
          ) : (
            // Show list of missed status checks that need attention
            <Stack spacing={2}>
              <Alert severity="error" sx={{ textTransform: "uppercase" }}>
                A ranger has not responded to their status check for over 5 minutes!
              </Alert>
              {missedStatusChecks
                .filter((c) => !c.resolvedAtUtc && c.rangerId?.toUpperCase() !== user?.rangerId?.toUpperCase())
                .map((check) => (
                  <Paper
                    key={check.id}
                    variant="outlined"
                    sx={{ p: 2, bgcolor: "action.hover", color: "text.primary", borderColor: check.responsibleUserId ? "divider" : "error.main" }}
                  >
                    <Stack spacing={1}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ textTransform: "uppercase" }}>
                            {check.rangerName || check.rangerId}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {check.incidentType?.toUpperCase() || "INCIDENT"} • CAD {check.cadId || "N/A"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {check.locationDescription || (check.latitude != null && check.longitude != null ? `LAT ${Number(check.latitude).toFixed(4)} / LON ${Number(check.longitude).toFixed(4)}` : "Unknown location")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Missed at: {formatUtcToLocalTime(check.missedAtUtc)}
                          </Typography>
                        </Box>
                        {check.responsibleUserId && check.responsibleUserId !== user?.id ? (
                          <Stack spacing={1} sx={{ maxWidth: "100%", minWidth: 0 }}>
                          <Chip
                            label={check.responsibleUserUnavailable ? `${check.responsibleUserName || "ASSIGNED ACCOUNT"} IS UNAVAILABLE` : `${check.responsibleUserName || "SOMEONE"} IS HANDLING`}
                            color="info"
                            size="small"
                            sx={{ textTransform: "uppercase", maxWidth: "100%", height: "auto", "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: 0.5 } }}
                          />
                          {check.canTakeOver && ["admin", "supervisor"].includes(String(user?.role || "").trim().toLowerCase()) && (
                            <Button variant="outlined" onClick={() => { setMissedAssignmentHistory(null); setMissedHandoffReview(check); }} sx={{ minHeight: 44 }}>Review handoff</Button>
                          )}
                          </Stack>
                        ) : (
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            disabled={missedStatusCheckTaking}
                            onClick={() => handleTakeResponsibility(check.id)}
                            sx={{ textTransform: "uppercase", minHeight: 44, width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
                          >
                            {missedStatusCheckTaking ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              check.responsibleUserId === user?.id ? "CONTINUE FOLLOW-UP" : "TAKE RESPONSIBILITY"
                            )}
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              {missedStatusChecks.filter(
                (c) => !c.resolvedAtUtc && c.rangerId?.toUpperCase() !== user?.rangerId?.toUpperCase()
              ).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: "uppercase", textAlign: "center" }}>
                  No missed status checks require attention.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {(selectedMissedCheck || missedHandoffReview) && (
            <Button disabled={missedStatusCheckResolving || missedStatusCheckTaking} onClick={() => { setSelectedMissedCheck(null); setMissedHandoffReview(null); setMissedAssignmentHistory(null); }} sx={{ minHeight: 44 }}>
              Back to alerts
            </Button>
          )}
          <Button
            onClick={() => {
              setMissedStatusCheckOpen(false);
              setSelectedMissedCheck(null);
              setMissedHandoffReview(null);
              setMissedAssignmentHistory(null);
            }}
            disabled={missedStatusCheckResolving || missedStatusCheckTaking}
            sx={{ textTransform: "uppercase", minHeight: 44 }}
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={newIncidentOpen}
        onClose={handleNewIncidentClose}
        maxWidth="sm"
        fullWidth
        keepMounted
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          New Incident
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleNewIncidentSubmit} sx={{ mt: 1 }}>
            <Stack spacing={3}>
              <Alert severity="info" sx={{ textTransform: "uppercase" }}>
                LOCATION COORDINATES WILL BE ATTACHED AUTOMATICALLY WHEN YOU SUBMIT.
              </Alert>
              {newIncidentError && (
                <Alert severity="error" sx={{ textTransform: "uppercase" }}>
                  {newIncidentError}
                </Alert>
              )}
              <TextField
                select
                label="Source"
                value={newIncidentForm.source}
                onChange={handleNewIncidentChange("source")}
                required
                fullWidth
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value
                      ? value
                      : (
                          <Typography sx={{ color: "text.disabled", fontWeight: 600 }}>
                            SELECT SOURCE
                          </Typography>
                        ),
                }}
                InputLabelProps={{ shrink: true, sx: { textTransform: "uppercase", fontWeight: 700 } }}
              >
                <MenuItem value="" disabled>
                  SELECT SOURCE
                </MenuItem>
                {SOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Incident Type"
                value={newIncidentForm.incidentType}
                onChange={handleNewIncidentChange("incidentType")}
                required
                fullWidth
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value
                      ? value
                      : (
                          <Typography sx={{ color: "text.disabled", fontWeight: 600 }}>
                            SELECT INCIDENT TYPE
                          </Typography>
                        ),
                }}
                InputLabelProps={{ shrink: true, sx: { textTransform: "uppercase", fontWeight: 700 } }}
              >
                <MenuItem value="" disabled>
                  SELECT INCIDENT TYPE
                </MenuItem>
                {INCIDENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Notes"
                value={newIncidentForm.notes}
                onChange={handleNewIncidentChange("notes")}
                multiline
                minRows={4}
                fullWidth
                placeholder="ADD CONTEXT FOR DISPATCH"
              />
              {needsVehicleInfo(newIncidentForm.incidentType) && (
                <>
                  <Typography variant="subtitle1" sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                    Vehicle Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Year"
                        value={newIncidentForm.vehicle.year}
                        onChange={handleNewVehicleChange("year")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Make"
                        value={newIncidentForm.vehicle.make}
                        onChange={handleNewVehicleChange("make")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Model"
                        value={newIncidentForm.vehicle.model}
                        onChange={handleNewVehicleChange("model")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Color"
                        value={newIncidentForm.vehicle.color}
                        onChange={handleNewVehicleChange("color")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="State"
                        value={newIncidentForm.vehicle.state}
                        onChange={handleNewVehicleChange("state")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Plate"
                        value={newIncidentForm.vehicle.plate}
                        onChange={handleNewVehicleChange("plate")}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Stack>
            <DialogActions sx={{ px: 0, pt: 4, justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              {!newIncidentForm.source && !newIncidentForm.incidentType && (
                <Stack spacing={1} sx={{ minWidth: 220 }}>
                  <Typography variant="caption" sx={{ letterSpacing: 1, fontWeight: 700, color: "text.secondary" }}>
                    QUICK CREATE
                  </Typography>
                  <Button
                    onClick={() => handleQuickCreateIncident({ source: "Self", incidentType: "Debris", notes: "" })}
                    variant="outlined"
                    disabled={newIncidentSubmitting}
                    sx={{ textTransform: "uppercase" }}
                  >
                    Quick Create Debris
                  </Button>
                  <Button
                    onClick={() => handleQuickCreateIncident({ source: "Self", incidentType: "Assist Motorist", notes: "" })}
                    variant="outlined"
                    disabled={newIncidentSubmitting}
                    sx={{ textTransform: "uppercase" }}
                  >
                    Quick Create Assist Motorist
                  </Button>
                </Stack>
              )}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button onClick={handleNewIncidentClose} sx={{ textTransform: "uppercase" }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={newIncidentSubmitting}
                  sx={{ textTransform: "uppercase", minWidth: 160 }}
                >
                  {newIncidentSubmitting ? "Saving..." : "Submit"}
                </Button>
              </Box>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Busy Status Modal */}
      <Dialog
        open={busyModalOpen}
        onClose={handleBusyModalCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Busy - Additional Information
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Alert severity="info" sx={{ textTransform: "uppercase" }}>
              {statusIntent.pending ? 'BUSY requested. Waiting for confirmation.'
                : 'Your confirmed status is BUSY. Optional details are saved with Save Details.'}
            </Alert>
            {statusIntent.error && <Alert severity="warning" action={
              <Button color="inherit" onClick={statusIntent.retry}>Retry requested change</Button>
            }>{statusIntent.error}</Alert>}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, textTransform: "uppercase" }}>
                Reason (Optional)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {BUSY_REASON_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    variant={busyReason === option.value ? "contained" : "outlined"}
                    color="inherit"
                    onClick={() => setBusyReason(busyReason === option.value ? "" : option.value)}
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: 700,
                      mb: 1,
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Stack>
            </Box>
            <TextField
              label="Location (Optional)"
              value={busyLocation}
              onChange={(e) => setBusyLocation(e.target.value)}
              placeholder="Enter current location"
              fullWidth
              InputLabelProps={{ sx: { textTransform: "uppercase", fontWeight: 600 } }}
            />
            <TextField
              label="Additional Information (Optional)"
              value={busyInfo}
              onChange={(e) => setBusyInfo(e.target.value)}
              placeholder="Enter extra context"
              multiline
              minRows={3}
              fullWidth
              InputLabelProps={{ sx: { textTransform: "uppercase", fontWeight: 600 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBusyModalCancel} sx={{ textTransform: "uppercase" }}>
            Skip
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleBusyModalSubmit}
            sx={{ textTransform: "uppercase", fontWeight: 700 }}
          >
            Save Details
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusModalOpen}
        onClose={() => {}}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        keepMounted
        BackdropProps={{ sx: statusBackdropStyles }}
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Status Check Required
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {statusCheckError && <Alert severity="error">{statusCheckError}</Alert>}
            <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
              YOU'VE BEEN ON SCENE FOR {statusCheckModalMinutes}+ MINUTES. PLEASE CONFIRM STATUS.
            </Alert>
            {statusModalIncident?.incident && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ textTransform: "uppercase", mb: 1 }}>
                  {statusModalIncident.incident.incidentType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CAD {statusModalIncident.incident.cadId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Started {statusModalIncident.incident.occurredAtUtc
                    ? formatUtcToLocal(statusModalIncident.incident.occurredAtUtc)
                    : "Unknown"}
                </Typography>
              </Paper>
            )}
            <Typography variant="body2" color="text.secondary">
              Choose "10-4" to reset the timer or "Clear" to finish the call details now.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleStatusCheckClear}
            sx={{ textTransform: "uppercase" }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleStatusCheckAcknowledge}
            sx={{ textTransform: "uppercase" }}
          >
            10-4
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
        keepMounted
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography component="div" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
              {detailIncident?.cadId ? `Incident ${detailIncident.cadId}` : "Incident Details"}
            </Typography>
            {detailEditMode
              && detailIncident
              && detailIncident.primaryRangerId?.toUpperCase() === user?.rangerId?.toUpperCase()
              && isAssistMotorist(detailForm?.incidentType) && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                {isTransportActiveForIncident ? (
                  <Button
                    onClick={() => {
                      if (!transportAddress.trim()) {
                        openTransportDialogFor("end");
                        return;
                      }
                      handleEndTransport();
                    }}
                    disabled={detailSaving}
                    color="warning"
                    variant="contained"
                    size="small"
                    sx={{ textTransform: "uppercase" }}
                  >
                    End Transport
                  </Button>
                ) : (
                  <Button
                    onClick={() => openTransportDialogFor("start")}
                    disabled={detailSaving}
                    color="warning"
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Transport
                  </Button>
                )}
                <Button
                  onClick={handleAbandonedClick}
                  disabled={detailSaving}
                  color="secondary"
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: "uppercase" }}
                >
                  Abandoned
                </Button>
              </Stack>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : detailError ? (
            <Alert severity="error" sx={{ textTransform: "uppercase" }}>
              {detailError}
            </Alert>
          ) : !detailIncident ? (
            <Alert severity="info" sx={{ textTransform: "uppercase" }}>
              SELECT AN INCIDENT TO VIEW DETAILS
            </Alert>
          ) : detailEditMode ? (
            <Box
              component="form"
              id="incident-edit-form"
              onSubmit={handleDetailSubmit}
              sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 3 }}
            >
              {detailRequiresSourceConfirm && (
                <Alert severity="info" sx={{ textTransform: "uppercase" }}>
                  QUICK CREATE: CONFIRM SOURCE AND INCIDENT TYPE BEFORE SAVING.
                </Alert>
              )}
              {detailValidationErrors.length > 0 && (
                <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
                  {detailValidationErrors.every(f => f.startsWith("vehicle."))
                    ? `MISSING REQUIRED VEHICLE FIELDS: ${formatMissingFieldList(detailValidationErrors)}`
                    : `MISSING REQUIRED FIELDS: ${formatMissingFieldList(detailValidationErrors)}`
                  }
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Source"
                    value={detailForm.source}
                    onChange={handleDetailFieldChange("source")}
                    required
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    error={hasValidationError("source")}
                    helperText={
                      hasValidationError("source") ? "REQUIRED TO CLEAR INCIDENT." : undefined
                    }
                  >
                    <MenuItem value="" disabled>
                      SELECT SOURCE
                    </MenuItem>
                    {SOURCE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Incident Type"
                    value={detailForm.incidentType}
                    onChange={handleDetailFieldChange("incidentType")}
                    required
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    error={hasValidationError("incidentType")}
                    helperText={
                      hasValidationError("incidentType")
                        ? "REQUIRED TO CLEAR INCIDENT."
                        : undefined
                    }
                  >
                    <MenuItem value="" disabled>
                      SELECT INCIDENT TYPE
                    </MenuItem>
                    {INCIDENT_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={detailForm.notes}
                    onChange={handleDetailFieldChange("notes")}
                    multiline
                    minRows={4}
                    fullWidth
                    placeholder="ADD CONTEXT FOR DISPATCH"
                    error={hasValidationError("notes")}
                    helperText={
                      hasValidationError("notes") ? "REQUIRED TO CLEAR INCIDENT." : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ textTransform: "uppercase", fontWeight: 700, mt: 1 }}>
                    Location
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Latitude"
                    value={detailForm.latitude}
                    onChange={handleDetailFieldChange("latitude")}
                    fullWidth
                    placeholder="E.g., 32.7767"
                    type="number"
                    inputProps={{ step: "any" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Longitude"
                    value={detailForm.longitude}
                    onChange={handleDetailFieldChange("longitude")}
                    fullWidth
                    placeholder="E.g., -96.7970"
                    type="number"
                    inputProps={{ step: "any" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Street/Highway"
                    value={detailForm.streetName}
                    onChange={handleDetailFieldChange("streetName")}
                    fullWidth
                    placeholder="E.g., I-35, US-75, SH-121"
                    helperText="For Waze alerts"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Traffic Direction"
                    value={detailForm.trafficDirection}
                    onChange={handleDetailFieldChange("trafficDirection")}
                    fullWidth
                    helperText="Direction affected (for Waze)"
                  >
                    <MenuItem value="">Not Specified</MenuItem>
                    <MenuItem value="NB">Northbound</MenuItem>
                    <MenuItem value="SB">Southbound</MenuItem>
                    <MenuItem value="EB">Eastbound</MenuItem>
                    <MenuItem value="WB">Westbound</MenuItem>
                    <MenuItem value="BOTH">Both Directions</MenuItem>
                  </TextField>
                </Grid>
                {detailForm && needsVehicleInfo(detailForm.incidentType) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                        Vehicle Information
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Year"
                        value={detailForm.vehicle.year}
                        onChange={handleDetailVehicleChange("year")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Make"
                        value={detailForm.vehicle.make}
                        onChange={handleDetailVehicleChange("make")}
                        fullWidth
                        error={hasValidationError("vehicle.make")}
                        helperText={hasValidationError("vehicle.make") ? "REQUIRED FOR ABANDONED" : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Model"
                        value={detailForm.vehicle.model}
                        onChange={handleDetailVehicleChange("model")}
                        fullWidth
                        error={hasValidationError("vehicle.model")}
                        helperText={hasValidationError("vehicle.model") ? "REQUIRED FOR ABANDONED" : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Color"
                        value={detailForm.vehicle.color}
                        onChange={handleDetailVehicleChange("color")}
                        fullWidth
                        error={hasValidationError("vehicle.color")}
                        helperText={hasValidationError("vehicle.color") ? "REQUIRED FOR ABANDONED" : undefined}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="State"
                        value={detailForm.vehicle.state}
                        onChange={handleDetailVehicleChange("state")}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Plate"
                        value={detailForm.vehicle.plate}
                        onChange={handleDetailVehicleChange("plate")}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  {detailForm && isDebrisType(detailForm.incidentType) && (
                    <Alert severity="info" sx={{ textTransform: "uppercase", mb: 2 }}>
                      DEFAULT DISPOSITION SET TO {DEBRIS_DISPOSITION}. ADD EXTRA ACTIONS IF NEEDED.
                    </Alert>
                  )}
                  <FormControl fullWidth error={hasValidationError("servicesProvided")} disabled={abandonedPrompt?.active}>
                    <InputLabel id="detail-services-label">Disposition</InputLabel>
                    <Select
                      labelId="detail-services-label"
                      multiple
                      value={detailForm?.servicesProvided || []}
                      onChange={handleDetailServicesChange}
                      input={<OutlinedInput label="Disposition" />}
                      renderValue={(selected) =>
                        selected.length === 0 ? "NONE" : selected.map(s => getTenCodeLabel(s, useTenCodes) || s).join(", ")
                      }
                    >
                      {SERVICE_OPTIONS.map((service) => (
                        <MenuItem key={service} value={service}>
                          {getTenCodeLabel(service, useTenCodes) || service}
                        </MenuItem>
                      ))}
                    </Select>
                    {hasValidationError("servicesProvided") && (
                      <FormHelperText sx={{ textTransform: "uppercase" }}>
                        DISPOSITION IS REQUIRED TO CLEAR INCIDENT.
                      </FormHelperText>
                    )}
                  </FormControl>
                  {abandonedPrompt?.active && (
                    <Alert severity="info" sx={{ textTransform: "uppercase", mt: 2 }}>
                      ABANDONED WILL SET DISPOSITION TO SAFETY CHECK AND ABANDONED.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StatusItem label="CAD ID" value={detailIncident.cadId} tabletMode={tabletMode} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusItem label="Source" value={detailIncident.source} tabletMode={tabletMode} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusItem
                    label="Status"
                    value={formatIncidentStatus(detailIncident.status)}
                    tabletMode={tabletMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusItem label="Incident Type" value={detailIncident.incidentType} tabletMode={tabletMode} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusItem label="Primary Ranger" value={detailIncident.primaryRangerId} tabletMode={tabletMode} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusItem
                    label="Start"
                    value={detailIncident.occurredAtUtc ? formatUtcToLocal(detailIncident.occurredAtUtc) : "—"}
                    tabletMode={tabletMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StatusItem
                    label="End"
                    value={detailIncident.occurredEndUtc ? formatUtcToLocal(detailIncident.occurredEndUtc) : "—"}
                    tabletMode={tabletMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StatusItem
                    label="Location"
                    value={formatIncidentLocation(detailIncident, decodedLocations)}
                    tabletMode={tabletMode}
                  />
                </Grid>
                <Grid item xs={12}><IncidentMedia key={detailIncident.id} incidentId={detailIncident.id}/></Grid>
              </Grid>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ textTransform: "uppercase", mb: 1 }}>
                  Notes
                </Typography>
                <Typography variant="body2">
                  {detailIncident.notes ? detailIncident.notes : "NO NOTES PROVIDED"}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ textTransform: "uppercase", mb: 1 }}>
                  Participants
                </Typography>
                {detailIncident.participants?.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {detailIncident.participants.map((participant) => (
                      <Chip
                        key={participant.id}
                        label={participant.leftAtUtc
                          ? `${participant.rangerId} (left)`
                          : participant.rangerId
                        }
                        size="small"
                        color={participant.leftAtUtc ? "default" : "primary"}
                        variant={participant.leftAtUtc ? "outlined" : "filled"}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2">NO RANGERS LISTED</Typography>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          {/* Only show Close button when NOT in edit mode */}
          {!detailEditMode && (
            <Button onClick={handleDetailClose} sx={{ textTransform: "uppercase" }}>
              Close
            </Button>
          )}
          {/* ATTACH button: show for any user who is NOT the primary ranger and NOT already an active participant */}
          {!detailEditMode && detailIncident && !detailLoading && !detailError &&
            detailIncident.primaryRangerId?.toUpperCase() !== user?.rangerId?.toUpperCase() &&
            !detailIncident.participants?.some(p =>
              p.rangerId?.toUpperCase() === user?.rangerId?.toUpperCase() && !p.leftAtUtc
            ) && (
            <Button
              onClick={handleAttachToIncident}
              disabled={detailAttaching}
              color="warning"
              variant="contained"
              sx={{ textTransform: "uppercase", fontWeight: 700 }}
            >
              {detailAttaching ? "Attaching..." : "Attach"}
            </Button>
          )}
          {canDeleteIncidents && detailIncident && (
            <Button
              color="error"
              onClick={() => openDeleteIncidentDialog(detailIncident)}
              disabled={deleteSubmitting}
              sx={{ textTransform: "uppercase" }}
            >
              {deleteSubmitting ? "Archiving..." : "Archive Call"}
            </Button>
          )}
          {/* Allow edit if admin/supervisor OR user is a participant in the incident */}
          {(canManageIncidents || (detailIncident && (
            detailIncident.primaryRangerId?.toUpperCase() === user?.rangerId?.toUpperCase() ||
            detailIncident.participants?.some(p => p.rangerId?.toUpperCase() === user?.rangerId?.toUpperCase())
          ))) && detailIncident && !detailLoading && !detailError && (
            detailEditMode ? (
              <>
                <Button
                  onClick={handleFinishLater}
                  disabled={detailSaving}
                  color="warning"
                  sx={{ textTransform: "uppercase" }}
                >
                  Finish Later
                </Button>
                {/* Only primary ranger can clear incident */}
                {detailIncident.primaryRangerId?.toUpperCase() === user?.rangerId?.toUpperCase() ? (
                  <>
                    <Button
                      onClick={handleClearIncident}
                      disabled={detailSaving}
                      color="success"
                      variant="contained"
                      sx={{ textTransform: "uppercase" }}
                    >
                      Clear Incident
                    </Button>
                  </>
                ) : (
                  // Only show Leave if user is an active participant (hasn't left yet)
                  detailIncident.participants?.some(p =>
                    p.rangerId?.toUpperCase() === user?.rangerId?.toUpperCase() && !p.leftAtUtc
                  ) ? (
                    <Button
                      onClick={handleLeaveIncident}
                      disabled={detailSaving}
                      color="info"
                      variant="contained"
                      sx={{ textTransform: "uppercase" }}
                    >
                      Leave Incident
                    </Button>
                  ) : null
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  onClick={() => {
                    setDetailEditMode(false);
                    setDetailRequiresSourceConfirm(false);
                  }}
                  disabled={detailSaving}
                  sx={{ textTransform: "uppercase" }}
                >
                  Cancel Edit
                </Button>
                <Button
                  type="submit"
                  form="incident-edit-form"
                  variant="contained"
                  disabled={detailSaving}
                  sx={{ textTransform: "uppercase" }}
                >
                  {detailSaving ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <>
                {/* Show Leave button for assist rangers when not in edit mode */}
                {detailIncident.primaryRangerId?.toUpperCase() !== user?.rangerId?.toUpperCase() &&
                 detailIncident.participants?.some(p =>
                   p.rangerId?.toUpperCase() === user?.rangerId?.toUpperCase() && !p.leftAtUtc
                 ) && (
                  <Button
                    onClick={handleLeaveIncident}
                    color="info"
                    variant="outlined"
                    sx={{ textTransform: "uppercase" }}
                  >
                    Leave Incident
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={beginIncidentEdit}
                  sx={{ textTransform: "uppercase" }}
                >
                  Edit Incident
                </Button>
              </>
            )
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={deletePrompt.open}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Archive Call
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
              THIS WILL ARCHIVE THE CALL AND RETAIN ITS PARTICIPANTS AND VIDEO ASSOCIATIONS.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {deleteTarget?.cadId
                ? `CAD ${deleteTarget.cadId} · ${deleteTarget.incidentType || "Unknown"}`
                : "Select a call to archive."}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The call will leave active dispatch and normal reports. Administrators can read it in Archived calls.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteIncident}
            disabled={deleteSubmitting}
            sx={{ textTransform: "uppercase" }}
          >
            {deleteSubmitting ? "Archiving..." : "Archive Call"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Abandoned Vehicle Dialog */}
      <Dialog
        open={abandonedPrompt.open}
        onClose={handleAbandonedCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          Abandoned Vehicle
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
              Select the shoulder/side of the highway where the vehicle is located:
            </Typography>
            <Stack direction="row" spacing={2} sx={{ justifyContent: "center", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAbandonedConfirm("Left Shoulder")}
                disabled={detailSaving}
                sx={{ textTransform: "uppercase", minWidth: 150, py: 2 }}
              >
                Left Shoulder
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAbandonedConfirm("Right Shoulder")}
                disabled={detailSaving}
                sx={{ textTransform: "uppercase", minWidth: 150, py: 2 }}
              >
                Right Shoulder
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleAbandonedConfirm("Median")}
                disabled={detailSaving}
                sx={{ textTransform: "uppercase", minWidth: 150, py: 2 }}
              >
                Median
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAbandonedCancel} disabled={detailSaving} sx={{ textTransform: "uppercase" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transportDialog.open}
        onClose={closeTransportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textTransform: "uppercase", fontWeight: 700 }}>
          {transportDialog.mode === "end" ? "End Transport" : "Start Transport"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ textTransform: "uppercase", fontWeight: 600 }}>
              {transportDialog.mode === "end"
                ? "Confirm transport destination (optional)."
                : "Enter destination if known (optional)."}
            </Typography>
            <TextField
              label="Transport destination"
              value={transportAddress}
              onChange={(event) => setTransportAddress(event.target.value.toUpperCase())}
              fullWidth
              autoComplete="off"
              helperText={
                transportDialog.mode === "end"
                  ? "Add destination if it was not recorded earlier."
                  : "Leave blank if unknown."
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={closeTransportDialog} sx={{ textTransform: "uppercase" }}>
            Cancel
          </Button>
          {transportDialog.mode === "end" && (
            <Button
              onClick={() => handleEndTransport("")}
              color="warning"
              variant="outlined"
              sx={{ textTransform: "uppercase" }}
            >
              Bypass
            </Button>
          )}
          <Button
            onClick={() => {
              if (transportDialog.mode === "end") {
                handleEndTransport();
                return;
              }
              handleStartTransport();
            }}
            color="warning"
            variant="contained"
            sx={{ textTransform: "uppercase" }}
          >
            {transportDialog.mode === "end" ? "End Transport" : "Start Transport"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Abandoned Vehicle Proximity Alert */}
      <AbandonedVehicleAlert
        gpsLocation={gpsCoords}
        enabled={!!gpsCoords}
      />

      {/* Event Signup Modal */}
      <EventSignupModal />

      {/* Emergency Countdown Confirmation Dialog */}
      <Dialog
        open={emergencyConfirmOpen}
        onClose={() => closeEmergencyConfirm()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Emergency Activation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ textTransform: "uppercase" }}>
              Emergency alert will activate in {emergencyCountdown} second{emergencyCountdown === 1 ? "" : "s"}.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Press cancel to stop activation before the countdown ends.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmergencyConfirm}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Emergency Self-Cancel Dialog */}
      <Dialog open={supervisorClear.open} onClose={() => !supervisorClearLoading && setSupervisorClear({ open: false, rangerId: null })}
        maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>Review emergency clearance</DialogTitle>
        <DialogContent dividers>
          <EmergencyClearanceReview review={emergencyClearance} busy={supervisorClearLoading}
            onRefresh={() => emergencyClearance.prepare(supervisorClear.rangerId ? 'ranger' : 'all', supervisorClear.rangerId)}/>
        </DialogContent>
        <DialogActions>
          <Button disabled={supervisorClearLoading} onClick={() => setSupervisorClear({ open: false, rangerId: null })}>Go Back</Button>
          <Button color="warning" variant="contained" onClick={confirmSupervisorClear}
            disabled={supervisorClearLoading || emergencyClearance.loading || !emergencyClearance.confirmation?.alerts.length}>
            {supervisorClearLoading ? 'Clearing...' : 'Confirm clearance'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={selfCancelOpen} onClose={() => !selfCancelLoading && setSelfCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Your Emergency</DialogTitle>
        <DialogContent>
          <EmergencyClearanceReview review={emergencyClearance} busy={selfCancelLoading}
            onRefresh={() => { setSelfCancelPin(''); setSelfCancelError(''); emergencyClearance.prepare('self'); }}/>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Reason for cancellation"
              value={selfCancelReason}
              onChange={(e) => setSelfCancelReason(e.target.value)}
              fullWidth
              required
            >
              <MenuItem value="Activated by mistake">Activated by mistake</MenuItem>
              <MenuItem value="Situation resolved">Situation resolved</MenuItem>
              <MenuItem value="False alarm">False alarm</MenuItem>
              <MenuItem value="Testing">Testing</MenuItem>
            </TextField>
            <TextField
              label="Enter your PIN"
              type="password"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={selfCancelPin}
              onChange={(e) => setSelfCancelPin(e.target.value)}
              fullWidth
              required
              autoComplete="off"
            />
            {selfCancelError && selfCancelError !== emergencyClearance.error && (
              <Alert severity="error">{selfCancelError}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelfCancelOpen(false)} disabled={selfCancelLoading}>
            Go Back
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSelfCancelEmergency}
            disabled={selfCancelLoading || emergencyClearance.loading || !emergencyClearance.confirmation?.alerts.length}
          >
            {selfCancelLoading ? "Cancelling..." : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

function SectionCard({ title, emptyMessage, isEmpty, tabletMode, actions, loading, error, warning, children }) {
  const horizontalPadding = tabletMode ? 3 : 2.5;
  const headerPaddingY = tabletMode ? 2 : 1.5;
  const bodyPaddingY = tabletMode ? 2.5 : 2;

  const content = loading ? (
    <Box sx={{ display: "flex", justifyContent: "center", py: bodyPaddingY }}>
      <CircularProgress size={32} />
    </Box>
  ) : error ? (
    <Alert severity="error" sx={{ textTransform: "uppercase" }}>
      {error}
    </Alert>
  ) : isEmpty ? (
    <Alert
      severity="info"
      sx={{ fontSize: tabletMode ? "1.05rem" : "0.95rem", textTransform: "uppercase" }}
    >
      {emptyMessage}
    </Alert>
  ) : (
    children
  );

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.35)",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(15, 23, 42, 0.72)" : "rgba(255, 255, 255, 0.86)",
        backdropFilter: "blur(6px)",
      }}
    >
      <Box sx={{ px: horizontalPadding, py: headerPaddingY }}>
        <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
          <Typography variant={tabletMode ? "h5" : "h6"} sx={{ textTransform: "uppercase" }}>
            {title}
          </Typography>
          <Divider sx={{ flexGrow: 1, opacity: 0.5 }} />
          {actions && <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>}
        </Stack>
      </Box>
      <Divider sx={{ opacity: 0.6 }} />
      <Box sx={{ px: horizontalPadding, py: bodyPaddingY }}>
        {warning && <Alert severity="warning" role="status" sx={{ mb: 1 }}>{warning}</Alert>}
        {content}
      </Box>
    </Paper>
  );
}

function StatusItem({ label, value, tabletMode }) {
  return (
    <Box>
      <Typography
        variant={tabletMode ? "subtitle2" : "caption"}
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
      >
        {formatDisplay(label)}
      </Typography>
      <Typography
        variant={tabletMode ? "h6" : "body1"}
        fontWeight={600}
        sx={{ textTransform: "uppercase" }}
      >
        {formatDisplay(value)}
      </Typography>
    </Box>
  );
}

function formatStatus(value) {
  switch (value) {
    case "emergency":
      return "Emergency";
    case "out-of-service":
      return "Out of Service";
    case "available":
      return "Available";
    case "busy":
      return "Busy";
    default:
      return "Available";
  }
}

function formatIncidentStatus(value) {
  switch ((value || "").toLowerCase()) {
    case STATUS_VALUES.CLEARED:
      return "Cleared";
    case STATUS_VALUES.INCOMPLETE:
      return "Incomplete";
    case STATUS_VALUES.OPEN:
      return "Open";
    default:
      return value || "Open";
  }
}

function formatIncidentLocation(incident, decodedLocations = {}) {
  if (!incident) {
    return "NOT PROVIDED";
  }

  if (typeof incident.latitude === "number" && typeof incident.longitude === "number") {
    const key = geocodeKey(incident.latitude, incident.longitude);
    if (key && decodedLocations[key]) {
      return decodedLocations[key];
    }
  }

  const storedCoordinates = parseCoordinatesFromLocation(incident.locationDescription);
  const storedKey = storedCoordinates ? geocodeKey(storedCoordinates.latitude, storedCoordinates.longitude) : null;
  if (storedKey && decodedLocations[storedKey]) return decodedLocations[storedKey];
  if (incident.locationDescription?.trim() && incident.locationDescription.trim().toUpperCase() !== 'NOT PROVIDED') return incident.locationDescription;
  if (incident.streetName) return incident.streetName;

  if (typeof incident.latitude === "number" && typeof incident.longitude === "number") {
    return `LAT ${incident.latitude.toFixed(4)} / LON ${incident.longitude.toFixed(4)}`;
  }

  return "NOT PROVIDED";
}

const isAssistMotorist = (value) => {
  const normalized = (value || "").toLowerCase().replace(/-/g, " ").trim();
  return normalized === "assist motorist" || normalized.includes("assist motorist");
};
const isAccidentType = (value) => {
  const normalized = (value || "").toLowerCase().replace(/-/g, " ").trim();
  return normalized === "accident" || normalized.includes("accident");
};
const needsVehicleInfo = (value) => isAssistMotorist(value) || isAccidentType(value);
const isDebrisType = (value) => (value || "").toLowerCase() === "debris";

export default Dashboard;
